import { useState, useMemo, useRef, useEffect } from "react";

const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Jost', sans-serif; background: #ede5d8; color: #2a1e12; }
    body.staff-mode { background: #ffffff !important; color: #2a1810 !important; }
    body.staff-mode input, body.staff-mode select, body.staff-mode textarea { background: #f0ebe3 !important; color: #2a1810 !important; border-color: #b89470 !important; }
    body.staff-mode select option { background: #f0ebe3; color: #2a1810; }
    input, select, textarea, button { font-family: 'Jost', sans-serif; }
    ::-webkit-scrollbar { width: 3px; height: 3px; }
    ::-webkit-scrollbar-thumb { background: #c4a882; border-radius: 2px; }
    input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
    select option { background: #3d2b1c; }
  `}</style>
);

const C = {
  bg:"#ede5d8", bg1:"#e4daca", bg2:"#d8ccba", bg3:"#ccbea8",
  border:"#b8a08866", border2:"#9a8468",
  gold:"#7a5030", gold2:"#9a7050", cream:"#2a1e12",
  muted:"#7a6050", muted2:"#9a8068",
  green:"#3a7a3a", red:"#b06050", accent:"#8a5830",
};
// Staff: white base × brown accent
const CS = {
  bg:"#ffffff", bg1:"#faf7f4", bg2:"#f0ebe3", bg3:"#e8ddd0",
  border:"#d4b89655", border2:"#b89470",
  gold:"#7a4e28", gold2:"#9a6e48", cream:"#2a1810",
  muted:"#8a6850", muted2:"#b09070",
  green:"#3a7a3a", red:"#b06050", accent:"#7a4e28",
};
const useC = (role) => role==="staff" ? CS : C;
const card = (extra={}) => ({ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:16, padding:18, ...extra });
const cardS = (role, extra={}) => { const cc=useC(role); return { background:cc.bg1, border:`1px solid ${cc.border}`, borderRadius:16, padding:18, ...extra }; };


// ═══════════════════════════════════════════════════════
// GAS API 連携（Netlify Functions経由でCORS回避）
// ═══════════════════════════════════════════════════════
const SYNC_URL = "https://script.google.com/macros/s/AKfycby63zKjMzQ5q46HxrqjHFtzjBrxsa4XbioIIRoj6Y4RSrQKGS6SDuVvQMcBGwcSPwin/exec";

const gasPost = async (action, data) => {
  try {
    const res = await fetch(SYNC_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify({ action, ...data }),
    });
    if (!res.ok) {
      console.error(`gasPost ${action} HTTP error:`, res.status);
      return { success: false, error: `HTTP ${res.status}` };
    }
    const json = await res.json();
    if (!json.success) {
      console.error(`gasPost ${action} failed:`, json.error);
    } else {
      console.log(`✓ ${action}:`, json.data);
    }
    return json;
  } catch(e) {
    console.error(`gasPost ${action} exception:`, e);
    return { success: false, error: e.message };
  }
};

// 🌸 リトライ付き保存（最大3回再試行・指数バックオフ）
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const gasPostWithRetry = async (action, data, maxRetry = 3) => {
  let lastErr = null;
  for (let i = 0; i < maxRetry; i++) {
    if (i > 0) {
      await sleep(1000 * i); // 1s, 2s, 3s
      console.log(`🔄 ${action} リトライ ${i+1}/${maxRetry}`);
    }
    const result = await gasPost(action, data);
    if (result?.success) return result;
    lastErr = result?.error;
  }
  return { success: false, error: lastErr || "リトライ失敗" };
};

const gasLoad = async () => {
  try {
    const res = await fetch(`${SYNC_URL}?action=loadAll`);
    if (!res.ok) return { success: false };
    return await res.json();
  } catch(e) {
    console.error("GAS load error:", e);
    return { success: false };
  }
};

// 繁忙期：7・8・12・1月
const BUSY_MONTHS = [1, 7, 8, 12];
const isBusy = () => BUSY_MONTHS.includes(new Date().getMonth() + 1);

// 🌸 過去月の確定目標（締め済み・歴史値の特例固定）
// { "yyyy-mm": { 店舗ID: 目標額 } }
const TARGET_OVERRIDES = {
  "2026-05": { s1: 4900000 }, // 5月 天神店の目標を490万に固定
};
// 指定月・店舗の目標を返す（上書きがあればそれ、なければ繁忙/通常）
const getMonthTarget = (store, ymStr) => {
  if (!store) return 0;
  const ov = TARGET_OVERRIDES[ymStr]?.[store.id];
  if (ov != null) return ov;
  return isBusy() ? (store.targetBusy||0) : (store.targetNormal||0);
};

// 階段式歩合計算（税抜きベース）
// 売上から消費税10%を引いた金額でインセンティブ計算
// ライン：税抜45万～2%、税抜55万～5%、税抜65万～6%、税抜75万～7%
const TAX_RATE = 1.1;
const DEFAULT_COMMISSION_TIERS = [
  { taxEx: 750000, rate: 7 },
  { taxEx: 650000, rate: 6 },
  { taxEx: 550000, rate: 5 },
  { taxEx: 450000, rate: 2 },
];
// 店舗ごとのティアを取得（未設定ならデフォルト）
const getStoreTiers = (store) => {
  if (store && Array.isArray(store.commissionTiers) && store.commissionTiers.length > 0) {
    return [...store.commissionTiers].sort((a,b) => b.taxEx - a.taxEx);
  }
  return DEFAULT_COMMISSION_TIERS;
};
const calcCommission = (sales, store) => {
  const taxExcluded = Math.round(sales / TAX_RATE);
  const tiers = getStoreTiers(store);
  for (const t of tiers) {
    if (taxExcluded >= t.taxEx) return Math.round(taxExcluded * (t.rate / 100));
  }
  return 0;
};
const getCommissionRate = (sales, store) => {
  const taxExcluded = Math.round(sales / TAX_RATE);
  const tiers = getStoreTiers(store);
  for (const t of tiers) {
    if (taxExcluded >= t.taxEx) return t.rate;
  }
  return 0;
};
// 税抜き売上を返す
const getTaxExcluded = (sales) => Math.round(sales / TAX_RATE);

// ③ 指名料インセンティブ（10本以上 → 全本数×500円）
const SHIMEI_BONUS_PER = 500;
const SHIMEI_BONUS_MIN = 10;
const calcShimeiBonus = (shimei) => {
  if (shimei >= SHIMEI_BONUS_MIN) return shimei * SHIMEI_BONUS_PER;
  return 0;
};

const EXPENSE_RATE_SUPPLY  = 0.04;  // 仕入れ 4%
const EXPENSE_RATE_CONSUMABLE = 0.025; // 消耗品 2.5%
const EXPENSE_RATE = EXPENSE_RATE_SUPPLY + EXPENSE_RATE_CONSUMABLE; // 合計 6.5%

const STORES_SEED = [
  { id:"s1", name:"天神店", targetBusy:1800000, targetNormal:1400000,
    shiftClosedDays:[], irregularClosedDates:[], shiftWeekdayMin:1, shiftWeekdayMax:2, shiftWeekendMin:2, shiftWeekendMax:2,
    shiftSchedule:"正社員① 9:45〜19:30 ／ ② 10:45〜20:30\n藤田（TR）火・木 10:45〜19:00 固定\n山本（パート）① 9:45〜19:30 ／ ② 10:45〜20:30" },
  { id:"s2", name:"大濠店", targetBusy:1200000, targetNormal:1000000,
    shiftClosedDays:[1], irregularClosedDates:[], shiftWeekdayMin:1, shiftWeekdayMax:2, shiftWeekendMin:2, shiftWeekendMax:2,
    shiftSchedule:"横手 9:45〜19:30 固定 ／ 石井 9:45〜19:00 固定\n福田 金曜 9:45〜17:00 固定" },
  { id:"s3", name:"薬院店", targetBusy:1100000, targetNormal:1000000,
    shiftClosedDays:[2], irregularClosedDates:[], shiftWeekdayMin:1, shiftWeekdayMax:2, shiftWeekendMin:2, shiftWeekendMax:2,
    shiftSchedule:"パート① 10:45〜19:00 ／ ② 9:45〜19:00\n山本 9:45〜17:00 固定 ／ 福田 月曜 9:45〜18:00 固定" },
  { id:"s4", name:"博多店", targetBusy:1700000, targetNormal:1500000,
    shiftClosedDays:[], irregularClosedDates:[], shiftWeekdayMin:1, shiftWeekdayMax:2, shiftWeekendMin:3, shiftWeekendMax:3,
    shiftSchedule:"正社員① 9:30〜19:30 ／ ② 10:00〜20:00 ／ ③ 10:00〜19:00\n日曜・祝日は正社員③固定" },
];
const STAFF_SEED = [
  { id:"p4",  name:"一ノ瀬 彩加", storeId:"s4", baseSalary:0, rate:0, target:600000, contractHours:0, pw:"0000", email:"aya_0128ka@yahoo.co.jp",      joinDate:"2024-05-01", shiftMaxDays:21, shiftType:"regular", shiftSlots:["①","②","③"], slotTimes:{"①":"9:30〜19:30","②":"10:00〜20:00","③":"10:00〜19:00"}, shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p8",  name:"藤井 千咲",   storeId:"s4", baseSalary:0, rate:0, target:600000, contractHours:0, pw:"0000", email:"chii_ss.1317@icloud.com",      joinDate:"2024-08-02", shiftMaxDays:21, shiftType:"regular", shiftSlots:["①","②","③"], slotTimes:{"①":"9:30〜19:30","②":"10:00〜20:00","③":"10:00〜19:00"}, shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p10", name:"藤田 沙代子", storeId:"s1", baseSalary:0, rate:0, target:250000, contractHours:0, pw:"0000", email:"sayoko.maia11@gmail.com",      joinDate:"2024-06-01", shiftMaxDays:8,  shiftType:"fixed",   shiftSlots:[],          slotTimes:{"固定":"10:45〜19:00"}, shiftFixedDays:[2,4], shiftNote:"火木のみ", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p15", name:"横手 琴乃",   storeId:"s2", baseSalary:0, rate:0, target:600000, contractHours:0, pw:"0000", email:"kotono340_1007@softbank.ne.jp", joinDate:"2025-04-08", shiftMaxDays:21, shiftType:"regular", shiftSlots:[],          slotTimes:{"固定":"9:45〜19:30"}, shiftFixedDays:[], shiftNote:"固定時間", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p28", name:"山本 沙織",   storeId:"s3", baseSalary:0, rate:0, target:300000, contractHours:0, pw:"0000", email:"milkchan.1.13@gmail.com",       joinDate:"2025-06-01", shiftMaxDays:17, shiftType:"part",    shiftSlots:[],          slotTimes:{"固定":"9:45〜17:00"}, shiftFixedDays:[], shiftNote:"固定時間", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p29", name:"古賀 純花",   storeId:"s1", baseSalary:0, rate:0, target:600000, contractHours:0, pw:"0000", email:"junka0301@gmail.com",           joinDate:"2025-07-20", shiftMaxDays:21, shiftType:"regular", shiftSlots:["①","②"],     slotTimes:{"①":"9:45〜19:30","②":"10:45〜20:30"}, shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p30", name:"石井 紗也加", storeId:"s2", baseSalary:0, rate:0, target:500000, contractHours:0, pw:"0000", email:"navikysyk@icloud.com",          joinDate:"2025-10-15", shiftMaxDays:19, shiftType:"part",    shiftSlots:[],          slotTimes:{"固定":"9:45〜19:00"}, shiftFixedDays:[], shiftNote:"固定時間", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p31", name:"八坂 麻巳子", storeId:"s4", baseSalary:0, rate:0, target:400000, contractHours:0, pw:"0000", email:"mamiko_sandaime@docomo.ne.jp",  joinDate:"2025-10-01", shiftMaxDays:19, shiftType:"part",    shiftSlots:["①","②","③"], slotTimes:{"①":"9:30〜19:00","②":"11:00〜20:00","③":"10:00〜19:00"}, shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p35", name:"坂口 朝夏",   storeId:"s3", baseSalary:0, rate:0, target:400000, contractHours:0, pw:"0000", email:"asaka.7562@gmail.com",          joinDate:"2026-04-01", shiftMaxDays:17, shiftType:"part",    shiftSlots:["①","②"],     slotTimes:{"①":"10:45〜19:00","②":"9:45〜19:00"}, shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p36", name:"江口 舞花",   storeId:"s1", baseSalary:0, rate:0, target:600000, contractHours:0, pw:"0000", email:"maimaikaburi1118@icloud.com",   joinDate:"2026-04-01", shiftMaxDays:21, shiftType:"regular", shiftSlots:["①","②"],     slotTimes:{"①":"9:45〜19:30","②":"10:45〜20:30"}, shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p37", name:"福田 やよい", storeId:"s2", storeIds:["s2","s3"], baseSalary:0, rate:0, target:250000, contractHours:0, pw:"0000", email:"tianail0317@yahoo.co.jp", joinDate:"2025-06-01", shiftMaxDays:4,  shiftType:"fixed",   shiftSlots:[], slotTimes:{"月曜(薬院)":"9:45〜18:00","金曜(大濠)":"9:45〜17:00"}, shiftFixedDays:[1,5], shiftNote:"月曜薬院・金曜大濠", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"p38", name:"山本 知夏", storeId:"s1", baseSalary:0, rate:0, target:0, contractHours:160, pw:"0000", email:"", joinDate:"2026-06-01", shiftMaxDays:17, shiftType:"part", shiftSlots:["①","②"], slotTimes:{"①":"9:45〜19:30","②":"10:45〜20:30"}, shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 },
  { id:"phelp", name:"［ヘルプ］", storeId:"s1", storeIds:["s1","s2","s3","s4"], baseSalary:0, rate:0, target:0, contractHours:0, pw:"nologin", email:"", joinDate:"2026-07-01", shiftMaxDays:0, shiftType:"part", shiftSlots:[], slotTimes:{}, shiftFixedDays:[], shiftNote:"ヘルプ要員（手動追加のみ・自動生成に入りません）", paidLeaveAnnual:0, paidLeaveInitial:0, isHelp:true },
];
const genRecords = () => [];
const ORDERS_SEED = [];

const NOTICES_SEED = [];
const THREADS_SEED = [];

const OWNER_PASS   = "0461";
const CLERK_PASS   = "9999"; // 🌸 事務員用PW（売上一覧のみ閲覧可能）
const fmt   = n => `¥${Number(n).toLocaleString()}`;
const ym    = d => d.slice(0,7);
const nowYM = () => { const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`; };
const todayStr = () => new Date().toISOString().slice(0,10);

// ── 有給休暇の計算 ────────────────────────────────────
// 入社日と年間付与日数から、現時点での累計付与日数を計算
// 初回付与は入社6ヶ月後、その後は1年ごと、有効期限2年
// 戻り値: { totalGranted, totalUsed, balance, nextGrantDate, history }
const calcPaidLeave = (joinDate, annualDays, initialBalance, usedDates) => {
  const result = {
    totalGranted: 0,
    totalUsed: 0,
    balance: 0,
    nextGrantDate: null,
    history: [], // [{date, action, days, expiry}]
    upcomingExpiry: null, // 次に失効する付与の情報
  };
  if (!joinDate) return result;

  const annual = parseInt(annualDays) || 0;
  const initial = parseInt(initialBalance) || 0;
  const today = new Date();
  today.setHours(0,0,0,0);
  const join = new Date(joinDate);
  join.setHours(0,0,0,0);

  // 初回付与: 入社6ヶ月後
  const firstGrant = new Date(join);
  firstGrant.setMonth(firstGrant.getMonth() + 6);

  // 有効な付与記録（2年以内）
  const grants = [];

  // 初回有給残数（中途扱い）— 失効期限なし扱い（手動入力分）
  if (initial > 0) {
    grants.push({ date: joinDate, days: initial, expiry: null, kind: "初回付与" });
  }

  // 自動付与: 初回付与日以降、1年ごとに annual 日付与
  let grantDate = new Date(firstGrant);
  while (grantDate <= today) {
    const expiry = new Date(grantDate);
    expiry.setFullYear(expiry.getFullYear() + 2); // 2年で失効
    grants.push({
      date: grantDate.toISOString().slice(0,10),
      days: annual,
      expiry: expiry.toISOString().slice(0,10),
      kind: "自動付与",
    });
    grantDate.setFullYear(grantDate.getFullYear() + 1);
  }

  // 次回付与日
  result.nextGrantDate = grantDate.toISOString().slice(0,10);

  // 失効した付与は除外
  const validGrants = grants.filter(g => !g.expiry || new Date(g.expiry) > today);
  result.totalGranted = validGrants.reduce((a,g)=>a+g.days, 0);

  // 使用日数
  result.totalUsed = (usedDates||[]).length;
  result.balance = result.totalGranted - result.totalUsed;

  // 履歴（新しい順）
  result.history = grants.map(g => ({
    ...g,
    expired: g.expiry && new Date(g.expiry) <= today,
  })).sort((a,b)=>b.date.localeCompare(a.date));

  // 次に失効する有効な付与
  const sortedByExpiry = validGrants.filter(g=>g.expiry).sort((a,b)=>a.expiry.localeCompare(b.expiry));
  if (sortedByExpiry.length > 0) {
    result.upcomingExpiry = sortedByExpiry[0];
  }

  return result;
};

// ── 管理者トークン（FCオーナー用URL発行）────────────────
// ランダムなトークンを生成（英数字20文字）
const generateToken = () => {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let token = "";
  for (let i = 0; i < 20; i++) token += chars[Math.floor(Math.random() * chars.length)];
  return token;
};
// 現在のURLから managerトークンを取得
const getManagerTokenFromURL = () => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("manager");
};

// CSV download
const downloadCSV = (filename, rows) => {
  const bom = "\uFEFF";
  const csv = bom + rows.map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type:"text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
};

// ── shared UI ─────────────────────────────────────────────────────────────

// 🌸 保存ステータスモーダル：画面中央にデカく表示
function SaveStatusModal({ status, message, onClose }) {
  if (!status) return null;
  const isSuccess = status === "success";
  const isError = status === "error";
  const isSaving = status === "saving";
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.5)",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:9999, padding:20
    }}>
      <div style={{
        background: isSuccess ? "#2a4a3a" : isError ? "#3a1a14" : "#3D2B1C",
        border: `2px solid ${isSuccess ? "#7eb89a" : isError ? "#e08070" : "#d4af37"}`,
        borderRadius:16, padding:"32px 28px", textAlign:"center",
        maxWidth:340, width:"100%",
        boxShadow:"0 10px 40px rgba(0,0,0,0.5)"
      }}>
        <div style={{ fontSize:48, marginBottom:12 }}>
          {isSuccess && "✅"}
          {isError && "⚠️"}
          {isSaving && "💾"}
        </div>
        <div style={{ fontSize:18, fontWeight:600, color:"#F5EDE0", marginBottom:8 }}>
          {isSuccess && "保存しました"}
          {isError && "保存に失敗しました"}
          {isSaving && "保存中..."}
        </div>
        {message && (
          <div style={{ fontSize:12, color:"#c9bcaa", lineHeight:1.6, marginBottom: isError ? 16 : 0, whiteSpace:"pre-line" }}>
            {message}
          </div>
        )}
        {isError && onClose && (
          <button onClick={onClose} style={{
            background:"#d4af37", color:"#2a1a0e", border:"none",
            borderRadius:8, padding:"8px 24px", fontSize:13, fontWeight:600,
            cursor:"pointer", marginTop:8
          }}>OK</button>
        )}
      </div>
    </div>
  );
}

function MiniBar({ data, color=C.gold, h=80 }) {
  const max=Math.max(...data.map(d=>d.v),1);
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:h+18 }}>
      {data.map((d,i)=>(
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
          <div style={{ width:"100%", background:color, opacity:i===data.length-1?1:0.5,
            height:Math.max(3,(d.v/max)*h), borderRadius:"3px 3px 0 0", transition:"height 0.6s ease" }}/>
          <span style={{ fontSize:9, color:C.muted2, lineHeight:1 }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}
function Donut({ pct, color=C.gold, size=76 }) {
  const r=28, circ=2*Math.PI*r, dash=(pct/100)*circ;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80">
      <circle cx={40} cy={40} r={r} fill="none" stroke={C.bg2} strokeWidth={9}/>
      <circle cx={40} cy={40} r={r} fill="none" stroke={color} strokeWidth={9}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 40 40)" style={{ transition:"stroke-dasharray 0.8s ease" }}/>
      <text x={40} y={42} textAnchor="middle" dominantBaseline="middle"
        fill={C.cream} fontSize={13} fontWeight={600}>{pct}%</text>
    </svg>
  );
}
function Tabs({ items, active, onChange }) {
  return (
    <div style={{ display:"flex", overflowX:"auto", background:C.bg, borderBottom:`1px solid ${C.border}`, paddingLeft:4 }}>
      {items.map(t=>(
        <button key={t.id} onClick={()=>onChange(t.id)} style={{
          background:"none", border:"none", padding:"10px 14px",
          color:active===t.id?C.gold:C.muted, fontSize:12,
          borderBottom:`2px solid ${active===t.id?C.gold:"transparent"}`,
          cursor:"pointer", whiteSpace:"nowrap", fontWeight:active===t.id?600:400
        }}>{t.label}</button>
      ))}
    </div>
  );
}
function RoleBadge({ role }) {
  const map = { owner:["OWNER",C.gold], staff:["スタッフ",CS.gold], clerk:["事務員",C.gold] };
  const [label, color] = map[role]||["",C.muted];
  return <div style={{ background:`${color}22`, border:`1px solid ${color}55`, borderRadius:20, padding:"3px 10px", fontSize:10, color }}>{label}</div>;
}

// ═══════════════════════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════════════════════
function Login({ staff, stores, onLogin }) {
  const [mode, setMode]         = useState(null);
  const [pwRole, setPwRole]     = useState("owner");
  const [pass, setPass]         = useState("");
  const [err, setErr]           = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffPw, setStaffPw]   = useState("");
  const [staffPwErr, setStaffPwErr] = useState(false);
  const [clerkMode, setClerkMode] = useState(false);  // 🌸 事務員モード
  const [clerkPw, setClerkPw]   = useState("");
  const [clerkPwErr, setClerkPwErr] = useState(false);

  const checkClerkPw = () => {
    if (clerkPw.trim() === CLERK_PASS) {
      onLogin("clerk", null);
    } else {
      setClerkPwErr(true); setTimeout(()=>setClerkPwErr(false), 2000);
    }
  };

  const checkStaffPw = () => {
    if (!selectedStaff) return;
    // 最新のstaff情報から再取得（データロード後でも正しいPWで判定）
    const current = staff.find(s => s.id === selectedStaff.id) || selectedStaff;
    const expected = String(current.pw||"0000").trim();
    const entered = staffPw.trim();
    console.log("Login attempt:", { id: current.id, entered, expected, match: entered===expected });
    if (entered === expected) {
      onLogin("staff", current.id);
    } else {
      setStaffPwErr(true); setTimeout(()=>setStaffPwErr(false), 2000);
    }
  };

  const checkPw = () => {
    if (pass===OWNER_PASS) { onLogin("owner", null); return; }
    setErr(true); setTimeout(()=>setErr(false),2000);
  };

  return (
    <div style={{ minHeight:"100vh", background:"radial-gradient(ellipse at 30% 20%, #d4c0a8 0%, #ede5d8 60%)",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:28 }}>
      <div style={{ fontFamily:"Cormorant Garamond", fontSize:42, color:"#7a5030", letterSpacing:6, marginBottom:4 }}>Worksplus</div>
      <div style={{ fontSize:11, color:C.muted, letterSpacing:3, marginBottom:48 }}>Salon Manager</div>

      {!mode && (
        <div style={{ width:"100%", maxWidth:320, display:"flex", flexDirection:"column", gap:12 }}>
          <button onClick={()=>setMode("staff")} style={{ background:`linear-gradient(135deg,${C.gold},${C.gold2})`, color:C.bg, border:"none", borderRadius:14, padding:16, fontSize:15, fontWeight:600, cursor:"pointer" }}>スタッフとしてログイン</button>
          <button onClick={()=>{ setMode("pw"); }} style={{ background:"transparent", color:C.gold, border:`1px solid ${C.gold}44`, borderRadius:14, padding:14, fontSize:13, cursor:"pointer" }}>オーナーとしてログイン</button>
        </div>
      )}

      {mode==="staff" && !selectedStore && !selectedStaff && !clerkMode && (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ fontSize:12, color:C.muted, textAlign:"center", marginBottom:14 }}>店舗を選んでください</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {stores.map(st=>(
              <button key={st.id} onClick={()=>setSelectedStore(st)} style={{
                background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:12,
                padding:"14px 16px", color:C.cream, fontSize:15, fontWeight:500, cursor:"pointer",
                display:"flex", justifyContent:"space-between", alignItems:"center"
              }}><span>{st.name}</span><span style={{ fontSize:11, color:C.muted }}>›</span></button>
            ))}
            {/* 🌸 事務員ログイン用 */}
            <button onClick={()=>setClerkMode(true)} style={{
              background:C.bg1, border:`1px dashed ${C.gold}66`, borderRadius:12,
              padding:"14px 16px", color:C.gold, fontSize:14, fontWeight:500, cursor:"pointer",
              display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4
            }}>
              <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span>📋 スタッフ</span>
                <span style={{ fontSize:9, color:C.muted, border:`1px solid ${C.muted}55`, borderRadius:10, padding:"2px 6px" }}>事務員・売上一覧のみ</span>
              </span>
              <span style={{ fontSize:11, color:C.muted }}>›</span>
            </button>
          </div>
          <button onClick={()=>setMode(null)} style={{ width:"100%", marginTop:12, background:"none", border:"none", color:C.muted, fontSize:12, cursor:"pointer" }}>← 戻る</button>
        </div>
      )}

      {/* 🌸 事務員PW入力 */}
      {mode==="staff" && clerkMode && (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ fontSize:14, color:C.cream, textAlign:"center", fontWeight:600, marginBottom:4 }}>📋 スタッフ（事務員）</div>
          <div style={{ fontSize:12, color:C.muted, textAlign:"center", marginBottom:14 }}>パスワードを入力してください</div>
          <input type="password" value={clerkPw} onChange={e=>setClerkPw(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&checkClerkPw()}
            placeholder="パスワード"
            style={{ width:"100%", background:C.bg1, border:`1px solid ${clerkPwErr?C.red:C.border2}`,
              borderRadius:10, padding:"12px 14px", color:C.cream, fontSize:14, marginBottom:8, outline:"none", textAlign:"center" }}/>
          {clerkPwErr && <div style={{ fontSize:11, color:C.red, textAlign:"center", marginBottom:8 }}>パスワードが違います</div>}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={checkClerkPw} style={{ flex:2, background:`linear-gradient(135deg,${C.gold},${C.gold2})`, color:C.bg, border:"none", borderRadius:10, padding:12, fontSize:13, fontWeight:600, cursor:"pointer" }}>ログイン</button>
            <button onClick={()=>{ setClerkMode(false); setClerkPw(""); setClerkPwErr(false); }} style={{ flex:1, background:C.bg1, color:C.muted, border:`1px solid ${C.border}`, borderRadius:10, padding:12, fontSize:12, cursor:"pointer" }}>戻る</button>
          </div>
        </div>
      )}

      {mode==="staff" && selectedStore && !selectedStaff && (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ fontSize:13, color:C.gold, textAlign:"center", fontWeight:600, marginBottom:4 }}>{selectedStore.name}</div>
          <div style={{ fontSize:12, color:C.muted, textAlign:"center", marginBottom:14 }}>あなたを選んでください</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {staff.filter(s=>(s.storeIds||[s.storeId]).includes(selectedStore.id)).map(s=>(
              <button key={s.id} onClick={()=>setSelectedStaff(s)} style={{
                background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:12,
                padding:"12px 16px", color:C.cream, fontSize:14, cursor:"pointer",
                display:"flex", justifyContent:"space-between", alignItems:"center"
              }}><span>{s.name}</span><span style={{ fontSize:11, color:C.muted }}>›</span></button>
            ))}
          </div>
          <button onClick={()=>setSelectedStore(null)} style={{ width:"100%", marginTop:12, background:"none", border:"none", color:C.muted, fontSize:12, cursor:"pointer" }}>← 店舗選択に戻る</button>
        </div>
      )}

      {mode==="staff" && selectedStaff && (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ fontSize:14, color:C.cream, textAlign:"center", fontWeight:600, marginBottom:4 }}>{selectedStaff.name}</div>
          <div style={{ fontSize:12, color:C.muted, textAlign:"center", marginBottom:14 }}>パスワードを入力してください</div>
          <input type="password" value={staffPw} onChange={e=>setStaffPw(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&checkStaffPw()}
            placeholder="パスワード"
            style={{ width:"100%", background:C.bg1, border:`1px solid ${staffPwErr?C.red:C.border2}`,
              borderRadius:10, padding:"12px 14px", color:C.cream, fontSize:14, marginBottom:8, outline:"none", textAlign:"center" }}/>
          {staffPwErr && <div style={{ fontSize:11, color:C.red, textAlign:"center", marginBottom:8 }}>パスワードが違います</div>}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={checkStaffPw} style={{ flex:2, background:`linear-gradient(135deg,${C.gold},${C.gold2})`, color:C.bg, border:"none", borderRadius:10, padding:12, fontSize:13, fontWeight:600, cursor:"pointer" }}>ログイン</button>
            <button onClick={()=>{ setSelectedStaff(null); setStaffPw(""); setStaffPwErr(false); }} style={{ flex:1, background:C.bg1, color:C.muted, border:`1px solid ${C.border}`, borderRadius:10, padding:12, fontSize:12, cursor:"pointer" }}>戻る</button>
          </div>
        </div>
      )}

      {mode==="pw" && (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ fontSize:12, color:C.muted, textAlign:"center", marginBottom:10 }}>オーナーパスワード</div>
          <input type="password" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&checkPw()} placeholder="パスワード"
            style={{ width:"100%", background:C.bg1, border:`1px solid ${err?C.red:C.border2}`, borderRadius:10, padding:"12px 14px", color:C.cream, fontSize:14, marginBottom:8, outline:"none" }}/>
          {err && <div style={{ fontSize:11, color:C.red, textAlign:"center", marginBottom:8 }}>パスワードが違います</div>}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={checkPw} style={{ flex:2, background:C.gold, color:C.bg, border:"none", borderRadius:10, padding:12, fontSize:13, fontWeight:600, cursor:"pointer" }}>ログイン</button>
            <button onClick={()=>{ setMode(null); setPass(""); }} style={{ flex:1, background:C.bg1, color:C.muted, border:`1px solid ${C.border}`, borderRadius:10, padding:12, fontSize:12, cursor:"pointer" }}>戻る</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD — reordered: KPI → 店舗別 → ランキング → グラフ
// ═══════════════════════════════════════════════════════════════════════════
function Dashboard({ records, storeSales=[], staff, stores, role, activeStaffId, onNav, orders, setOrders, approve, rejectOpen }) {
  const cc = useC(role);
  const [viewYM, setViewYM] = useState(nowYM());
  const curYM = viewYM;
  // 🌸 スタッフは自分の所属店舗を初期選択（兼任は最初の店舗）、オーナーは全店
  const initialStoreFilter = (()=>{
    if (role === "staff" && activeStaffId) {
      const me = staff.find(s => s.id === activeStaffId);
      const myStores = me?.storeIds || (me?.storeId ? [me.storeId] : []);
      if (myStores.length > 0) return myStores[0];
    }
    return "all";
  })();
  const [storeFilter, setStoreFilter] = useState(initialStoreFilter);
  const [rankTab, setRankTab] = useState("sales");
  const [showAllSales, setShowAllSales] = useState(false);
  const [showAllAvg, setShowAllAvg] = useState(false);
  const [showAllShimei, setShowAllShimei] = useState(false);

  // 過去6ヶ月分の選択肢
  const monthOptions = useMemo(()=>{
    const opts = [];
    const d = new Date();
    for (let i=0; i<6; i++) {
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      opts.push({ value: key, label: `${d.getFullYear()}年${d.getMonth()+1}月${i===0?"（今月）":i===1?"（前月）":""}` });
      d.setMonth(d.getMonth()-1);
    }
    return opts;
  }, []);

  const months6 = useMemo(()=>Array.from({length:6},(_,i)=>{
    const d=new Date(); d.setDate(1); d.setMonth(d.getMonth()-5+i);
    const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const src=storeFilter==="all"?storeSales:storeSales.filter(r=>r.storeId===storeFilter);
    return { label:`${d.getMonth()+1}月`, v:src.filter(r=>ym(r.date)===key).reduce((a,r)=>a+(r.totalSales||0),0) };
  }),[storeSales,storeFilter]);


  // 🌸 KPIは「店舗売上」ベース（個人売上はインセンティブ計算用）
  const filteredStoreSales = (storeFilter==="all"?storeSales:storeSales.filter(s=>s.storeId===storeFilter)).filter(s=>ym(s.date)===curYM);
  const totalSales  = filteredStoreSales.reduce((a,s)=>a+(s.totalSales||0),0);
  const totalVisits = filteredStoreSales.reduce((a,s)=>a+(s.totalGuests||0),0);
  const avgSpend    = totalVisits>0?Math.round(totalSales/totalVisits):0;

  const storeStats = stores.map(st=>{
    // 🌸 店舗の売上は店舗売上ベース
    const ssales = storeSales.filter(s=>s.storeId===st.id&&ym(s.date)===curYM);
    const sales = ssales.reduce((a,s)=>a+(s.totalSales||0),0);
    const visits = ssales.reduce((a,s)=>a+(s.totalGuests||0),0);
    // 個人売上から労働時間を集計
    const sr=records.filter(r=>r.storeId===st.id&&ym(r.date)===curYM);
    const workHours=Math.round(sr.reduce((a,r)=>a+(r.workHours||0),0)*10)/10;
    const target = getMonthTarget(st, curYM);
    const expenseSupply     = Math.round(target * EXPENSE_RATE_SUPPLY);
    const expenseConsumable = Math.round(target * EXPENSE_RATE_CONSUMABLE);
    const expenseBudget     = expenseSupply + expenseConsumable;

    // 🌸 営業日計算（B案：定休日除外）
    const recordCount = ssales.length; // 店舗売上が入力された日数 = 経過営業日
    const avgPerDay = recordCount > 0 ? Math.round(sales / recordCount) : 0;

    // 今月の総営業日数（定休除外）と残り営業日
    const [yyyy, mm] = curYM.split("-").map(Number);
    const daysInMo = new Date(yyyy, mm, 0).getDate();
    const closedDays = st.shiftClosedDays || []; // [0,1,2...] 数字配列（0=日,1=月...）
    const irregularClosed = st.irregularClosedDates || []; // ["2026-05-15"...]
    let totalBusinessDays = 0, remainBusinessDays = 0;
    const todayStrLocal = (()=>{ const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
    const isCurrentMonth = curYM === nowYM();
    for (let d = 1; d <= daysInMo; d++) {
      const dateStr = `${yyyy}-${String(mm).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const dowNum = new Date(yyyy, mm-1, d).getDay(); // 数字 0-6
      if (closedDays.includes(dowNum)) continue;
      if (irregularClosed.includes(dateStr)) continue;
      totalBusinessDays++;
      // 残り営業日 = 今日より後の営業日（過去月なら0、未来月なら全営業日）
      if (isCurrentMonth) {
        if (dateStr > todayStrLocal) remainBusinessDays++;
      } else if (curYM > nowYM()) {
        remainBusinessDays++;
      }
    }

    return { ...st, target, sales, visits, avg: visits>0?Math.round(sales/visits):0,
      pct: target>0 ? Math.min(100,Math.round(sales/target*100)) : 0, workHours,
      expenseSupply, expenseConsumable, expenseBudget,
      recordCount, totalBusinessDays, remainBusinessDays, avgPerDay };
  });
  const maxStoreSales = Math.max(...storeStats.map(s=>s.sales),1);

  const staffStats = useMemo(()=>{
    const src=storeFilter==="all"?staff:staff.filter(s=>(s.storeIds||[s.storeId]).includes(storeFilter));
    return src.map(s=>{
      const sr=records.filter(r=>r.staffId===s.id&&ym(r.date)===curYM);
      const sales=sr.reduce((a,r)=>a+r.amount,0);
      const totalGuests=sr.reduce((a,r)=>a+(r.totalGuests||0),0);
      const shimei=sr.reduce((a,r)=>a+(r.shimei||0),0);
      // visits = 総客数, recordCount = 出社日数(レコード数), avg = 売上÷総客数
      return { ...s, sales, visits:totalGuests, recordCount:sr.length, shimei,
        avg:totalGuests>0?Math.round(sales/totalGuests):0,
        pct:Math.min(100,Math.round(sales/(s.target||1)*100)) };
    }).sort((a,b)=>b.sales-a.sales);
  },[records,staff,storeFilter,curYM]);

  const selfStat = role==="staff"&&activeStaffId ? staffStats.find(s=>s.id===activeStaffId) : null;

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      {/* 月選択 */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
        <div style={{ fontSize:10, color:cc.muted, whiteSpace:"nowrap" }}>📅</div>
        <select value={viewYM} onChange={e=>setViewYM(e.target.value)}
          style={{ flex:1, background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:6, padding:"4px 8px", color:cc.cream, fontSize:11 }}>
          {monthOptions.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      {/* store filter */}
      <div style={{ display:"flex", gap:4, marginBottom:8, overflowX:"auto" }}>
        {[{id:"all",name:"全店"},...stores].map(st=>(
          <button key={st.id} onClick={()=>setStoreFilter(st.id)} style={{
            background:storeFilter===st.id?cc.gold:cc.bg2, color:storeFilter===st.id?cc.bg:cc.muted,
            border:`1px solid ${storeFilter===st.id?cc.gold:cc.border}`,
            borderRadius:14, padding:"3px 10px", fontSize:10, cursor:"pointer", whiteSpace:"nowrap"
          }}>{st.name}</button>
        ))}
      </div>

      {/* 1. KPI */}
      {(()=>{
        // 🌸 店舗目標を計算（全店なら合計、特定店なら該当店舗）
        const targetSum = (storeFilter==="all"
          ? stores.reduce((a,st)=>a+getMonthTarget(st, curYM),0)
          : (()=>{ const st=stores.find(s=>s.id===storeFilter); return getMonthTarget(st, curYM); })()
        );
        const achievePct = targetSum > 0 ? Math.round(totalSales/targetSum*100) : 0;
        return (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:8 }}>
            <div style={{ ...cardS(role, {padding:"7px 8px"}), textAlign:"center", minWidth:0, overflow:"hidden" }}>
              <div style={{ fontSize:9, color:cc.muted2, marginBottom:2 }}>今月売上</div>
              <div style={{ fontSize:13, fontWeight:700, color:cc.gold, fontFamily:"Cormorant Garamond" }}>{fmt(totalSales)}</div>
              {targetSum > 0 && (
                <div style={{ fontSize:8, color:achievePct>=100?cc.green:cc.muted2, marginTop:1 }}>
                  目標 {fmt(targetSum)} / {achievePct}%
                </div>
              )}
            </div>
            <div style={{ ...cardS(role, {padding:"7px 8px"}), textAlign:"center", minWidth:0, overflow:"hidden" }}>
              <div style={{ fontSize:9, color:cc.muted2, marginBottom:2 }}>総客数</div>
              <div style={{ fontSize:13, fontWeight:700, color:cc.gold2, fontFamily:"Cormorant Garamond" }}>{totalVisits}名</div>
            </div>
            <div style={{ ...cardS(role, {padding:"7px 8px"}), textAlign:"center", minWidth:0, overflow:"hidden" }}>
              <div style={{ fontSize:9, color:cc.muted2, marginBottom:2 }}>客単価</div>
              <div style={{ fontSize:13, fontWeight:700, color:cc.accent, fontFamily:"Cormorant Garamond" }}>{fmt(avgSpend)}</div>
            </div>
          </div>
        );
      })()}

      {/* self highlight for staff：今月の目標カード（コンパクト版） */}
      {selfStat && role==="staff" && (()=>{
        const myStore = stores.find(st=>st.id===selfStat.storeId);
        const storeSalesAmt = records.filter(r=>r.storeId===selfStat.storeId&&ym(r.date)===curYM).reduce((a,r)=>a+r.amount,0);
        const storeTarget = getMonthTarget(myStore, curYM);
        const storePct = storeTarget>0?Math.min(100,Math.round(storeSalesAmt/storeTarget*100)):0;
        // 🌸 前月売上の6.5%を経費目安として計算
        const prevYM = (()=>{
          const [y,m] = curYM.split("-").map(Number);
          const pm = m === 1 ? 12 : m - 1;
          const py = m === 1 ? y - 1 : y;
          return `${py}-${String(pm).padStart(2,"0")}`;
        })();
        const prevMonthStoreSales = storeSales.filter(s=>s.storeId===selfStat.storeId && ym(s.date)===prevYM).reduce((a,s)=>a+(s.totalSales||0),0);
        const expenseEstimate = Math.round(prevMonthStoreSales * 0.065);
        return (
          <div style={{ marginBottom:8 }}>
            <div style={{ ...cardS(role,{marginBottom:6,padding:"8px 10px",background:cc.bg2,border:`1px solid ${cc.gold}44`}) }}>
              <div style={{ fontSize:10,color:cc.muted,fontWeight:600,marginBottom:6 }}>📊 今月の目標</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:6 }}>
                <div style={{ background:cc.bg1,borderRadius:6,padding:"6px 8px" }}>
                  <div style={{ fontSize:9,color:cc.muted2 }}>個人目標</div>
                  <div style={{ fontSize:12,fontFamily:"Cormorant Garamond",color:cc.gold,fontWeight:600 }}>{fmt(selfStat.target||0)}</div>
                  <div style={{ marginTop:3,height:3,background:cc.bg3,borderRadius:2 }}>
                    <div style={{ height:"100%",width:`${selfStat.pct}%`,background:cc.gold,borderRadius:2,transition:"width 0.8s" }}/>
                  </div>
                  <div style={{ fontSize:8,color:selfStat.pct>=100?cc.green:cc.muted2,marginTop:2,textAlign:"right" }}>{selfStat.pct}% 達成</div>
                </div>
                <div style={{ background:cc.bg1,borderRadius:6,padding:"6px 8px" }}>
                  <div style={{ fontSize:9,color:cc.muted2 }}>{myStore?.name} 目標</div>
                  <div style={{ fontSize:12,fontFamily:"Cormorant Garamond",color:cc.accent,fontWeight:600 }}>{fmt(storeTarget)}</div>
                  <div style={{ marginTop:3,height:3,background:cc.bg3,borderRadius:2 }}>
                    <div style={{ height:"100%",width:`${storePct}%`,background:cc.accent,borderRadius:2,transition:"width 0.8s" }}/>
                  </div>
                  <div style={{ fontSize:8,color:storePct>=100?cc.green:cc.muted2,marginTop:2,textAlign:"right" }}>{storePct}% 達成</div>
                </div>
              </div>
              {/* 🌸 経費目安（前月売上 × 6.5%） */}
              {expenseEstimate > 0 && (
                <div style={{ marginTop:6, background:cc.bg1, borderRadius:6, padding:"6px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <span style={{ fontSize:9, color:cc.muted2 }}>💰 今月の経費目安</span>
                    <span style={{ fontSize:8, color:cc.muted2, marginLeft:6 }}>（前月売上 × 6.5%・アスクル他消耗品含む）</span>
                  </div>
                  <span style={{ fontSize:13, fontFamily:"Cormorant Garamond", color:cc.gold2, fontWeight:600 }}>{fmt(expenseEstimate)}</span>
                </div>
              )}
            </div>
            <div style={{ ...cardS(role,{padding:"7px 10px",background:cc.bg2,border:`1px solid ${cc.gold}22`}),display:"flex",alignItems:"center",gap:10 }}>
              <Donut pct={selfStat.pct} size={50}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:9,color:cc.muted }}>あなたの今月実績</div>
                <div style={{ fontSize:16,fontFamily:"Cormorant Garamond",color:cc.gold,lineHeight:1.1 }}>{fmt(selfStat.sales)}</div>
                <div style={{ fontSize:9,color:cc.muted,marginTop:1 }}>総客数 {selfStat.visits}名｜客単価 {fmt(selfStat.avg)}｜指名 {selfStat.shimei}本</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 🌸 スタッフショートカット：今月のシフト & 発注申請 */}
      {role==="staff" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
          <button onClick={()=>onNav("myshift")} style={{
            background:cc.bg2, border:`1px solid ${cc.gold}44`, borderRadius:10,
            padding:"12px 8px", cursor:"pointer", textAlign:"center"
          }}>
            <div style={{ fontSize:18, marginBottom:2 }}>📅</div>
            <div style={{ fontSize:11, color:cc.cream, fontWeight:500 }}>今月のシフト</div>
          </button>
          <button onClick={()=>onNav("order")} style={{
            background:cc.bg2, border:`1px solid ${cc.gold}44`, borderRadius:10,
            padding:"12px 8px", cursor:"pointer", textAlign:"center"
          }}>
            <div style={{ fontSize:18, marginBottom:2 }}>📦</div>
            <div style={{ fontSize:11, color:cc.cream, fontWeight:500 }}>発注申請</div>
          </button>
        </div>
      )}

      {/* 🌸 オーナー：本日の売上（店舗別）※18時前は前日分を表示 */}
      {role==="owner" && (()=>{
        const _now = new Date();
        // 18時前なら前日扱い
        const baseDate = new Date(_now);
        if (_now.getHours() < 18) {
          baseDate.setDate(baseDate.getDate() - 1);
        }
        const _today = `${baseDate.getFullYear()}-${String(baseDate.getMonth()+1).padStart(2,"0")}-${String(baseDate.getDate()).padStart(2,"0")}`;
        const todayStores = stores.map(st=>{
          const today = storeSales.filter(s=>s.storeId===st.id && s.date===_today);
          const sales = today.reduce((a,s)=>a+(s.totalSales||0),0);
          const visits = today.reduce((a,s)=>a+(s.totalGuests||0),0);
          return { ...st, todaySales:sales, todayVisits:visits };
        });
        const todayTotal = todayStores.reduce((a,s)=>a+s.todaySales,0);
        const todayLabel = `${baseDate.getMonth()+1}/${baseDate.getDate()}`;
        const isYesterday = _now.getHours() < 18;
        return (
          <div style={{ ...cardS(role,{padding:10, border:`1px solid ${cc.gold}55`}), marginBottom:8 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <span style={{ fontSize:11, color:cc.gold, fontWeight:600 }}>📍 {isYesterday ? "前日" : "本日"}（{todayLabel}）の売上</span>
              <span style={{ fontSize:13, color:cc.gold, fontFamily:"Cormorant Garamond", fontWeight:700 }}>{fmt(todayTotal)}</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
              {todayStores.map(st=>(
                <div key={st.id} style={{ background:cc.bg2, borderRadius:6, padding:"6px 9px", minWidth:0, overflow:"hidden" }}>
                  <div style={{ fontSize:10, color:cc.muted, marginBottom:1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{st.name}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                    <span style={{ fontSize:13, color:cc.gold, fontWeight:600, fontFamily:"Cormorant Garamond" }}>{fmt(st.todaySales)}</span>
                    <span style={{ fontSize:9, color:cc.muted2 }}>{st.todayVisits}名</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 🌸 オーナー：経費目安カード（前月売上 × 6.5%） */}
      {role==="owner" && (()=>{
        const prevYM = (()=>{
          const [y,m] = curYM.split("-").map(Number);
          const pm = m === 1 ? 12 : m - 1;
          const py = m === 1 ? y - 1 : y;
          return `${py}-${String(pm).padStart(2,"0")}`;
        })();
        const storeExpenses = stores.map(st=>{
          const prevSales = storeSales.filter(s=>s.storeId===st.id && ym(s.date)===prevYM).reduce((a,s)=>a+(s.totalSales||0),0);
          return { id:st.id, name:st.name, expense: Math.round(prevSales * 0.065) };
        });
        const totalExpense = storeExpenses.reduce((a,s)=>a+s.expense, 0);
        if (totalExpense === 0) return null;
        return (
          <div style={{ ...cardS(role,{padding:10}), marginBottom:8 }}>
            <div style={{ marginBottom:6 }}>
              <span style={{ fontSize:11, color:cc.muted, fontWeight:600 }}>💰 今月の経費目安（前月売上 × 6.5%・アスクル他消耗品含む）</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
              {storeExpenses.map(st=>(
                <div key={st.id} style={{ background:cc.bg2, borderRadius:6, padding:"5px 9px", minWidth:0, display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <span style={{ fontSize:10, color:cc.muted, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{st.name}</span>
                  <span style={{ fontSize:11, color:cc.gold2, fontFamily:"Cormorant Garamond", fontWeight:600 }}>{fmt(st.expense)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 🌸 オーナーショートカット：シフト管理 / スタッフ売上 / 店舗売上 */}
      {role==="owner" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:8 }}>
          <button onClick={()=>onNav("shiftmgr")} style={{
            background:cc.bg2, border:`1px solid ${cc.gold}44`, borderRadius:10,
            padding:"10px 6px", cursor:"pointer", textAlign:"center"
          }}>
            <div style={{ fontSize:16, marginBottom:1 }}>📅</div>
            <div style={{ fontSize:10, color:cc.cream, fontWeight:500 }}>シフト管理</div>
          </button>
          <button onClick={()=>onNav("staffsales")} style={{
            background:cc.bg2, border:`1px solid ${cc.gold}44`, borderRadius:10,
            padding:"10px 6px", cursor:"pointer", textAlign:"center"
          }}>
            <div style={{ fontSize:16, marginBottom:1 }}>📊</div>
            <div style={{ fontSize:10, color:cc.cream, fontWeight:500 }}>スタッフ売上</div>
          </button>
          <button onClick={()=>onNav("saleslist")} style={{
            background:cc.bg2, border:`1px solid ${cc.gold}44`, borderRadius:10,
            padding:"10px 6px", cursor:"pointer", textAlign:"center"
          }}>
            <div style={{ fontSize:16, marginBottom:1 }}>🏪</div>
            <div style={{ fontSize:10, color:cc.cream, fontWeight:500 }}>店舗売上</div>
          </button>
        </div>
      )}

      {/* 2. 店舗別今月（コンパクト版） */}
      <div style={{ ...cardS(role,{padding:10}), marginBottom:8, maxWidth:"100%", overflow:"hidden", boxSizing:"border-box" }}>
        <div style={{ fontSize:10, color:cc.muted, marginBottom:6 }}>店舗別今月</div>
        {storeStats.map(st=>(
          <div key={st.id} style={{ marginBottom:9, minWidth:0 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2, gap:8, minWidth:0 }}>
              <span style={{ fontSize:11, color:cc.cream, fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{st.name}</span>
              <span style={{ fontSize:12, color:cc.gold, fontWeight:600, whiteSpace:"nowrap", flexShrink:0 }}>{fmt(st.sales)}</span>
            </div>
            <div style={{ height:4, background:cc.bg2, borderRadius:2, marginBottom:3 }}>
              <div style={{ height:"100%", width:`${(st.sales/maxStoreSales)*100}%`, background:cc.gold, borderRadius:2, transition:"width 0.8s" }}/>
            </div>
            {/* 1段目：総客数 / 客単価 */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:"2px 10px", fontSize:9, color:cc.muted2, marginBottom:1 }}>
              <span>総客数 {st.visits}名</span>
              <span>客単価 {fmt(st.avg)}</span>
              {st.target>0 && <span style={{ color:st.pct>=100?cc.green:cc.muted2 }}>目標 {st.pct}%</span>}
            </div>
            {/* 2段目：営業日 / 残り / 1日平均 */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:"2px 10px", fontSize:9, color:cc.muted2 }}>
              <span>営業 {st.recordCount}/{st.totalBusinessDays}日</span>
              <span>残 {st.remainBusinessDays}日</span>
              <span>1日平均 {fmt(st.avgPerDay)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* 🌸 オーナー：発注申請を店舗別売上とスタッフランキングの間に配置 */}
      {role==="owner" && (()=>{
        const pending=orders.filter(o=>o.status==="申請中");
        if(pending.length===0) return null;
        return (
          <div style={{ ...cardS(role,{marginBottom:12, border:`1px solid ${cc.gold}55`}) }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <span style={{ fontSize:16 }}>📦</span>
                <span style={{ fontSize:13,color:cc.gold,fontWeight:600 }}>発注申請 申請中</span>
                <span style={{ background:cc.gold,color:cc.bg,borderRadius:10,padding:"1px 8px",fontSize:10,fontWeight:700 }}>{pending.length}</span>
              </div>
              <button onClick={()=>onNav("order")} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:10,padding:"3px 10px",color:cc.muted,fontSize:10,cursor:"pointer" }}>すべて見る›</button>
            </div>
            {pending.map(o=>{
              const st=stores.find(s=>s.id===o.storeId);
              return (
                <div key={o.id} style={{ padding:"8px 0",borderBottom:`1px solid ${cc.border}` }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex",gap:6,alignItems:"center",marginBottom:2 }}>
                        <span style={{ fontSize:10,background:cc.bg2,color:cc.gold,borderRadius:10,padding:"1px 7px" }}>{o.category}</span>
                        <span style={{ fontSize:12,color:cc.cream,fontWeight:500 }}>{o.supplier}</span>
                        <span style={{ fontSize:11,color:cc.muted2 }}>{o.payMethod}</span>
                      </div>
                      <div style={{ fontSize:12,color:cc.gold,fontWeight:600,marginBottom:2 }}>{fmt(o.amount||0)}</div>
                      <div style={{ fontSize:10,color:cc.muted2 }}>{st?.name}｜{o.author}｜{o.date}{o.note?`｜${o.note}`:""}</div>
                      {o.fileName&&<div style={{ fontSize:10,color:cc.gold,marginTop:2 }}>📎 {o.fileName}</div>}
                    </div>
                    <div style={{ display:"flex",flexDirection:"column",gap:4,flexShrink:0 }}>
                      <button onClick={()=>approve(o.id)} style={{ background:"#2a4a3a",color:cc.green,border:`1px solid ${cc.green}`,borderRadius:10,padding:"4px 12px",fontSize:10,cursor:"pointer",fontWeight:600 }}>承認</button>
                      <button onClick={()=>rejectOpen(o.id)} style={{ background:"#3a1a14",color:cc.red,border:`1px solid ${cc.red}`,borderRadius:10,padding:"4px 12px",fontSize:10,cursor:"pointer" }}>差し戻し</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}


      {/* 3. スタッフランキング（表形式・簡素化）— スタッフのみ */}
      {role==="staff" && (
      <div style={{ ...cardS(role,{padding:0}), marginBottom:12, overflowX:"auto" }}>
        <div style={{ padding:"10px 12px", borderBottom:`1px solid ${cc.border}` }}>
          <span style={{ fontSize:11, color:cc.muted, fontWeight:600 }}>🏆 スタッフ実績</span>
          <span style={{ fontSize:9, color:cc.muted2, marginLeft:8 }}>売上順</span>
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:10, color:cc.cream, minWidth:0 }}>
          <thead>
            <tr style={{ background:cc.bg2 }}>
              <th style={{ padding:"6px 6px", textAlign:"left", fontWeight:600, color:cc.muted2, fontSize:9, width:30 }}>順位</th>
              <th style={{ padding:"6px 6px", textAlign:"left", fontWeight:600, color:cc.muted2, fontSize:9 }}>氏名</th>
              <th style={{ padding:"6px 6px", textAlign:"right", fontWeight:600, color:cc.muted2, fontSize:9 }}>出社</th>
              <th style={{ padding:"6px 6px", textAlign:"right", fontWeight:600, color:cc.muted2, fontSize:9 }}>売上</th>
              <th style={{ padding:"6px 6px", textAlign:"right", fontWeight:600, color:cc.muted2, fontSize:9 }}>客単価</th>
              <th style={{ padding:"6px 6px", textAlign:"right", fontWeight:600, color:cc.muted2, fontSize:9 }}>指名</th>
            </tr>
          </thead>
          <tbody>
            {(showAllSales ? staffStats : staffStats.slice(0,5)).map((s,i)=>{
              const isMe = role==="staff" && s.id===activeStaffId;
              const rankColor = i===0?cc.gold:i===1?"#9a9a9a":i===2?"#cd7f32":cc.muted2;
              return (
                <tr key={s.id} style={{
                  borderBottom:`1px solid ${cc.border}`,
                  background: isMe?`${cc.gold}11`:"none"
                }}>
                  <td style={{ padding:"7px 6px", color:rankColor, fontWeight:600 }}>{i+1}</td>
                  <td style={{ padding:"7px 6px", color:cc.cream, fontWeight:500 }}>
                    {s.name}
                    {isMe && <span style={{ fontSize:9, color:cc.gold, marginLeft:4 }}>▶</span>}
                  </td>
                  <td style={{ padding:"7px 6px", textAlign:"right", color:cc.muted }}>{s.recordCount||0}日</td>
                  <td style={{ padding:"7px 6px", textAlign:"right", color:cc.gold, fontWeight:600 }}>{fmt(s.sales)}</td>
                  <td style={{ padding:"7px 6px", textAlign:"right", color:cc.muted }}>{fmt(s.avg)}</td>
                  <td style={{ padding:"7px 6px", textAlign:"right", color:cc.muted }}>{s.shimei||0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {staffStats.length>5 && (
          <button onClick={()=>setShowAllSales(v=>!v)} style={{ width:"100%", background:"none", border:"none", color:cc.muted, fontSize:11, cursor:"pointer", padding:"8px 0", textAlign:"center" }}>
            {showAllSales ? "▲ 閉じる" : `▼ もっと見る（残り${staffStats.length-5}名）`}
          </button>
        )}
      </div>
      )}

      {/* 発注申請 申請中一覧（スタッフ用：従来位置） */}
      {role!=="owner" && (()=>{
        const pending=orders.filter(o=>o.status==="申請中");
        if(pending.length===0) return null;
        return (
          <div style={{ ...cardS(role,{marginBottom:12, border:`1px solid ${cc.gold}55`}) }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <span style={{ fontSize:16 }}>📦</span>
                <span style={{ fontSize:13,color:cc.gold,fontWeight:600 }}>発注申請 申請中</span>
                <span style={{ background:cc.gold,color:cc.bg,borderRadius:10,padding:"1px 8px",fontSize:10,fontWeight:700 }}>{pending.length}</span>
              </div>
              <button onClick={()=>onNav("order")} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:10,padding:"3px 10px",color:cc.muted,fontSize:10,cursor:"pointer" }}>すべて見る›</button>
            </div>
            {pending.map(o=>{
              const st=stores.find(s=>s.id===o.storeId);
              return (
                <div key={o.id} style={{ padding:"8px 0",borderBottom:`1px solid ${cc.border}` }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex",gap:6,alignItems:"center",marginBottom:2 }}>
                        <span style={{ fontSize:10,background:cc.bg2,color:cc.gold,borderRadius:10,padding:"1px 7px" }}>{o.category}</span>
                        <span style={{ fontSize:12,color:cc.cream,fontWeight:500 }}>{o.supplier}</span>
                        <span style={{ fontSize:11,color:cc.muted2 }}>{o.payMethod}</span>
                      </div>
                      <div style={{ fontSize:12,color:cc.gold,fontWeight:600,marginBottom:2 }}>{fmt(o.amount||0)}</div>
                      <div style={{ fontSize:10,color:cc.muted2 }}>{st?.name}｜{o.author}｜{o.date}{o.note?`｜${o.note}`:""}</div>
                      {o.fileName&&<div style={{ fontSize:10,color:cc.gold,marginTop:2 }}>📎 {o.fileName}</div>}
                    </div>
                    <span style={{ fontSize:10,color:cc.gold,background:`${cc.gold}22`,borderRadius:10,padding:"2px 8px",flexShrink:0 }}>申請中</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* 4. 月次グラフ */}
      <div style={{ ...cardS(role), marginBottom:12 }}>
        <div style={{ fontSize:11, color:cc.muted, marginBottom:10 }}>月次売上推移</div>
        <MiniBar data={months6} h={80}/>
      </div>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SALES LIST + CSV (日別・月別)
// ═══════════════════════════════════════════════════════════════════════════
function SalesList({ records, storeSales, setStoreSales, staff, stores, role, reloadSalesFromGAS, cashRegisterOuts=[] }) {
  const curYM = nowYM();
  const [viewMode, setViewMode] = useState("daily"); // daily | monthly
  const [monthFilter, setMonthFilter] = useState(curYM);
  const [storeFilter, setStoreFilter] = useState("all");
  // 🌸 編集モーダル
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMsg, setSaveMsg] = useState("");

  // 🌸 売上一覧は「店舗売上」ベース（個人売上はインセンティブ計算用）
  // available months
  const allMonths = useMemo(()=>{
    const set = new Set(storeSales.map(r=>ym(r.date)));
    return [...set].sort((a,b)=>b.localeCompare(a));
  },[storeSales]);

  // filtered store sales
  const filtered = useMemo(()=>{
    let r = storeSales.filter(rec=>ym(rec.date)===monthFilter);
    if (storeFilter!=="all") r=r.filter(rec=>rec.storeId===storeFilter);
    return r;
  },[storeSales,monthFilter,storeFilter]);

  // daily aggregation（店舗売上ベース）— 店舗別に分けて表示
  const dailyRows = useMemo(()=>{
    const map={};
    filtered.forEach(r=>{
      const k = `${r.date}_${r.storeId}`;
      if(!map[k]) map[k]={ date:r.date, storeId:r.storeId, sales:0, visits:0, cash:0, credit:0, point:0, other:0, register:0 };
      map[k].sales += (r.totalSales || 0);
      map[k].visits += (r.totalGuests || 0);
      map[k].cash += (r.cashSales || 0);
      map[k].credit += (r.creditSales || 0);
      map[k].point += (r.pointSales || 0);
      map[k].other += (r.otherSales || 0);
      map[k].register = (r.register || 0);
    });
    return Object.values(map).sort((a,b)=>{
      if (a.date !== b.date) return b.date.localeCompare(a.date);
      return String(a.storeId).localeCompare(String(b.storeId));
    });
  },[filtered]);

  // monthly aggregation（店舗売上ベース）
  const monthlyRows = useMemo(()=>{
    const src = storeFilter==="all"?storeSales:storeSales.filter(r=>r.storeId===storeFilter);
    const map={};
    src.forEach(r=>{
      const key=ym(r.date);
      if(!map[key]) map[key]={ month:key, sales:0, visits:0, cash:0, credit:0, point:0, other:0 };
      map[key].sales += (r.totalSales || 0);
      map[key].visits += (r.totalGuests || 0);
      map[key].cash += (r.cashSales || 0);
      map[key].credit += (r.creditSales || 0);
      map[key].point += (r.pointSales || 0);
      map[key].other += (r.otherSales || 0);
    });
    return Object.values(map).sort((a,b)=>b.month.localeCompare(a.month));
  },[storeSales,storeFilter]);

  // 店舗別月間契約実働時間（staff.contractHoursの合計）
  const storeMonthlyHours = useMemo(()=>{
    const result={};
    stores.forEach(st=>{
      result[st.id] = staff.filter(s=>s.storeId===st.id).reduce((a,s)=>a+(s.contractHours||0),0);
    });
    result["all"] = staff.reduce((a,s)=>a+(s.contractHours||0),0);
    return result;
  },[staff,stores]);

  const exportDailyCSV = () => {
    const storeName = storeFilter==="all"?"全店":stores.find(s=>s.id===storeFilter)?.name||"";
    const header = ["日付","店舗","現金売上","クレジット売上","ポイント売上","その他売上","売上合計","レジ残高","来客数","客単価"];
    const rows   = dailyRows.map(r=>{
      const st = stores.find(s=>s.id===r.storeId);
      return [r.date, st?.name||"-", r.cash, r.credit, r.point, r.other, r.sales, r.register, r.visits, r.visits>0?Math.round(r.sales/r.visits):0];
    });
    downloadCSV(`日別売上_${monthFilter}_${storeName||"全店"}.csv`, [header,...rows]);
  };
  const exportMonthlyCSV = () => {
    const storeName = storeFilter==="all"?"全店":stores.find(s=>s.id===storeFilter)?.name||"";
    const contractH = storeMonthlyHours[storeFilter] || 0;
    const header = ["年月","店舗","現金売上","クレジット売上","ポイント売上","その他売上","売上合計","来客数","客単価","店舗月間契約実働時間(h)","時間当たり売上"];
    const rows   = monthlyRows.map(r=>[
      r.month, storeName, r.cash, r.credit, r.point, r.other, r.sales, r.visits,
      r.visits>0?Math.round(r.sales/r.visits):0,
      contractH,
      contractH>0?Math.round(r.sales/contractH):"-"
    ]);
    downloadCSV(`月別売上_${storeName||"全店"}.csv`, [header,...rows]);
  };
  const exportCashOutCSV = () => {
    const storeName = storeFilter==="all"?"全店":stores.find(s=>s.id===storeFilter)?.name||"";
    let outs = (cashRegisterOuts||[]).filter(o=>ym(o.date)===monthFilter);
    if (storeFilter!=="all") outs = outs.filter(o=>o.storeId===storeFilter);
    outs = outs.sort((a,b)=>b.date.localeCompare(a.date));
    const header = ["日付","店舗","出金額","メモ","入力者"];
    const rows = outs.map(o=>[o.date, stores.find(s=>s.id===o.storeId)?.name||"-", o.amount||0, o.memo||"", o.inputBy||""]);
    downloadCSV(`レジ出金_${monthFilter}_${storeName||"全店"}.csv`, [header,...rows]);
  };

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      {/* controls */}
      <div style={{ display:"flex", gap:6, marginBottom:10, overflowX:"auto" }}>
        {[{id:"daily",label:"日別"},{id:"monthly",label:"月別"}].map(m=>(
          <button key={m.id} onClick={()=>setViewMode(m.id)} style={{
            background:viewMode===m.id?C.gold:C.bg2, color:viewMode===m.id?C.bg:C.muted,
            border:`1px solid ${viewMode===m.id?C.gold:C.border}`, borderRadius:20, padding:"5px 16px", fontSize:12, cursor:"pointer"
          }}>{m.label}</button>
        ))}
        <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
          <button onClick={viewMode==="daily"?exportDailyCSV:exportMonthlyCSV} style={{
            background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:20,
            padding:"5px 14px", fontSize:11, color:C.gold, cursor:"pointer", whiteSpace:"nowrap"
          }}>📥 売上CSV</button>
          <button onClick={exportCashOutCSV} style={{
            background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:20,
            padding:"5px 14px", fontSize:11, color:C.gold, cursor:"pointer", whiteSpace:"nowrap"
          }}>💸 出金CSV</button>
        </div>
      </div>

      {/* store filter */}
      <div style={{ display:"flex", gap:6, marginBottom:12, overflowX:"auto" }}>
        {[{id:"all",name:"全店"},...stores].map(st=>(
          <button key={st.id} onClick={()=>setStoreFilter(st.id)} style={{
            background:storeFilter===st.id?C.gold:C.bg2, color:storeFilter===st.id?C.bg:C.muted,
            border:`1px solid ${storeFilter===st.id?C.gold:C.border}`,
            borderRadius:20, padding:"4px 12px", fontSize:11, cursor:"pointer", whiteSpace:"nowrap"
          }}>{st.name}</button>
        ))}
      </div>

      {/* month selector (daily only) */}
      {viewMode==="daily" && (
        <div style={{ marginBottom:12 }}>
          <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
            style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 10px", color:C.cream, fontSize:12 }}>
            {allMonths.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      )}

      {/* daily list — タップで編集可能なカード形式 */}
      {viewMode==="daily" && (
        <div style={card()}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:12 }}>
            <div style={{ fontSize:11, color:C.muted }}>日別売上一覧（{filtered.length}件）</div>
            {(role==="owner"||role==="clerk") && filtered.length>0 && (
              <div style={{ fontSize:9, color:C.muted2 }}>💡 タップで編集</div>
            )}
          </div>
          {filtered.length===0
            ? <div style={{ textAlign:"center", fontSize:12, color:C.muted2, padding:16 }}>データがありません</div>
            : [...filtered].sort((a,b)=>{
                if(a.date!==b.date) return b.date.localeCompare(a.date);
                return String(a.storeId).localeCompare(String(b.storeId));
              }).map(s=>{
                const st=stores.find(x=>x.id===s.storeId);
                const canEdit = role==="owner" || role==="clerk";
                return (
                  <div key={s.key||`${s.date}_${s.storeId}`}
                    onClick={canEdit ? ()=>{
                      setEditTarget(s);
                      setEditForm({
                        newGuests: s.newGuests||0,
                        regularGuests: s.regularGuests||0,
                        cashSales: s.cashSales||0,
                        creditSales: s.creditSales||0,
                        pointSales: s.pointSales||0,
                        otherSales: s.otherSales||0,
                        nextReserves: s.nextReserves||0,
                        register: s.register||10000,
                        memo: s.memo||""
                      });
                    } : undefined}
                    style={{
                      padding:"10px 4px", borderBottom:`1px solid ${C.border}`,
                      cursor:canEdit?"pointer":"default",
                      transition:"background 0.15s",
                    }}
                    onMouseEnter={canEdit?(e)=>e.currentTarget.style.background=C.bg2:undefined}
                    onMouseLeave={canEdit?(e)=>e.currentTarget.style.background="transparent":undefined}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:3 }}>
                      <span style={{ fontSize:13, color:C.cream, fontWeight:500 }}>
                        🏪 <span style={{ color:C.gold, fontWeight:600 }}>{st?.name||"-"}</span>
                        <span style={{ fontSize:11, color:C.muted2, marginLeft:6 }}>{s.date}</span>
                      </span>
                      <span style={{ fontSize:14, fontWeight:600, color:C.gold }}>{fmt(s.totalSales||0)}</span>
                    </div>
                    <div style={{ display:"flex", gap:10, fontSize:10, color:C.muted2, flexWrap:"wrap" }}>
                      <span>新規 {s.newGuests||0}名</span>
                      <span>顧客 {s.regularGuests||0}名</span>
                      <span>計 {s.totalGuests||0}名</span>
                      <span>PT {fmt(s.pointSales||0)}</span>
                      <span>次回予約 {s.nextReserves||0}</span>
                      <span>レジ {fmt(s.register||0)}</span>
                    </div>
                    {s.memo && <div style={{ fontSize:10, color:C.muted2, marginTop:3 }}>📝 {s.memo}</div>}
                    <div style={{ fontSize:9, color:C.muted2, marginTop:3 }}>
                      入力: {s.inputBy||"-"} {s.inputAt||""}
                      {s.inputLog?.length>1 && ` ／ 修正${s.inputLog.length-1}回`}
                    </div>
                  </div>
                );
              })
          }
          {filtered.length>0 && (
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"12px 4px 0", borderTop:`1px solid ${C.border2}` }}>
              <span style={{ fontSize:11, color:C.muted, fontWeight:600 }}>合計（{filtered.length}件）</span>
              <span style={{ fontSize:14, color:C.gold, fontWeight:700 }}>{fmt(filtered.reduce((a,r)=>a+(r.totalSales||0),0))}</span>
            </div>
          )}
        </div>
      )}

      {/* monthly table */}
      {viewMode==="monthly" && (
        <div style={card()}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:12 }}>月別売上一覧（{monthlyRows.length}ヶ月分）</div>
          {monthlyRows.length===0
            ? <div style={{ textAlign:"center", fontSize:12, color:C.muted2, padding:16 }}>データがありません</div>
            : <>
                <div style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr 2fr", gap:4, padding:"0 0 8px", borderBottom:`1px solid ${C.border2}` }}>
                  {["年月","売上","来客","客単価"].map(h=>(
                    <div key={h} style={{ fontSize:9, color:C.muted2 }}>{h}</div>
                  ))}
                </div>
                {monthlyRows.map(r=>(
                  <div key={r.month} style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr 2fr", gap:4, padding:"9px 0", borderBottom:`1px solid ${C.border}`, alignItems:"center" }}>
                    <div style={{ fontSize:11, color:C.cream }}>{r.month}</div>
                    <div style={{ fontSize:12, color:C.gold, fontWeight:600 }}>{fmt(r.sales)}</div>
                    <div style={{ fontSize:11, color:C.muted }}>{r.visits}名</div>
                    <div style={{ fontSize:11, color:C.muted2 }}>{fmt(Math.round(r.sales/r.visits))}</div>
                  </div>
                ))}
              </>
          }
        </div>
      )}

      {/* 🌸 編集モーダル（owner/clerk のみ表示） */}
      {editTarget && editForm && (role==="owner"||role==="clerk") && (()=>{
        const st = stores.find(s=>s.id===editTarget.storeId);
        const totalSales = (parseInt(editForm.cashSales)||0)+(parseInt(editForm.creditSales)||0)+(parseInt(editForm.pointSales)||0)+(parseInt(editForm.otherSales)||0);
        const totalGuests = (parseInt(editForm.newGuests)||0)+(parseInt(editForm.regularGuests)||0);
        const saveEdit = async () => {
          const inputterName = role==="clerk" ? "事務員" : "オーナー";
          const now = new Date().toLocaleString("ja-JP");
          const prevLog = editTarget.inputLog || [];
          const updated = {
            ...editTarget,
            newGuests: parseInt(editForm.newGuests)||0,
            regularGuests: parseInt(editForm.regularGuests)||0,
            totalGuests, totalSales,
            cashSales: parseInt(editForm.cashSales)||0,
            creditSales: parseInt(editForm.creditSales)||0,
            pointSales: parseInt(editForm.pointSales)||0,
            otherSales: parseInt(editForm.otherSales)||0,
            nextReserves: parseInt(editForm.nextReserves)||0,
            register: parseInt(editForm.register)||10000,
            memo: editForm.memo||"",
            inputBy: inputterName,
            inputAt: now,
            inputLog: [...prevLog, { by:inputterName, at:now, action:"修正" }]
          };
          const storeName = st?.name||"";
          setStoreSales(prev=>{ const filtered=prev.filter(s=>s.key!==editTarget.key); return [updated,...filtered]; });
          setSaveStatus("saving");
          setSaveMsg(`${storeName} ${editTarget.date}\n¥${totalSales.toLocaleString()}`);
          try {
            const result = await gasPostWithRetry("saveStoreSales", { records: [{...updated, storeName}] });
            if (!result || result.success===false) {
              setSaveStatus("error");
              setSaveMsg(`保存に失敗しました: ${result?.error||"不明なエラー"}`);
              return;
            }
            setSaveStatus("success");
            setSaveMsg(`${storeName} ${editTarget.date}\n¥${totalSales.toLocaleString()}\n修正完了`);
            if (reloadSalesFromGAS) await reloadSalesFromGAS();
            setTimeout(()=>{ setSaveStatus(null); setEditTarget(null); setEditForm(null); }, 1500);
          } catch(e) {
            setSaveStatus("error");
            setSaveMsg(`保存エラー: ${e.message}`);
          }
        };
        const Field = ({label, value, onChange, suffix, wide}) => (
          <div style={{ marginBottom:10, flex:wide?"1 1 100%":"1 1 calc(50% - 4px)", minWidth:0 }}>
            <label style={{ fontSize:10, color:C.muted, display:"block", marginBottom:3 }}>{label}</label>
            <div style={{ display:"flex", alignItems:"center", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"7px 9px" }}>
              <input type="number" value={value} onChange={e=>onChange(e.target.value)} inputMode="numeric"
                style={{ flex:1, background:"transparent", border:"none", color:C.cream, fontSize:13, outline:"none", minWidth:0, width:"100%" }}/>
              {suffix && <span style={{ fontSize:10, color:C.muted2, marginLeft:4 }}>{suffix}</span>}
            </div>
          </div>
        );
        return (
          <div style={{ position:"fixed", inset:0, background:"#0009", zIndex:300, display:"flex", alignItems:"flex-end", justifyContent:"center" }}
            onClick={()=>{ setEditTarget(null); setEditForm(null); }}>
            <div onClick={e=>e.stopPropagation()}
              style={{ background:C.bg1, borderRadius:"16px 16px 0 0", padding:"18px 16px 24px",
                width:"100%", maxWidth:480, maxHeight:"92vh", overflowY:"auto",
                border:`1px solid ${C.border2}`, paddingBottom:"calc(24px + env(safe-area-inset-bottom, 0px))" }}>
              <div style={{ fontSize:14, color:C.gold, fontWeight:700, textAlign:"center", marginBottom:4 }}>
                ✏️ {st?.name||"-"} {editTarget.date} の修正
              </div>
              <div style={{ fontSize:10, color:C.muted2, textAlign:"center", marginBottom:14 }}>
                変更したい項目だけ書き換えて「保存」をタップ
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"0 8px" }}>
                {Field({label:"新規客数", value:editForm.newGuests, onChange:v=>setEditForm(f=>({...f,newGuests:v})), suffix:"名"})}
                {Field({label:"顧客客数", value:editForm.regularGuests, onChange:v=>setEditForm(f=>({...f,regularGuests:v})), suffix:"名"})}
                {Field({label:"現金売上", value:editForm.cashSales, onChange:v=>setEditForm(f=>({...f,cashSales:v})), suffix:"円"})}
                {Field({label:"クレジット売上", value:editForm.creditSales, onChange:v=>setEditForm(f=>({...f,creditSales:v})), suffix:"円"})}
                {Field({label:"ポイント売上", value:editForm.pointSales, onChange:v=>setEditForm(f=>({...f,pointSales:v})), suffix:"円"})}
                {Field({label:"その他売上", value:editForm.otherSales, onChange:v=>setEditForm(f=>({...f,otherSales:v})), suffix:"円"})}
                {Field({label:"次回予約数", value:editForm.nextReserves, onChange:v=>setEditForm(f=>({...f,nextReserves:v})), suffix:"件"})}
                {Field({label:"レジ残高", value:editForm.register, onChange:v=>setEditForm(f=>({...f,register:v})), suffix:"円"})}
              </div>
              {/* 合計プレビュー */}
              <div style={{ background:C.bg2, borderRadius:8, padding:"10px 12px", marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                <span style={{ fontSize:11, color:C.muted }}>合計売上 / 総客数</span>
                <span><span style={{ fontSize:16, color:C.gold, fontWeight:700 }}>¥{totalSales.toLocaleString()}</span> <span style={{ fontSize:11, color:C.muted2, marginLeft:6 }}>{totalGuests}名</span></span>
              </div>
              {/* 備考欄 */}
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:10, color:C.muted, display:"block", marginBottom:3 }}>📝 備考（修正理由・担当者名など）</label>
                <textarea value={editForm.memo} onChange={e=>setEditForm(f=>({...f,memo:e.target.value}))}
                  placeholder={role==="clerk" ? "例：事務員 田中が修正（明細との不一致のため）" : "特記事項"}
                  style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 10px", color:C.cream, fontSize:12, height:60, resize:"none", fontFamily:"Jost,sans-serif", boxSizing:"border-box" }}/>
              </div>
              {/* 修正履歴 */}
              {editTarget.inputLog?.length>1 && (
                <div style={{ background:C.bg2, borderRadius:8, padding:"8px 10px", marginBottom:14, maxHeight:90, overflowY:"auto" }}>
                  <div style={{ fontSize:10, color:C.muted, fontWeight:600, marginBottom:4 }}>📋 修正履歴</div>
                  {[...editTarget.inputLog].reverse().map((log,i)=>(
                    <div key={i} style={{ fontSize:9, color:C.muted2 }}>
                      {log.at} ｜ {log.by} ｜ {log.action}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>{ setEditTarget(null); setEditForm(null); }}
                  style={{ flex:1, background:C.bg2, color:C.muted, border:`1px solid ${C.border}`, borderRadius:10, padding:12, fontSize:13, cursor:"pointer" }}>
                  キャンセル
                </button>
                <button onClick={saveEdit}
                  style={{ flex:2, background:`linear-gradient(135deg,${C.gold},${C.gold2})`, color:C.bg, border:"none", borderRadius:10, padding:12, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  💾 保存して上書き
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      <SaveStatusModal status={saveStatus} message={saveMsg} onClose={()=>setSaveStatus(null)}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SALES INPUT (individual — staff enters for themselves)
// ═══════════════════════════════════════════════════════════════════════════
function SalesInput({ records, setRecords, staff, stores, role, activeStaffId, expenses, setExpenses, saveRecordsToGAS }) {
  const cc = useC(role);
  const defaultStaffId = role==="staff"?activeStaffId:(staff[0]?.id||"");
  const emptyForm = { date:todayStr(), staffId:defaultStaffId, amount:"", newGuests:"", regularGuests:"", nextReserve:"", shimei:"", memo:"" };
  const [form, setForm] = useState(emptyForm);
  const [saved, setSaved] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [subTab, setSubTab] = useState("sales"); // "sales" or "expense"

  // 経費精算用
  const emptyExpForm = { date:todayStr(), amount:"", memo:"", fileData:"", fileName:"" };
  const [expForm, setExpForm] = useState(emptyExpForm);
  const [expSaved, setExpSaved] = useState(false);
  const expFileRef = useRef(null);

  const onExpFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3*1024*1024) { alert("ファイルサイズは3MBまでです"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setExpForm(f => ({ ...f, fileData: reader.result, fileName: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const saveExpense = () => {
    if (!expForm.amount || !expForm.date) return;
    const inputterName = role==="staff"?(staff.find(x=>x.id===activeStaffId)?.name||"スタッフ"):"長尾";
    const now = new Date().toLocaleString("ja-JP");
    const newExp = {
      id: `e${Date.now()}`,
      staffId: activeStaffId,
      date: expForm.date,
      amount: parseInt(expForm.amount)||0,
      memo: expForm.memo||"",
      fileData: expForm.fileData||"",
      fileName: expForm.fileName||"",
      uploadedAt: expForm.fileData ? now : "",
      inputBy: inputterName,
      inputAt: now,
    };
    setExpenses(prev => [newExp, ...prev]);
    // スプシにも保存
    if (typeof gasPost === "function") {
      gasPost("saveExpenses", { records: [{ ...newExp, staffName: staff.find(s=>s.id===activeStaffId)?.name||"" }] });
    }
    setExpForm(emptyExpForm);
    if (expFileRef.current) expFileRef.current.value = "";
    setExpSaved(true); setTimeout(()=>setExpSaved(false), 2000);
  };

  const removeExpense = (id) => {
    if (!window.confirm("この経費を削除しますか？")) return;
    setExpenses(prev => prev.filter(e => e.id !== id));
    if (typeof gasPost === "function") {
      gasPost("deleteExpense", { id });
    }
  };

  const myExpenses = (expenses||[]).filter(e => e.staffId === activeStaffId).sort((a,b)=>b.date.localeCompare(a.date));

  const totalGuests = (parseInt(form.newGuests)||0) + (parseInt(form.regularGuests)||0);

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      date:r.date, staffId:r.staffId, amount:String(r.amount||""),
      newGuests:String(r.newGuests||""), regularGuests:String(r.regularGuests||""),
      nextReserve:String(r.nextReserve||""), shimei:String(r.shimei||""), memo:r.memo||""
    });
    window.scrollTo(0,0);
  };

  const openConfirm = () => {
    if (!form.amount||!form.staffId||!form.date) return;
    setConfirm(true);
  };

  const [saveStatus, setSaveStatus] = useState(null); // null | "saving" | "success" | "error"
  const [saveMsg, setSaveMsg] = useState("");
  const DRAFT_KEY = `salonapp_pending_personal_${activeStaffId||"owner"}`;

  // 起動時：未送信の下書きを確認
  useEffect(()=>{
    if (!activeStaffId) return;
    try {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (draft) {
        const list = JSON.parse(draft);
        if (Array.isArray(list) && list.length > 0) {
          setTimeout(()=>{
            if (window.confirm(`📝 未送信の個人売上データが ${list.length}件 あります。\n再送信しますか？`)) {
              resendDrafts(list);
            }
          }, 1500);
        }
      }
    } catch(e){}
  }, [activeStaffId]);

  const resendDrafts = async (drafts) => {
    setSaveStatus("saving");
    setSaveMsg(`${drafts.length}件のデータを再送信中...`);
    try {
      if (saveRecordsToGAS) await saveRecordsToGAS(drafts);
      localStorage.removeItem(DRAFT_KEY);
      setSaveStatus("success");
      setSaveMsg(`未送信の${drafts.length}件を保存しました`);
      setTimeout(()=>setSaveStatus(null), 1800);
    } catch(e) {
      setSaveStatus("error");
      setSaveMsg(`再送信失敗：${e.message}\n後ほどもう一度お試しください`);
    }
  };

  const save = async () => {
    setConfirm(false);
    const s = staff.find(p=>p.id===form.staffId);
    const inputterName = role==="staff"?(staff.find(x=>x.id===activeStaffId)?.name||"スタッフ"):role==="clerk"?"事務員":"オーナー";
    const now = new Date().toLocaleString("ja-JP");
    const newRecord = {
      id:`r${Date.now()}`, date:form.date, staffId:form.staffId,
      storeId:s?.storeId||"", amount:parseInt(form.amount)||0,
      newGuests:parseInt(form.newGuests)||0, regularGuests:parseInt(form.regularGuests)||0,
      totalGuests, nextReserve:parseInt(form.nextReserve)||0, shimei:parseInt(form.shimei)||0,
      memo:form.memo||"", inputBy:inputterName, inputAt:now,
    };
    setRecords(prev=>[newRecord, ...prev]);
    setForm({...emptyForm, date:form.date, staffId:form.staffId});

    setSaveStatus("saving");
    setSaveMsg(`${s?.name||""} ${form.date}\n¥${(parseInt(form.amount)||0).toLocaleString()}`);

    // GAS用フォーマット
    const gasRecord = {
      id: newRecord.id,
      date: newRecord.date,
      staffId: newRecord.staffId,
      storeId: newRecord.storeId,
      total: newRecord.amount,
      newCust: newRecord.newGuests,
      cust: newRecord.regularGuests,
      totalCust: newRecord.totalGuests,
      nextReserv: newRecord.nextReserve,
      nominations: newRecord.shimei,
      createdAt: now,
    };

    // 🌸 下書き保存（先にlocalStorageに保存→送信→成功で削除）
    try {
      const existing = JSON.parse(localStorage.getItem(DRAFT_KEY)||"[]");
      localStorage.setItem(DRAFT_KEY, JSON.stringify([...existing, gasRecord]));
    } catch(e){}

    // 🔧 スプシに保存（リトライ付き）
    try {
      if (saveRecordsToGAS) {
        await saveRecordsToGAS([gasRecord]);
      }
      // 🌸 保存後スプシ照合（自分のレコードIDがあるか確認）
      let verified = false;
      try {
        const fresh = await gasLoad();
        if (fresh?.staffSales) {
          verified = fresh.staffSales.some(r => String(r["ID"]) === gasRecord.id);
        }
      } catch(e){}

      if (verified) {
        // 完璧！下書き削除
        try {
          const existing = JSON.parse(localStorage.getItem(DRAFT_KEY)||"[]");
          const filtered = existing.filter(r => r.id !== gasRecord.id);
          if (filtered.length === 0) localStorage.removeItem(DRAFT_KEY);
          else localStorage.setItem(DRAFT_KEY, JSON.stringify(filtered));
        } catch(e){}
        setSaveStatus("success");
        setSaveMsg(`${s?.name||""} ${form.date}\n¥${gasRecord.total.toLocaleString()}\n✓ スプシ確認済`);
      } else {
        // 保存はしたけど照合失敗（スプシ反映待ち）
        setSaveStatus("success");
        setSaveMsg(`${s?.name||""} ${form.date}\n¥${gasRecord.total.toLocaleString()}\n⚠️ 反映確認中（後で再表示されます）`);
      }
      setTimeout(()=>setSaveStatus(null), 1800);
    } catch(e) {
      setSaveStatus("error");
      setSaveMsg(`${e.message}\n\nデータは下書き保存されました。\nアプリを開き直すと自動再送信できます。`);
    }
  };

  const todayRecs = records.filter(r=>r.date===form.date&&(role==="staff"?r.staffId===activeStaffId:true)).sort((a,b)=>b.id.localeCompare(a.id));

  const numInp = (label, key, placeholder="0") => (
    <div>
      <label style={{ fontSize:10, color:cc.muted, display:"block", marginBottom:3 }}>{label}</label>
      <input type="number" value={form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder={placeholder}
        style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12 }}/>
    </div>
  );

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      {/* サブタブ：売上入力 / 経費精算 */}
      {role==="staff" && (
        <div style={{ display:"flex", gap:6, marginBottom:12 }}>
          <button onClick={()=>setSubTab("sales")} style={{ flex:1, padding:"10px", borderRadius:10, border:"none",
            background: subTab==="sales" ? cc.gold : cc.bg2,
            color: subTab==="sales" ? cc.bg : cc.muted,
            fontSize:13, fontWeight:600, cursor:"pointer" }}>💰 売上入力</button>
          <button onClick={()=>setSubTab("expense")} style={{ flex:1, padding:"10px", borderRadius:10, border:"none",
            background: subTab==="expense" ? cc.gold : cc.bg2,
            color: subTab==="expense" ? cc.bg : cc.muted,
            fontSize:13, fontWeight:600, cursor:"pointer" }}>🧾 経費精算</button>
        </div>
      )}

      {/* 経費精算タブ */}
      {subTab==="expense" && role==="staff" && (
        <>
          <div style={{ ...cardS(role), marginBottom:12 }}>
            <div style={{ fontSize:12, color:cc.gold, fontWeight:600, marginBottom:12 }}>🧾 経費精算入力</div>
            <div style={{ background:cc.bg2, borderRadius:8, padding:"7px 10px", marginBottom:10, fontSize:10, color:cc.muted, lineHeight:1.5 }}>
              💡 業務に関係する経費を申請できます。レシート画像やPDFを添付してください。
            </div>

            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:10, color:cc.muted }}>日付</label>
              <input type="date" value={expForm.date} onChange={e=>setExpForm(f=>({...f,date:e.target.value}))}
                style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}/>
            </div>

            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:10, color:cc.muted }}>金額（円）</label>
              <input type="number" inputMode="numeric" value={expForm.amount} onChange={e=>setExpForm(f=>({...f,amount:e.target.value}))}
                placeholder="例: 3500"
                style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}/>
            </div>

            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:10, color:cc.muted }}>メモ</label>
              <textarea value={expForm.memo} onChange={e=>setExpForm(f=>({...f,memo:e.target.value}))}
                placeholder="例: 材料購入（ジェル）、セミナー交通費 など"
                style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3, height:60, resize:"none", fontFamily:"inherit" }}/>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:10, color:cc.muted, display:"block", marginBottom:5 }}>添付ファイル（レシート・領収書）</label>
              <input ref={expFileRef} type="file" accept="image/*,application/pdf" onChange={onExpFilePick}
                style={{ fontSize:11, color:cc.cream }}/>
              {expForm.fileName && (
                <div style={{ fontSize:10, color:cc.gold, marginTop:4 }}>📎 {expForm.fileName}</div>
              )}
              <div style={{ fontSize:9, color:cc.muted2, marginTop:3 }}>※ 3MBまで、画像またはPDF</div>
            </div>

            <button onClick={saveExpense} disabled={!expForm.amount||!expForm.date}
              style={{ width:"100%", background:expSaved?"#4a7a5a":(!expForm.amount||!expForm.date)?cc.bg2:cc.gold, color:expSaved?"#fff":(!expForm.amount||!expForm.date)?cc.muted:cc.bg, border:"none", borderRadius:10, padding:11, fontSize:13, fontWeight:600, cursor:(!expForm.amount||!expForm.date)?"not-allowed":"pointer", transition:"background 0.3s" }}>
              {expSaved?"✓ 申請しました":"経費を申請する"}
            </button>
          </div>

          {/* 申請履歴 */}
          <div style={cardS(role)}>
            <div style={{ fontSize:11, color:cc.muted, marginBottom:8 }}>申請履歴（新しい順）</div>
            {myExpenses.length === 0 ? (
              <div style={{ fontSize:11, color:cc.muted2, textAlign:"center", padding:16 }}>まだ申請がありません</div>
            ) : (
              myExpenses.map(e => (
                <div key={e.id} style={{ padding:"10px 0", borderBottom:`1px solid ${cc.border}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                    <span style={{ fontSize:11, color:cc.muted }}>{e.date}</span>
                    <span style={{ fontSize:14, fontWeight:600, color:cc.gold }}>{fmt(e.amount)}</span>
                  </div>
                  {e.memo && <div style={{ fontSize:11, color:cc.cream, marginTop:3, whiteSpace:"pre-wrap" }}>{e.memo}</div>}
                  {e.fileData && (
                    <div style={{ marginTop:5, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                      <a href={e.fileData} download={e.fileName} style={{ fontSize:10, color:cc.gold, textDecoration:"none" }}>📎 {e.fileName}</a>
                      {e.uploadedAt && <span style={{ fontSize:9, color:cc.muted2 }}>添付日 {e.uploadedAt.split(" ")[0]}</span>}
                    </div>
                  )}
                  <button onClick={()=>removeExpense(e.id)} style={{ background:"none", border:"none", color:cc.muted2, fontSize:10, cursor:"pointer", marginTop:3, padding:0 }}>削除</button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* 売上入力タブ */}
      {subTab==="sales" && (
      <>
      <div style={{ ...cardS(role), marginBottom:12 }}>
        <div style={{ fontSize:12, color:cc.gold, fontWeight:600, marginBottom:12 }}>個人売上入力</div>

        <div style={{ marginBottom:10 }}>
          <label style={{ fontSize:10, color:cc.muted }}>日付</label>
          <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}
            style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}/>
        </div>

        {role==="owner" && (
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:10, color:cc.muted }}>スタッフ</label>
            <select value={form.staffId} onChange={e=>setForm(f=>({...f,staffId:e.target.value}))}
              style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}>
              {stores.map(st=><optgroup key={st.id} label={st.name}>{staff.filter(s=>(s.storeIds||[s.storeId]).includes(st.id)).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>)}
            </select>
          </div>
        )}
        {role==="staff" && (
          <div style={{ ...cardS(role, {padding:"10px 12px", background:cc.bg2, marginBottom:10}) }}>
            <div style={{ fontSize:10, color:cc.muted }}>入力者</div>
            <div style={{ fontSize:13, color:cc.cream, marginTop:2 }}>{staff.find(s=>s.id===activeStaffId)?.name}</div>
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:10 }}>
          {numInp("新規客数", "newGuests")}
          {numInp("顧客数", "regularGuests")}
          <div>
            <label style={{ fontSize:10, color:cc.muted, display:"block", marginBottom:3 }}>総人数（自動）</label>
            <div style={{ background:cc.bg3, border:`1px solid ${cc.border}`, borderRadius:8, padding:"8px 10px", color:cc.gold, fontSize:13, fontWeight:600, textAlign:"center" }}>
              {totalGuests}名
            </div>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
          {numInp("次回予約数", "nextReserve")}
          {numInp("指名数", "shimei")}
        </div>

        <div style={{ marginBottom:10 }}>
          <label style={{ fontSize:10, color:cc.muted }}>総売上（円）</label>
          <input type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="例: 15000"
            style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}/>
          {(parseInt(form.amount)||0) > 0 && (
            <div style={{ fontSize:9, color:cc.muted2, marginTop:2, textAlign:"right" }}>(税抜 {fmt(Math.round((parseInt(form.amount)||0)/TAX_RATE))})</div>
          )}
        </div>

        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:10, color:cc.muted }}>メモ（任意）</label>
          <textarea value={form.memo} onChange={e=>setForm(f=>({...f,memo:e.target.value}))} placeholder="特記事項など"
            style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3, height:52, resize:"none", fontFamily:"Jost,sans-serif" }}/>
        </div>

        <button onClick={openConfirm} style={{ width:"100%", background:saved?"#4a7a5a":cc.gold, color:cc.bg, border:"none", borderRadius:10, padding:11, fontSize:13, fontWeight:600, cursor:"pointer", transition:"background 0.3s" }}>
          {saved?"✓ 保存しました":editId?"修正内容を確認":"内容を確認して登録"}
        </button>

        {/* 確認モーダル */}
        {confirm && (
          <div style={{ position:"fixed",inset:0,background:"#0008",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
            <div style={{ background:cc.bg1,borderRadius:16,padding:20,width:"100%",maxWidth:320,border:`1px solid ${cc.border2}` }}>
              <div style={{ fontSize:14,color:cc.gold,fontWeight:700,marginBottom:14,textAlign:"center" }}>{editId?"修正内容の確認":"入力内容の確認"}</div>
              {[
                ["日付", form.date],
                ["総売上", `${fmt(parseInt(form.amount)||0)}  (税抜 ${fmt(Math.round((parseInt(form.amount)||0)/TAX_RATE))})`],
                ["新規客数", `${parseInt(form.newGuests)||0}名`],
                ["顧客数", `${parseInt(form.regularGuests)||0}名`],
                ["総人数", `${totalGuests}名`],
                ["次回予約", `${parseInt(form.nextReserve)||0}件`],
                ["指名数", `${parseInt(form.shimei)||0}名`],
                ...(form.memo?[["メモ",form.memo]]:[]),
              ].map(([label,val])=>(
                <div key={label} style={{ display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${cc.border}`,fontSize:12 }}>
                  <span style={{ color:cc.muted }}>{label}</span>
                  <span style={{ color:cc.cream,fontWeight:500 }}>{val}</span>
                </div>
              ))}
              <div style={{ display:"flex",gap:8,marginTop:14 }}>
                <button onClick={save} style={{ flex:2,background:cc.gold,color:cc.bg,border:"none",borderRadius:8,padding:11,fontSize:13,fontWeight:600,cursor:"pointer" }}>登録する</button>
                <button onClick={()=>setConfirm(false)} style={{ flex:1,background:cc.bg2,color:cc.muted,border:`1px solid ${cc.border}`,borderRadius:8,padding:11,fontSize:12,cursor:"pointer" }}>戻る</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={cardS(role)}>
        <div style={{ fontSize:11, color:cc.muted, marginBottom:10 }}>{form.date} 入力済み（{todayRecs.length}件）</div>
        {todayRecs.length===0
          ? <div style={{ textAlign:"center", fontSize:12, color:cc.muted2, padding:12 }}>まだ入力がありません</div>
          : todayRecs.map(r=>{ const s=staff.find(p=>p.id===r.staffId); return (
            <div key={r.id} style={{ padding:"8px 0", borderBottom:`1px solid ${cc.border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                <div style={{ fontSize:12, color:cc.cream }}>{s?.name}</div>
                <div style={{ fontSize:13, fontWeight:600, color:cc.gold }}>{fmt(r.amount)}</div>
              </div>
              <div style={{ display:"flex", gap:10, fontSize:10, color:cc.muted2, flexWrap:"wrap" }}>
                <span>新規 {r.newGuests||0}名</span>
                <span>顧客 {r.regularGuests||0}名</span>
                <span>計 {r.totalGuests||0}名</span>
                <span>次回予約 {r.nextReserve||0}</span>
                {(r.shimei||0)>0 && <span style={{ color:cc.gold }}>指名 {r.shimei}名</span>}
              </div>
              {r.memo && <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>📝 {r.memo}</div>}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4 }}>
                <div style={{ fontSize:9, color:cc.muted2 }}>
                  入力: {r.inputBy||""}
                  {r.editLog?.length>0 && ` ／ 修正${r.editLog.length}回`}
                </div>
                <button onClick={()=>startEdit(r)} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:8,padding:"2px 8px",color:cc.muted,fontSize:10,cursor:"pointer" }}>修正</button>
              </div>
            </div>
          );})}
      </div>
      </>
      )}
      <SaveStatusModal status={saveStatus} message={saveMsg} onClose={()=>setSaveStatus(null)}/>
    </div>
  );
}

function StoreSalesInput({ storeSales, setStoreSales, stores, staff, activeStaffId, role, cashRegisterOuts, setCashRegisterOuts, reloadSalesFromGAS }) {
  const cc = useC(role);
  const activeS = staff.find(s=>s.id===activeStaffId);
  const myStore = role==="staff" ? (activeS?.storeId||stores[0]?.id) : stores[0]?.id;
  const emptyForm = { date:todayStr(), storeId:myStore, newGuests:"", regularGuests:"",
    cashSales:"", creditSales:"", pointSales:"", otherSales:"",
    nextReserves:"", register:"10000", memo:"" };
  const [form, setForm] = useState(emptyForm);
  const [saved, setSaved] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMsg, setSaveMsg] = useState("");
  const STORE_DRAFT_KEY = `salonapp_pending_store_${activeStaffId||"owner"}`;

  // 起動時：未送信の下書きを再送信
  useEffect(()=>{
    try {
      const draft = localStorage.getItem(STORE_DRAFT_KEY);
      if (draft) {
        const list = JSON.parse(draft);
        if (Array.isArray(list) && list.length > 0) {
          setTimeout(()=>{
            if (window.confirm(`📝 未送信の店舗売上データが ${list.length}件 あります。\n再送信しますか？`)) {
              resendStoreDrafts(list);
            }
          }, 1500);
        }
      }
    } catch(e){}
  }, []);

  const resendStoreDrafts = async (drafts) => {
    setSaveStatus("saving");
    setSaveMsg(`${drafts.length}件のデータを再送信中...`);
    try {
      for (const d of drafts) {
        await gasPostWithRetry("saveStoreSales", { records: [d] });
      }
      localStorage.removeItem(STORE_DRAFT_KEY);
      if (reloadSalesFromGAS) await reloadSalesFromGAS();
      setSaveStatus("success");
      setSaveMsg(`未送信の${drafts.length}件を保存しました`);
      setTimeout(()=>setSaveStatus(null), 1800);
    } catch(e) {
      setSaveStatus("error");
      setSaveMsg(`再送信失敗：${e.message}`);
    }
  };

  const [confirmStore, setConfirmStore] = useState(false);
  const [subTab, setSubTab] = useState("sales"); // "sales" or "cashout"

  // レジ出金フォーム
  const emptyOutForm = { date:todayStr(), storeId:myStore, amount:"", memo:"" };
  const [outForm, setOutForm] = useState(emptyOutForm);
  const [outSaved, setOutSaved] = useState(false);

  const totalGuests = (parseInt(form.newGuests)||0)+(parseInt(form.regularGuests)||0);
  // 総売上 = 現金 + クレジット + ポイント + その他（自動計算）
  const totalSales = (parseInt(form.cashSales)||0) + (parseInt(form.creditSales)||0)
    + (parseInt(form.pointSales)||0) + (parseInt(form.otherSales)||0);

  // ───── レジ残高の計算 ─────
  const prevRegisterBalance = (() => {
    const targetDate = form.date || todayStr();
    const targetStore = form.storeId;
    const prevCashIn = storeSales
      .filter(s => s.storeId === targetStore && s.date < targetDate)
      .reduce((a, s) => a + (s.cashSales || 0), 0);
    const prevCashOut = (cashRegisterOuts||[])
      .filter(o => o.storeId === targetStore && o.date < targetDate)
      .reduce((a, o) => a + (o.amount || 0), 0);
    return prevCashIn - prevCashOut;
  })();
  const todayCashIn = parseInt(form.cashSales)||0;
  const todayCashOut = (cashRegisterOuts||[])
    .filter(o => o.storeId === form.storeId && o.date === form.date)
    .reduce((a, o) => a + (o.amount || 0), 0);
  const todayRegisterBalance = prevRegisterBalance + todayCashIn - todayCashOut;

  // 日割り目標の計算（平日/週末で分岐）
  const currentStore = stores.find(s=>s.id===form.storeId);
  const [fy, fm, fd] = (form.date||todayStr()).split("-").map(Number);
  const selectedDow = new Date(fy, fm-1, fd||1).getDay();
  const isWeekend = selectedDow===0 || selectedDow===6;
  const selectedMonth = fm;
  const isBusyMonth = [1,7,8,12].includes(selectedMonth);

  const dailyTarget = currentStore ? (
    isBusyMonth
      ? (isWeekend ? (currentStore.weekendTargetBusy||currentStore.weekendTarget||0) : (currentStore.weekdayTargetBusy||currentStore.weekdayTarget||0))
      : (isWeekend ? (currentStore.weekendTarget||0) : (currentStore.weekdayTarget||0))
  ) : 0;
  const dailyAchieveRate = dailyTarget>0 ? Math.round(totalSales/dailyTarget*100) : 0;

  // 月間目標 = 平日目標×平日数 + 週末目標×週末数（定休日・臨時休業日は除外）
  const daysInSelectedMonth = new Date(fy, fm, 0).getDate();
  const wdClosed = currentStore?.shiftClosedDays || [];
  const irregularClosed = currentStore?.irregularClosedDates || [];
  let wdCount=0, weCount=0;
  for (let d=1; d<=daysInSelectedMonth; d++) {
    const dowN = new Date(fy, fm-1, d).getDay();
    const dateStr = `${fy}-${String(fm).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    if (wdClosed.includes(dowN)) continue;        // 曜日定休日は除外
    if (irregularClosed.includes(dateStr)) continue; // 不定期定休日も除外
    if (dowN===0 || dowN===6) weCount++; else wdCount++;
  }
  const weekdayT = currentStore ? (isBusyMonth ? (currentStore.weekdayTargetBusy||currentStore.weekdayTarget||0) : (currentStore.weekdayTarget||0)) : 0;
  const weekendT = currentStore ? (isBusyMonth ? (currentStore.weekendTargetBusy||currentStore.weekendTarget||0) : (currentStore.weekendTarget||0)) : 0;
  const monthlyTarget = weekdayT*wdCount + weekendT*weCount;

  // 今月の累計（当該店舗・当該月）
  const monthKey = `${fy}-${String(fm).padStart(2,"0")}`;
  const monthTotalSales = storeSales
    .filter(s => s.storeId===form.storeId && ym(s.date)===monthKey && s.key!==`${form.date}_${form.storeId}`)
    .reduce((a,s)=>a+(s.totalSales||0), 0) + totalSales;
  const monthAchieveRate = monthlyTarget>0 ? Math.round(monthTotalSales/monthlyTarget*100) : 0;

  // existing entry for this date+store (for overwrite)
  const existingKey = `${form.date}_${form.storeId}`;
  const existing = storeSales.find(s=>s.key===existingKey);

  const openConfirmStore = () => {
    if (!totalSales||!form.storeId||!form.date) return;
    setConfirmStore(true);
  };

  const save = async () => {
    const inputterName = role==="staff"?(staff.find(s=>s.id===activeStaffId)?.name||"スタッフ"):role==="clerk"?"事務員":"オーナー";
    const now = new Date().toLocaleString("ja-JP");
    const prevLog = existing?.inputLog || [];
    const entry = { key:existingKey, date:form.date, storeId:form.storeId,
      newGuests:parseInt(form.newGuests)||0, regularGuests:parseInt(form.regularGuests)||0,
      totalGuests, totalSales,
      cashSales:parseInt(form.cashSales)||0, creditSales:parseInt(form.creditSales)||0,
      pointSales:parseInt(form.pointSales)||0, otherSales:parseInt(form.otherSales)||0,
      nextReserves:parseInt(form.nextReserves)||0,
      register:parseInt(form.register)||10000, memo:form.memo||"",
      inputBy:inputterName, inputAt:now,
      inputLog:[...prevLog, { by:inputterName, at:now, action:existing?"修正":"新規" }]
    };
    const storeName = stores.find(s=>s.id===entry.storeId)?.name||"";
    setStoreSales(prev=>{ const filtered=prev.filter(s=>s.key!==existingKey); return [entry,...filtered]; });
    setConfirmStore(false);

    setSaveStatus("saving");
    setSaveMsg(`${storeName} ${form.date}\n¥${totalSales.toLocaleString()}`);

    // 🌸 下書き保存
    const gasRecord = { ...entry, storeName };
    try {
      const existing2 = JSON.parse(localStorage.getItem(STORE_DRAFT_KEY)||"[]");
      const filtered = existing2.filter(r => r.key !== gasRecord.key);
      localStorage.setItem(STORE_DRAFT_KEY, JSON.stringify([...filtered, gasRecord]));
    } catch(e){}

    // 🔧 リトライ付き保存
    try {
      const result = await gasPostWithRetry("saveStoreSales", { records: [gasRecord] });
      if (!result || result.success === false) {
        setSaveStatus("error");
        setSaveMsg(`${result?.error || "GASからの応答がありません"}\n\nデータは下書き保存されました。\nアプリを開き直すと自動再送信できます。`);
        return;
      }
      // 🌸 スプシ照合
      let verified = false;
      try {
        const fresh = await gasLoad();
        if (fresh?.storeSales) {
          verified = fresh.storeSales.some(s => String(s["キー"]) === gasRecord.key);
        }
      } catch(e){}

      // 下書き削除
      try {
        const existing2 = JSON.parse(localStorage.getItem(STORE_DRAFT_KEY)||"[]");
        const filtered = existing2.filter(r => r.key !== gasRecord.key);
        if (filtered.length === 0) localStorage.removeItem(STORE_DRAFT_KEY);
        else localStorage.setItem(STORE_DRAFT_KEY, JSON.stringify(filtered));
      } catch(e){}

      // 画面に反映
      if (reloadSalesFromGAS) await reloadSalesFromGAS();

      setSaveStatus("success");
      setSaveMsg(`${storeName} ${form.date}\n¥${totalSales.toLocaleString()}\n${verified ? "✓ スプシ確認済" : "⚠️ 反映確認中"}`);
      setTimeout(()=>setSaveStatus(null), 1800);
    } catch(e) {
      setSaveStatus("error");
      setSaveMsg(`通信エラー：${e.message}\n\nデータは下書き保存されました。\nアプリを開き直すと自動再送信できます。`);
    }
  };

  // レジ出金 保存
  const saveCashOut = () => {
    if (!outForm.amount || !outForm.date || !outForm.storeId) return;
    const inputterName = role==="staff" ? (activeS?.name || "スタッフ") : role==="clerk" ? "事務員" : "長尾";
    const now = new Date().toLocaleString("ja-JP");
    const newOut = {
      id: `co${Date.now()}`,
      storeId: outForm.storeId,
      date: outForm.date,
      amount: parseInt(outForm.amount) || 0,
      memo: outForm.memo || "",
      inputBy: inputterName,
      inputAt: now,
    };
    setCashRegisterOuts(prev => [newOut, ...prev]);
    if (typeof gasPost === "function") {
      gasPost("saveCashRegisterOuts", { records: [{ ...newOut, storeName: stores.find(s=>s.id===newOut.storeId)?.name||"" }] });
    }
    setOutForm(emptyOutForm);
    setOutSaved(true); setTimeout(()=>setOutSaved(false), 2000);
  };

  const deleteCashOut = (id) => {
    if (!window.confirm("このレジ出金記録を削除しますか？")) return;
    setCashRegisterOuts(prev => prev.filter(o => o.id !== id));
    if (typeof gasPost === "function") {
      gasPost("deleteCashRegisterOut", { id });
    }
  };

  // 今月のレジ出金履歴（選択店舗）
  const thisYM = form.date ? form.date.slice(0,7) : nowYM();
  const thisMonthOuts = (cashRegisterOuts||[])
    .filter(o => o.storeId === form.storeId && o.date.slice(0,7) === thisYM)
    .sort((a,b) => b.date.localeCompare(a.date));
  const thisMonthOutTotal = thisMonthOuts.reduce((a,o)=>a+(o.amount||0), 0);

  // prefill if existing
  const loadExisting = () => {
    if (!existing) return;
    setForm(f=>({ ...f,
      newGuests:String(existing.newGuests||0),
      regularGuests:String(existing.regularGuests||0),
      cashSales:String(existing.cashSales||0),
      creditSales:String(existing.creditSales||0),
      pointSales:String(existing.pointSales||0),
      otherSales:String(existing.otherSales||0),
      nextReserves:String(existing.nextReserves||0),
      register:String(existing.register||10000),
      memo:existing.memo||""
    }));
  };

  const numInp = (label, key, placeholder="0", hint="", showTaxEx=false) => {
    const val = parseInt(form[key])||0;
    return (
    <div>
      <label style={{ fontSize:10, color:cc.muted, display:"block", marginBottom:3 }}>
        {label} {hint && <span style={{ color:cc.muted2, fontSize:9 }}>{hint}</span>}
      </label>
      <input type="number" inputMode="numeric" value={form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
        placeholder={placeholder}
        style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12 }}/>
      {showTaxEx && val>0 && (
        <div style={{ fontSize:9, color:cc.muted2, marginTop:2, textAlign:"right" }}>(税抜 {fmt(Math.round(val/TAX_RATE))})</div>
      )}
    </div>
    );
  };

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      {/* サブタブ：売上入力 / レジ出金 */}
      <div style={{ display:"flex", gap:6, marginBottom:12 }}>
        <button onClick={()=>setSubTab("sales")} style={{ flex:1, padding:"10px", borderRadius:10, border:"none",
          background: subTab==="sales" ? cc.gold : cc.bg2,
          color: subTab==="sales" ? cc.bg : cc.muted,
          fontSize:13, fontWeight:600, cursor:"pointer" }}>🏪 店舗売上</button>
        <button onClick={()=>setSubTab("cashout")} style={{ flex:1, padding:"10px", borderRadius:10, border:"none",
          background: subTab==="cashout" ? cc.gold : cc.bg2,
          color: subTab==="cashout" ? cc.bg : cc.muted,
          fontSize:13, fontWeight:600, cursor:"pointer" }}>💸 レジ出金</button>
      </div>

      {/* レジ出金タブ */}
      {subTab==="cashout" && (
        <>
          <div style={{ ...cardS(role), marginBottom:12 }}>
            <div style={{ fontSize:12, color:cc.gold, fontWeight:600, marginBottom:12 }}>💸 レジ出金記録</div>
            <div style={{ background:cc.bg2, borderRadius:8, padding:"7px 10px", marginBottom:10, fontSize:10, color:cc.muted, lineHeight:1.5 }}>
              💡 レジから現金を出した際に記録してください（両替・銀行入金・材料費支払いなど）
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
              <div>
                <label style={{ fontSize:10, color:cc.muted }}>日付</label>
                <input type="date" value={outForm.date} onChange={e=>setOutForm(f=>({...f,date:e.target.value}))}
                  style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}/>
              </div>
              <div>
                <label style={{ fontSize:10, color:cc.muted }}>店舗</label>
                <select value={outForm.storeId} onChange={e=>setOutForm(f=>({...f,storeId:e.target.value}))}
                  style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}>
                  {stores.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:10, color:cc.muted }}>出金額（円）</label>
              <input type="number" inputMode="numeric" value={outForm.amount} onChange={e=>setOutForm(f=>({...f,amount:e.target.value}))}
                placeholder="例: 3000"
                style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}/>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:10, color:cc.muted }}>概要</label>
              <textarea value={outForm.memo} onChange={e=>setOutForm(f=>({...f,memo:e.target.value}))}
                placeholder="例: 銀行入金、材料費支払い、両替 など"
                style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3, height:60, resize:"none", fontFamily:"inherit" }}/>
            </div>

            <button onClick={saveCashOut} disabled={!outForm.amount||!outForm.date}
              style={{ width:"100%", background:outSaved?"#4a7a5a":(!outForm.amount||!outForm.date)?cc.bg2:cc.gold, color:outSaved?"#fff":(!outForm.amount||!outForm.date)?cc.muted:cc.bg, border:"none", borderRadius:10, padding:11, fontSize:13, fontWeight:600, cursor:(!outForm.amount||!outForm.date)?"not-allowed":"pointer", transition:"background 0.3s" }}>
              {outSaved?"✓ 記録しました":"レジ出金を記録する"}
            </button>
          </div>

          {/* 今月の出金履歴 */}
          <div style={cardS(role)}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8 }}>
              <span style={{ fontSize:11, color:cc.muted }}>今月の出金履歴（{stores.find(s=>s.id===outForm.storeId)?.name||""}）</span>
              <span style={{ fontSize:10, color:cc.muted2 }}>合計: <span style={{ color:cc.gold, fontWeight:600, fontSize:13 }}>{fmt(thisMonthOutTotal)}</span></span>
            </div>
            {thisMonthOuts.length === 0 ? (
              <div style={{ fontSize:11, color:cc.muted2, textAlign:"center", padding:16 }}>今月の出金記録はありません</div>
            ) : (
              thisMonthOuts.map(o => (
                <div key={o.id} style={{ padding:"10px 0", borderBottom:`1px solid ${cc.border}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                    <span style={{ fontSize:11, color:cc.muted }}>{o.date}</span>
                    <span style={{ fontSize:14, fontWeight:600, color:cc.accent }}>− {fmt(o.amount)}</span>
                  </div>
                  {o.memo && <div style={{ fontSize:11, color:cc.cream, marginTop:3, whiteSpace:"pre-wrap" }}>{o.memo}</div>}
                  <div style={{ fontSize:9, color:cc.muted2, marginTop:2 }}>入力: {o.inputBy}</div>
                  <button onClick={()=>deleteCashOut(o.id)} style={{ background:"none", border:"none", color:cc.muted2, fontSize:10, cursor:"pointer", marginTop:3, padding:0 }}>削除</button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* 店舗売上タブ */}
      {subTab==="sales" && (
      <>
      <div style={{ ...cardS(role), marginBottom:12 }}>
        <div style={{ fontSize:12, color:cc.gold, fontWeight:600, marginBottom:12 }}>店舗売上入力</div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
          <div>
            <label style={{ fontSize:10, color:cc.muted }}>日付</label>
            <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}
              style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}/>
          </div>
          <div>
            <label style={{ fontSize:10, color:cc.muted }}>店舗</label>
            <select value={form.storeId} onChange={e=>setForm(f=>({...f,storeId:e.target.value}))}
              style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12, marginTop:3 }}>
              {stores.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        {existing && (
          <div style={{ background:`${cc.accent}22`, border:`1px solid ${cc.accent}55`, borderRadius:8, padding:"8px 12px", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ fontSize:11, color:cc.accent }}>この日付・店舗のデータが既にあります（上書き可）</div>
            <button onClick={loadExisting} style={{ background:"none", border:`1px solid ${cc.accent}`, borderRadius:8, padding:"3px 10px", color:cc.accent, fontSize:10, cursor:"pointer" }}>読込</button>
          </div>
        )}

        {/* guests */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:10 }}>
          {numInp("新規客数", "newGuests")}
          {numInp("顧客数", "regularGuests")}
          <div>
            <label style={{ fontSize:10, color:cc.muted, display:"block", marginBottom:3 }}>総人数（自動）</label>
            <div style={{ background:cc.bg3, border:`1px solid ${cc.border}`, borderRadius:8, padding:"8px 10px", color:cc.gold, fontSize:13, fontWeight:600, textAlign:"center" }}>
              {totalGuests}名
            </div>
          </div>
        </div>

        {/* 売上内訳（現金・クレジット・ポイント・その他） */}
        <div style={{ fontSize:10, color:cc.muted, marginBottom:6, letterSpacing:1 }}>💴 売上内訳（円）</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
          {numInp("現金", "cashSales", "0", "(任意)", true)}
          {numInp("クレジット", "creditSales", "0", "(任意)", true)}
          {numInp("ポイント", "pointSales", "0", "(任意)", true)}
          {numInp("その他", "otherSales", "0", "(任意)", true)}
        </div>

        {/* 総売上（自動計算）＋ 日割り目標 */}
        <div style={{ background:cc.bg3, border:`1px solid ${cc.gold}55`, borderRadius:10, padding:"10px 12px", marginBottom:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:2 }}>
            <span style={{ fontSize:11, color:cc.muted }}>総売上（自動）</span>
            <span style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:cc.gold, fontWeight:600 }}>{fmt(totalSales)}</span>
          </div>
          {totalSales>0 && (
            <div style={{ fontSize:10, color:cc.muted2, textAlign:"right", marginBottom:6 }}>(税抜 {fmt(Math.round(totalSales/TAX_RATE))})</div>
          )}
          {dailyTarget>0 && (
            <>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:cc.muted2, marginBottom:3 }}>
                <span>🎯 {isWeekend?"週末・祝":"平日"}の目標 {isBusyMonth && <span style={{color:cc.accent}}>🔥繁忙期</span>}</span>
                <span style={{ color:dailyAchieveRate>=100?"#4a7a5a":cc.muted, fontWeight:500 }}>
                  {fmt(dailyTarget)} / 達成{dailyAchieveRate}%
                </span>
              </div>
              <div style={{ height:3, background:cc.bg2, borderRadius:2, marginBottom:6 }}>
                <div style={{ height:"100%", width:`${Math.min(100,dailyAchieveRate)}%`, background:dailyAchieveRate>=100?"#4a7a5a":cc.gold, borderRadius:2, transition:"width 0.5s" }}/>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:cc.muted2, marginBottom:3 }}>
                <span>📅 今月の目標（累計）</span>
                <span style={{ color:monthAchieveRate>=100?"#4a7a5a":cc.muted, fontWeight:500 }}>
                  {fmt(monthTotalSales)} / {fmt(monthlyTarget)} ({monthAchieveRate}%)
                </span>
              </div>
              <div style={{ height:3, background:cc.bg2, borderRadius:2 }}>
                <div style={{ height:"100%", width:`${Math.min(100,monthAchieveRate)}%`, background:monthAchieveRate>=100?"#4a7a5a":cc.gold, borderRadius:2, transition:"width 0.5s" }}/>
              </div>
            </>
          )}
          {dailyTarget===0 && (
            <div style={{ fontSize:9, color:cc.muted2 }}>※店舗設定で月間目標を設定すると目標達成度が表示されます</div>
          )}
        </div>

        {/* レジ残高 */}
        <div style={{ background:cc.bg3, border:`1px solid ${cc.gold}55`, borderRadius:10, padding:"10px 12px", marginBottom:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8 }}>
            <span style={{ fontSize:11, color:cc.muted, fontWeight:600 }}>💰 レジ残高</span>
            <span style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:cc.gold, fontWeight:600 }}>{fmt(todayRegisterBalance)}</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, fontSize:9, color:cc.muted2 }}>
            <div style={{ textAlign:"center", padding:"6px 4px", background:cc.bg2, borderRadius:6 }}>
              <div style={{ marginBottom:2 }}>前日残高</div>
              <div style={{ color:cc.cream, fontSize:11, fontWeight:500 }}>{fmt(prevRegisterBalance)}</div>
            </div>
            <div style={{ textAlign:"center", padding:"6px 4px", background:cc.bg2, borderRadius:6 }}>
              <div style={{ marginBottom:2 }}>＋ 現金売上</div>
              <div style={{ color:"#4a7a5a", fontSize:11, fontWeight:500 }}>{fmt(todayCashIn)}</div>
            </div>
            <div style={{ textAlign:"center", padding:"6px 4px", background:cc.bg2, borderRadius:6 }}>
              <div style={{ marginBottom:2 }}>− レジ出金</div>
              <div style={{ color:todayCashOut>0?cc.accent:cc.muted2, fontSize:11, fontWeight:500 }}>{fmt(todayCashOut)}</div>
            </div>
          </div>
          {todayCashOut>0 && (
            <div style={{ fontSize:9, color:cc.muted2, marginTop:6, textAlign:"center" }}>
              💡 本日の出金は「💸 レジ出金」タブで確認・編集できます
            </div>
          )}
        </div>

        {/* 次回予約・レジ残高 */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
          {numInp("次回予約数", "nextReserves")}
          <div>
            <label style={{ fontSize:10, color:cc.muted, display:"block", marginBottom:3 }}>レジ残高（円）</label>
            <input type="number" inputMode="numeric" value={form.register} onChange={e=>setForm(f=>({...f,register:e.target.value}))}
              placeholder="10000"
              style={{ width:"100%", background:cc.bg2, border:`1px solid ${cc.border2}`, borderRadius:8, padding:"8px 10px", color:cc.cream, fontSize:12 }}/>
          </div>
        </div>

        {/* メモ・備考 */}
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:10,color:cc.muted }}>📝 備考・メモ（任意）</label>
          <textarea value={form.memo} onChange={e=>setForm(f=>({...f,memo:e.target.value}))} placeholder="特記事項・修正時の理由など"
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginTop:3,height:52,resize:"none",fontFamily:"Jost,sans-serif" }}/>
        </div>

        <button onClick={openConfirmStore} style={{ width:"100%", background:saved?"#4a7a5a":cc.gold, color:cc.bg, border:"none", borderRadius:10, padding:11, fontSize:13, fontWeight:600, cursor:"pointer", transition:"background 0.3s" }}>
          {saved?"✓ 保存しました":existing?"修正内容を確認":"内容を確認して登録"}
        </button>

        {/* 確認モーダル */}
        {confirmStore && (()=>{
          const st=stores.find(s=>s.id===form.storeId);
          const inputterName = role==="staff"?(staff.find(s=>s.id===activeStaffId)?.name||"スタッフ"):role==="clerk"?"事務員":"オーナー";
          return (
            <div style={{ position:"fixed",inset:0,background:"#0008",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
              <div style={{ background:cc.bg1,borderRadius:16,padding:20,width:"100%",maxWidth:320,border:`1px solid ${cc.border2}` }}>
                <div style={{ fontSize:14,color:cc.gold,fontWeight:700,marginBottom:14,textAlign:"center" }}>
                  {existing?"修正内容の確認":"入力内容の確認"}
                </div>
                {[
                  ["日付", form.date],
                  ["店舗", st?.name||""],
                  ["入力者", inputterName],
                  ["総売上（自動）", `${fmt(totalSales)}  (税抜 ${fmt(Math.round(totalSales/TAX_RATE))})`],
                  ...((parseInt(form.cashSales)||0)>0 ? [["　 現金", fmt(parseInt(form.cashSales)||0)]] : []),
                  ...((parseInt(form.creditSales)||0)>0 ? [["　 クレジット", fmt(parseInt(form.creditSales)||0)]] : []),
                  ...((parseInt(form.pointSales)||0)>0 ? [["　 ポイント", fmt(parseInt(form.pointSales)||0)]] : []),
                  ...((parseInt(form.otherSales)||0)>0 ? [["　 その他", fmt(parseInt(form.otherSales)||0)]] : []),
                  ["新規客数", `${parseInt(form.newGuests)||0}名`],
                  ["顧客数", `${parseInt(form.regularGuests)||0}名`],
                  ["総人数", `${totalGuests}名`],
                  ["次回予約数", `${parseInt(form.nextReserves)||0}件`],
                  ["レジ残高", fmt(parseInt(form.register)||10000)],
                  ...(form.memo?[["メモ",form.memo]]:[]),
                ].map(([label,val])=>(
                  <div key={label} style={{ display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${cc.border}`,fontSize:12 }}>
                    <span style={{ color:cc.muted }}>{label}</span>
                    <span style={{ color:cc.cream,fontWeight:500 }}>{val}</span>
                  </div>
                ))}
                <div style={{ display:"flex",gap:8,marginTop:14 }}>
                  <button onClick={save} style={{ flex:2,background:cc.gold,color:cc.bg,border:"none",borderRadius:8,padding:11,fontSize:13,fontWeight:600,cursor:"pointer" }}>登録する</button>
                  <button onClick={()=>setConfirmStore(false)} style={{ flex:1,background:cc.bg2,color:cc.muted,border:`1px solid ${cc.border}`,borderRadius:8,padding:11,fontSize:12,cursor:"pointer" }}>戻る</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* recent store sales */}
      <div style={cardS(role)}>
        <div style={{ fontSize:11, color:cc.muted, marginBottom:12 }}>最近の店舗売上</div>
        {storeSales.slice(0,10).length===0
          ? <div style={{ textAlign:"center", fontSize:12, color:cc.muted2, padding:12 }}>まだ入力がありません</div>
          : storeSales.slice(0,10).map(s=>{
            const st=stores.find(x=>x.id===s.storeId);
            return (
              <div key={s.key} style={{ padding:"8px 0", borderBottom:`1px solid ${cc.border}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:3 }}>
                  <span style={{ fontSize:13, color:cc.cream, fontWeight:500 }}>
                    🏪 <span style={{ color:cc.gold, fontWeight:600 }}>{st?.name||"-"}</span>
                    <span style={{ fontSize:11, color:cc.muted2, marginLeft:6 }}>{s.date}</span>
                  </span>
                  <span style={{ fontSize:14, fontWeight:600, color:cc.gold }}>{fmt(s.totalSales)}</span>
                </div>
                <div style={{ display:"flex", gap:10, fontSize:10, color:cc.muted2, flexWrap:"wrap" }}>
                  <span>新規 {s.newGuests}名</span><span>顧客 {s.regularGuests}名</span><span>計 {s.totalGuests}名</span>
                  <span>PT {fmt(s.pointSales)}</span><span>次回予約 {s.nextReserves}</span><span>レジ {fmt(s.register)}</span>
                </div>
                {s.memo && <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>📝 {s.memo}</div>}
                <div style={{ fontSize:9, color:cc.muted2, marginTop:2 }}>
                  入力: {s.inputBy} {s.inputAt}
                  {s.inputLog?.length>1 && ` ／ 修正${s.inputLog.length-1}回`}
                </div>
              </div>
            );
          })
        }
      </div>
      </>
      )}
      <SaveStatusModal status={saveStatus} message={saveMsg} onClose={()=>setSaveStatus(null)}/>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════
function StoreMaster({ stores, setStores }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({});

  // その月の平日・週末日数を取得
  const countDays = (yy, mm) => {
    const days = new Date(yy, mm, 0).getDate();
    let wd=0, we=0;
    for (let d=1; d<=days; d++) {
      const dow = new Date(yy, mm-1, d).getDay();
      if (dow===0 || dow===6) we++; else wd++;
    }
    return { weekday: wd, weekend: we, total: days };
  };

  const now = new Date();
  const { weekday: curWd, weekend: curWe } = countDays(now.getFullYear(), now.getMonth()+1);

  // 月間目標から平日/週末の日割りを逆算（週末は平日の1.5倍で配分）
  const WEEKEND_RATIO = 1.5;
  const calcDailyFromMonthly = (monthly, wd, we) => {
    const denom = wd + we * WEEKEND_RATIO;
    if (denom <= 0 || !monthly) return { weekday: 0, weekend: 0 };
    const weekday = Math.round(monthly / denom);
    const weekend = Math.round(weekday * WEEKEND_RATIO);
    return { weekday, weekend };
  };

  const save = () => {
    const monthlyNormal = parseInt(form.targetNormal)||0;
    const monthlyBusy = parseInt(form.targetBusy)||0;
    const { weekday: wdN, weekend: weN } = calcDailyFromMonthly(monthlyNormal, curWd, curWe);
    const { weekday: wdB, weekend: weB } = calcDailyFromMonthly(monthlyBusy, curWd, curWe);
    setStores(prev=>prev.map(s=>s.id===editing?{...s,
      name: form.name,
      targetNormal: monthlyNormal,
      targetBusy: monthlyBusy,
      weekdayTarget: wdN,
      weekendTarget: weN,
      weekdayTargetBusy: wdB,
      weekendTargetBusy: weB,
      irregularClosedDates: form.irregularClosedDates || [],
      shiftWeekdayMin: parseInt(form.shiftWeekdayMin) || 1,
      shiftWeekdayMax: parseInt(form.shiftWeekdayMax) || 2,
      shiftWeekendMin: parseInt(form.shiftWeekendMin) || 2,
      shiftWeekendMax: parseInt(form.shiftWeekendMax) || 2,
      commissionTiers: (form.useCustomTiers && Array.isArray(form.commissionTiers))
        ? form.commissionTiers.filter(t=>t.taxEx>0&&t.rate>0).map(t=>({taxEx:parseInt(t.taxEx)||0, rate:parseFloat(t.rate)||0}))
        : [],
    }:s));
    setEditing(null);
  };

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <div style={{ ...card({marginBottom:12, background:C.bg2}), fontSize:11, color:C.muted }}>
        <div style={{ fontWeight:600, color:C.gold, marginBottom:6 }}>📊 歩合給ティア（デフォルト）</div>
        {(() => {
          const sorted = [...DEFAULT_COMMISSION_TIERS].sort((a,b)=>a.taxEx-b.taxEx);
          const lowestMan = sorted[0].taxEx/10000;
          return [
            [`~${lowestMan-1}万(税抜~${lowestMan-1}万)`, "なし"],
            ...sorted.map(t => [`${t.taxEx/10000}万~(税抜${t.taxEx/10000}万~)`, `${t.rate}%`])
          ];
        })().map(([range,rate])=>(
          <div key={range} style={{ display:"flex", justifyContent:"space-between", fontSize:10, marginBottom:3 }}>
            <span>{range}</span><span style={{ color:C.gold, fontWeight:600 }}>{rate}</span>
          </div>
        ))}
        <div style={{ fontSize:9, color:C.accent, marginTop:6 }}>※インセンティブは税抜き金額（売上÷1.1）で計算／店舗ごとに変更可能</div>
      </div>
      <div style={{ fontSize:11, color:C.muted, marginBottom:12, lineHeight:1.6 }}>
        店舗の <span style={{color:C.gold}}>月間目標額</span> を入力すると、平日と週末の日割り目標を自動計算します（週末は平日の約1.5倍として配分）
      </div>
      {stores.map(s=>{
        const isEdit=editing===s.id;
        const editNormal = parseInt(form.targetNormal)||0;
        const editBusy = parseInt(form.targetBusy)||0;
        const { weekday: editWdN, weekend: editWeN } = calcDailyFromMonthly(editNormal, curWd, curWe);
        const { weekday: editWdB, weekend: editWeB } = calcDailyFromMonthly(editBusy, curWd, curWe);
        const displayMonthly = isBusy() ? (s.targetBusy||0) : (s.targetNormal||0);
        const displayWd = isBusy() ? (s.weekdayTargetBusy||s.weekdayTarget||0) : (s.weekdayTarget||0);
        const displayWe = isBusy() ? (s.weekendTargetBusy||s.weekendTarget||0) : (s.weekendTarget||0);
        return (
          <div key={s.id} style={{ ...card(), marginBottom:10 }}>
            {isEdit ? (
              <div>
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:10, color:C.muted }}>店舗名</label>
                  <input value={form.name||""} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                    style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"7px 9px", color:C.cream, fontSize:12, marginTop:3 }}/>
                </div>

                <div style={{ fontSize:11, color:C.gold, fontWeight:600, marginBottom:6 }}>🗓 通常期の月間目標額（2・3・4・5・6・9・10・11月）</div>
                <input type="number" inputMode="numeric" value={form.targetNormal||""} onChange={e=>setForm(f=>({...f,targetNormal:e.target.value}))}
                  placeholder="例: 1400000"
                  style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 10px", color:C.cream, fontSize:14, marginBottom:6 }}/>
                {editNormal>0 && (
                  <div style={{ background:C.bg2, borderRadius:6, padding:"6px 10px", fontSize:10, color:C.muted, marginBottom:12, lineHeight:1.6 }}>
                    ↳ 今月の試算（平日{curWd}日 / 週末祝{curWe}日）<br/>
                    平日: <span style={{color:C.cream}}>{fmt(editWdN)}</span> / 週末祝: <span style={{color:C.cream}}>{fmt(editWeN)}</span>
                  </div>
                )}

                <div style={{ fontSize:11, color:C.gold, fontWeight:600, marginBottom:6 }}>🔥 繁忙期の月間目標額（7・8・12・1月）</div>
                <input type="number" inputMode="numeric" value={form.targetBusy||""} onChange={e=>setForm(f=>({...f,targetBusy:e.target.value}))}
                  placeholder="例: 1800000"
                  style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 10px", color:C.cream, fontSize:14, marginBottom:6 }}/>
                {editBusy>0 && (
                  <div style={{ background:C.bg2, borderRadius:6, padding:"6px 10px", fontSize:10, color:C.muted, marginBottom:12, lineHeight:1.6 }}>
                    ↳ 繁忙月の試算（平日{curWd}日 / 週末祝{curWe}日）<br/>
                    平日: <span style={{color:C.cream}}>{fmt(editWdB)}</span> / 週末祝: <span style={{color:C.cream}}>{fmt(editWeB)}</span>
                  </div>
                )}

                {/* 不定期定休日カレンダー */}
                <div style={{ fontSize:11, color:C.gold, fontWeight:600, marginBottom:6 }}>🚫 不定期定休日・臨時休業</div>
                <div style={{ fontSize:10, color:C.muted2, marginBottom:8, lineHeight:1.5 }}>
                  日付をタップして選択（曜日定休以外の臨時休業日）。シフト生成・目標計算から自動除外されます。
                </div>
                {(() => {
                  const sel = new Set(form.irregularClosedDates || []);
                  const toggleDate = (dateStr) => {
                    const newSet = new Set(sel);
                    if (newSet.has(dateStr)) newSet.delete(dateStr); else newSet.add(dateStr);
                    setForm(f => ({ ...f, irregularClosedDates: Array.from(newSet).sort() }));
                  };
                  // 表示する月（当月・翌月・翌々月の3ヶ月分）
                  const months = [];
                  for (let offset=0; offset<3; offset++) {
                    const d = new Date(now.getFullYear(), now.getMonth()+offset, 1);
                    months.push({ y:d.getFullYear(), m:d.getMonth()+1 });
                  }
                  const weekdayClosed = form.shiftClosedDays || [];
                  const DAYS = ["日","月","火","水","木","金","土"];
                  return (
                    <div style={{ marginBottom:12 }}>
                      {months.map(({y,m}) => {
                        const daysInM = new Date(y, m, 0).getDate();
                        const firstDow = new Date(y, m-1, 1).getDay();
                        return (
                          <div key={`${y}-${m}`} style={{ background:C.bg2, borderRadius:8, padding:"8px 10px", marginBottom:8 }}>
                            <div style={{ fontSize:10, color:C.gold, fontWeight:600, marginBottom:5 }}>{y}年{m}月</div>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:2 }}>
                              {DAYS.map((d,i) => (
                                <div key={d} style={{ textAlign:"center", fontSize:8, color:i===0||i===6?"#c75a5a":C.muted }}>{d}</div>
                              ))}
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
                              {Array.from({length:firstDow}).map((_,i) => <div key={`e${i}`}/>)}
                              {Array.from({length:daysInM}).map((_,i) => {
                                const d = i+1;
                                const dateStr = `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                                const dow = new Date(y, m-1, d).getDay();
                                const isWeekdayClosed = weekdayClosed.includes(dow);
                                const isSel = sel.has(dateStr);
                                const isRed = dow===0||dow===6;
                                let bg, color, border;
                                if (isWeekdayClosed) { bg="#ddd"; color="#999"; border="1px solid #ccc"; }
                                else if (isSel) { bg=C.gold; color="#fff"; border=`1px solid ${C.gold}`; }
                                else { bg="#fff"; color=isRed?"#c75a5a":C.cream; border=`1px solid ${C.border}`; }
                                return (
                                  <div key={d}
                                    role="button"
                                    onClick={()=>{ if (!isWeekdayClosed) toggleDate(dateStr); }}
                                    onTouchEnd={(e)=>{ if (!isWeekdayClosed) { e.preventDefault(); toggleDate(dateStr); } }}
                                    style={{
                                      textAlign:"center", fontSize:10, padding:"6px 0", borderRadius:4,
                                      background:bg, color, border,
                                      cursor:isWeekdayClosed?"default":"pointer",
                                      fontWeight:isSel?600:400,
                                      userSelect:"none",
                                      WebkitUserSelect:"none",
                                      WebkitTapHighlightColor:"transparent",
                                      touchAction:"manipulation",
                                    }}>{d}</div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {(form.irregularClosedDates||[]).length > 0 && (
                        <div style={{ fontSize:10, color:C.muted, padding:"6px 10px", background:C.bg2, borderRadius:6 }}>
                          選択中: {(form.irregularClosedDates||[]).length}日
                          <button onClick={()=>setForm(f=>({...f,irregularClosedDates:[]}))} style={{ marginLeft:8, background:"none", border:"none", color:C.accent, fontSize:10, cursor:"pointer" }}>クリア</button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* シフト人数編集 */}
                <div style={{ fontSize:11, color:C.gold, fontWeight:600, marginBottom:6 }}>👥 シフト人数</div>
                <div style={{ background:C.bg2, borderRadius:6, padding:"10px 12px", marginBottom:12 }}>
                  <div style={{ fontSize:9, color:C.muted, marginBottom:8, lineHeight:1.5 }}>
                    💡 各日のシフトに入る最低/最大人数。最大人数を高くすると、スタッフの勤務日数が増えやすくなります
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"60px 1fr 1fr", gap:8, alignItems:"center", marginBottom:8 }}>
                    <span style={{ fontSize:10, color:C.muted }}>平日</span>
                    <div>
                      <label style={{ fontSize:9, color:C.muted2 }}>最低人数</label>
                      <input type="number" min="0" value={form.shiftWeekdayMin === undefined || form.shiftWeekdayMin === null ? "1" : form.shiftWeekdayMin}
                        onChange={e=>setForm(f=>({...f, shiftWeekdayMin: e.target.value === "" ? "" : Number(e.target.value)}))}
                        onFocus={e=>e.target.select()}
                        style={{ width:"100%", background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"5px 8px", color:C.cream, fontSize:11, marginTop:2 }}/>
                    </div>
                    <div>
                      <label style={{ fontSize:9, color:C.muted2 }}>最大人数</label>
                      <input type="number" min="1" value={form.shiftWeekdayMax === undefined || form.shiftWeekdayMax === null ? "2" : form.shiftWeekdayMax}
                        onChange={e=>setForm(f=>({...f, shiftWeekdayMax: e.target.value === "" ? "" : Number(e.target.value)}))}
                        onFocus={e=>e.target.select()}
                        style={{ width:"100%", background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"5px 8px", color:C.cream, fontSize:11, marginTop:2 }}/>
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"60px 1fr 1fr", gap:8, alignItems:"center" }}>
                    <span style={{ fontSize:10, color:C.muted }}>週末・祝日</span>
                    <div>
                      <label style={{ fontSize:9, color:C.muted2 }}>最低人数</label>
                      <input type="number" min="0" value={form.shiftWeekendMin === undefined || form.shiftWeekendMin === null ? "2" : form.shiftWeekendMin}
                        onChange={e=>setForm(f=>({...f, shiftWeekendMin: e.target.value === "" ? "" : Number(e.target.value)}))}
                        onFocus={e=>e.target.select()}
                        style={{ width:"100%", background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"5px 8px", color:C.cream, fontSize:11, marginTop:2 }}/>
                    </div>
                    <div>
                      <label style={{ fontSize:9, color:C.muted2 }}>最大人数</label>
                      <input type="number" min="1" value={form.shiftWeekendMax === undefined || form.shiftWeekendMax === null ? "2" : form.shiftWeekendMax}
                        onChange={e=>setForm(f=>({...f, shiftWeekendMax: e.target.value === "" ? "" : Number(e.target.value)}))}
                        onFocus={e=>e.target.select()}
                        style={{ width:"100%", background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"5px 8px", color:C.cream, fontSize:11, marginTop:2 }}/>
                    </div>
                  </div>
                </div>

                {/* 歩合給ティア編集 */}
                <div style={{ fontSize:11, color:C.gold, fontWeight:600, marginBottom:6 }}>💰 歩合給ティア</div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:C.muted, cursor:"pointer" }}>
                    <input type="checkbox" checked={!!form.useCustomTiers}
                      onChange={e=>setForm(f=>({
                        ...f,
                        useCustomTiers:e.target.checked,
                        commissionTiers: e.target.checked && (!Array.isArray(f.commissionTiers)||f.commissionTiers.length===0)
                          ? DEFAULT_COMMISSION_TIERS.slice().sort((a,b)=>a.taxEx-b.taxEx)
                          : f.commissionTiers
                      }))}/>
                    この店舗の歩合ティアを個別設定する
                  </label>
                </div>
                {!form.useCustomTiers ? (
                  <div style={{ background:C.bg2, borderRadius:6, padding:"6px 10px", fontSize:10, color:C.muted, marginBottom:12, lineHeight:1.6 }}>
                    デフォルト：{[...DEFAULT_COMMISSION_TIERS].sort((a,b)=>a.taxEx-b.taxEx).map(t=>`税抜${t.taxEx/10000}万~ ${t.rate}%`).join(" ／ ")}
                  </div>
                ) : (
                  <div style={{ background:C.bg2, borderRadius:6, padding:"8px 10px", marginBottom:12 }}>
                    {(form.commissionTiers||[]).map((t, idx) => (
                      <div key={idx} style={{ display:"flex", gap:6, alignItems:"center", marginBottom:6 }}>
                        <span style={{ fontSize:10, color:C.muted, minWidth:50 }}>税抜き</span>
                        <input type="number" inputMode="numeric" value={t.taxEx||""}
                          onChange={e=>{
                            const v = e.target.value;
                            setForm(f=>({...f, commissionTiers: f.commissionTiers.map((x,i)=>i===idx?{...x,taxEx:parseInt(v)||0}:x)}));
                          }}
                          placeholder="550000"
                          style={{ flex:1, background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"5px 8px", color:C.cream, fontSize:11 }}/>
                        <span style={{ fontSize:10, color:C.muted }}>円 〜</span>
                        <input type="number" step="0.1" value={t.rate||""}
                          onChange={e=>{
                            const v = e.target.value;
                            setForm(f=>({...f, commissionTiers: f.commissionTiers.map((x,i)=>i===idx?{...x,rate:parseFloat(v)||0}:x)}));
                          }}
                          placeholder="5"
                          style={{ width:56, background:C.bg1, border:`1px solid ${C.border2}`, borderRadius:6, padding:"5px 8px", color:C.cream, fontSize:11 }}/>
                        <span style={{ fontSize:10, color:C.muted }}>%</span>
                        <button onClick={()=>setForm(f=>({...f, commissionTiers: f.commissionTiers.filter((_,i)=>i!==idx)}))}
                          style={{ background:"none", border:"none", color:C.accent, fontSize:14, cursor:"pointer", padding:"0 4px" }}>×</button>
                      </div>
                    ))}
                    <button onClick={()=>setForm(f=>({...f, commissionTiers:[...(f.commissionTiers||[]), {taxEx:0, rate:0}]}))}
                      style={{ background:"none", border:`1px dashed ${C.border2}`, borderRadius:6, padding:"4px 10px", color:C.gold, fontSize:10, cursor:"pointer", width:"100%" }}>
                      + 段階を追加
                    </button>
                  </div>
                )}

                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={save} style={{ flex:2, background:C.gold, color:C.bg, border:"none", borderRadius:8, padding:9, fontSize:12, fontWeight:600, cursor:"pointer" }}>保存</button>
                  <button onClick={()=>setEditing(null)} style={{ flex:1, background:C.bg2, color:C.muted, border:`1px solid ${C.border}`, borderRadius:8, padding:9, fontSize:12, cursor:"pointer" }}>戻る</button>
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontSize:13, color:C.cream, fontWeight:500 }}>{s.name}</div>
                  <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>
                    今月目標: <span style={{color:C.gold,fontWeight:500}}>{fmt(displayMonthly)}</span>
                  </div>
                  <div style={{ fontSize:10, color:C.muted2, marginTop:1 }}>
                    日割 平日 {fmt(displayWd)} / 週末祝 {fmt(displayWe)}
                  </div>
                  {(s.irregularClosedDates||[]).length > 0 && (
                    <div style={{ fontSize:10, color:C.accent, marginTop:1 }}>
                      🚫 臨時休業 {(s.irregularClosedDates||[]).length}日 登録
                    </div>
                  )}
                </div>
                <button onClick={()=>{
                  setEditing(s.id);
                  setForm({
                    ...s,
                    useCustomTiers: Array.isArray(s.commissionTiers) && s.commissionTiers.length > 0,
                    commissionTiers: (Array.isArray(s.commissionTiers) && s.commissionTiers.length > 0)
                      ? [...s.commissionTiers].sort((a,b)=>a.taxEx-b.taxEx)
                      : DEFAULT_COMMISSION_TIERS.slice().sort((a,b)=>a.taxEx-b.taxEx),
                  });
                }} style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:8, padding:"5px 12px", color:C.gold, fontSize:11, cursor:"pointer" }}>編集</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════
// STAFF SALES VIEW (owner — read-only)
// ═══════════════════════════════════════════════════════════════════════════
function StaffSalesView({ records, staff, stores }) {
  const curYM = nowYM();
  const [monthFilter, setMonthFilter] = useState(curYM);
  const [storeFilter, setStoreFilter] = useState("all");

  const allMonths = useMemo(()=>{
    const set=new Set(records.map(r=>ym(r.date)));
    // 現月と前月は必ず選択肢に含める
    set.add(curYM);
    const [y,m]=curYM.split("-").map(Number);
    const pm=new Date(y, m-2, 1);
    set.add(`${pm.getFullYear()}-${String(pm.getMonth()+1).padStart(2,"0")}`);
    return [...set].sort((a,b)=>b.localeCompare(a));
  },[records,curYM]);

  const filteredStaff = storeFilter==="all" ? staff : staff.filter(s=>(s.storeIds||[s.storeId]).includes(storeFilter));

  const staffStats = useMemo(()=>filteredStaff.map(s=>{
    const sr=records.filter(r=>r.staffId===s.id&&ym(r.date)===monthFilter);
    const sales=sr.reduce((a,r)=>a+r.amount,0);
    const totalGuests=sr.reduce((a,r)=>a+(r.totalGuests||0),0);
    const newGuests=sr.reduce((a,r)=>a+(r.newGuests||0),0);
    const shimei=sr.reduce((a,r)=>a+(r.shimei||0),0);
    const nextReserve=sr.reduce((a,r)=>a+(r.nextReserve||0),0);
    const workHours=Math.round(sr.reduce((a,r)=>a+(r.workHours||0),0)*10)/10;
    const recordCount = sr.length; // 出社日数 = レコード数
    const avg=totalGuests>0?Math.round(sales/totalGuests):0;
    const pct=Math.min(100,Math.round(sales/(s.target||1)*100));
    const myStore = stores.find(st=>st.id===s.storeId);
    const commission = s.rate > 0 ? Math.round(sales*(s.rate/100)) : calcCommission(sales, myStore);
    const currentRate = s.rate > 0 ? s.rate : getCommissionRate(sales, myStore);
    return { ...s, sales, totalGuests, newGuests, shimei, nextReserve, workHours, recordCount, avg, pct, commission, currentRate };
  }).sort((a,b)=>b.sales-a.sales),[records,filteredStaff,monthFilter,stores]);

  const totalSales=staffStats.reduce((a,s)=>a+s.sales,0);

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <div style={{ display:"flex", gap:6, marginBottom:10, overflowX:"auto" }}>
        {[{id:"all",name:"全店"},...stores].map(st=>(
          <button key={st.id} onClick={()=>setStoreFilter(st.id)} style={{
            background:storeFilter===st.id?C.gold:C.bg2, color:storeFilter===st.id?C.bg:C.muted,
            border:`1px solid ${storeFilter===st.id?C.gold:C.border}`,
            borderRadius:20, padding:"5px 14px", fontSize:11, cursor:"pointer", whiteSpace:"nowrap"
          }}>{st.name}</button>
        ))}
      </div>
      <div style={{ marginBottom:14 }}>
        <div style={{ display:"flex", gap:6, marginBottom:6 }}>
          <button onClick={()=>setMonthFilter(curYM)} style={{
            flex:1, background:monthFilter===curYM?C.gold:C.bg2, color:monthFilter===curYM?C.bg:C.muted,
            border:`1px solid ${monthFilter===curYM?C.gold:C.border}`, borderRadius:8, padding:"6px 10px", fontSize:11, cursor:"pointer", fontWeight:monthFilter===curYM?600:400
          }}>今月</button>
          <button onClick={()=>{
            const [y,m]=curYM.split("-").map(Number);
            const pm=new Date(y, m-2, 1);
            setMonthFilter(`${pm.getFullYear()}-${String(pm.getMonth()+1).padStart(2,"0")}`);
          }} style={{
            flex:1, background:monthFilter!==curYM?C.gold:C.bg2, color:monthFilter!==curYM?C.bg:C.muted,
            border:`1px solid ${monthFilter!==curYM?C.gold:C.border}`, borderRadius:8, padding:"6px 10px", fontSize:11, cursor:"pointer", fontWeight:monthFilter!==curYM?600:400
          }}>前月</button>
        </div>
        <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
          style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 10px", color:C.cream, fontSize:12 }}>
          {allMonths.map(m=><option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div style={{ ...card({marginBottom:10, padding:"10px 14px"}), display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontSize:11, color:C.muted }}>スタッフ合計</div>
        <div style={{ fontFamily:"Cormorant Garamond", fontSize:20, color:C.gold }}>{fmt(totalSales)}</div>
      </div>

      {/* 表形式（見やすさ重視） */}
      <div style={{ ...card({padding:0}), overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, color:C.cream, minWidth:600 }}>
          <thead>
            <tr style={{ background:C.bg2, borderBottom:`1px solid ${C.border}` }}>
              <th style={{ padding:"8px 6px", textAlign:"left", fontWeight:600, color:C.muted, fontSize:10 }}>順位</th>
              <th style={{ padding:"8px 6px", textAlign:"left", fontWeight:600, color:C.muted, fontSize:10 }}>氏名</th>
              <th style={{ padding:"8px 6px", textAlign:"left", fontWeight:600, color:C.muted, fontSize:10 }}>店舗</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>出社</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>個人目標</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>売上</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>達成率</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>歩合給</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>来客</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>新規</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>単価</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>指名</th>
              <th style={{ padding:"8px 6px", textAlign:"right", fontWeight:600, color:C.muted, fontSize:10 }}>次回</th>
            </tr>
          </thead>
          <tbody>
            {staffStats.map((s,i)=>{
              const storeNameSS=(s.storeIds||[s.storeId]).map(id=>stores.find(st=>st.id===id)?.name).filter(Boolean).join('・');
              const rankColor = i===0?C.gold:i===1?"#9a9a9a":i===2?"#cd7f32":C.muted2;
              return (
                <tr key={s.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:"8px 6px", color:rankColor, fontWeight:600 }}>{i+1}</td>
                  <td style={{ padding:"8px 6px", color:C.cream, fontWeight:500 }}>{s.name}</td>
                  <td style={{ padding:"8px 6px", color:C.muted, fontSize:10 }}>{storeNameSS}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right", color:C.muted }}>{s.recordCount||0}日</td>
                  <td style={{ padding:"8px 6px", textAlign:"right", color:C.muted2 }}>{fmt(s.target||0)}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right", color:C.gold, fontWeight:600, fontFamily:"Cormorant Garamond", fontSize:13 }}>{fmt(s.sales)}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right", color:s.pct>=100?C.green:C.muted, fontWeight:600 }}>{s.pct}%</td>
                  <td style={{ padding:"8px 6px", textAlign:"right", color:s.commission>0?C.gold:C.muted2, fontFamily:"Cormorant Garamond", fontSize:12 }}>{s.commission>0?fmt(s.commission):"—"}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right" }}>{s.totalGuests}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right" }}>{s.newGuests}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right" }}>{fmt(s.avg)}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right" }}>{s.shimei}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right" }}>{s.nextReserve}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
function StaffMaster({ staff, setStaff, stores, role }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({});
  const showSalary = role==="owner";

  const save = () => {
    setStaff(prev=>prev.map(s=>s.id===editing
      ? { ...s, name:form.name, storeId:form.storeId, pw:form.pw||"0000", email:form.email||"", joinDate:form.joinDate||"",
          shiftMaxDays:parseInt(form.shiftMaxDays)||21,
          shiftType:form.shiftType||"regular",
          shiftSlots:form.shiftSlots||[],
          shiftFixedDays:form.shiftFixedDays||[],
          shiftNote:form.shiftNote||"",
          ...(showSalary?{baseSalary:parseInt(form.baseSalary),rate:parseInt(form.rate),target:parseInt(form.target),contractHours:parseInt(form.contractHours)||0}:{}) }
      : s));
    setEditing(null);
  };
  const add = () => {
    const id=`p${Date.now()}`, ns={ id, name:"新スタッフ", storeId:stores[0].id, baseSalary:180000, rate:10, target:500000, contractHours:160, pw:"0000", email:"", joinDate:"",
      shiftMaxDays:21, shiftType:"regular", shiftSlots:["①","②"], shiftFixedDays:[], shiftNote:"", paidLeaveAnnual:0, paidLeaveInitial:0 };
    setStaff(prev=>[...prev,ns]); setEditing(id); setForm(ns);
  };
  const remove = id => { if(window.confirm("このスタッフを削除しますか？")) setStaff(prev=>prev.filter(s=>s.id!==id)); };

  const toggleSlot = (slot) => {
    const cur = form.shiftSlots || [];
    const newSlots = cur.includes(slot) ? cur.filter(s=>s!==slot) : [...cur, slot].sort();
    setForm(f=>({...f, shiftSlots:newSlots}));
  };
  const toggleFixedDay = (day) => {
    const cur = form.shiftFixedDays || [];
    const newDays = cur.includes(day) ? cur.filter(d=>d!==day) : [...cur, day].sort();
    setForm(f=>({...f, shiftFixedDays:newDays}));
  };
  const DAY_LABELS = ["日","月","火","水","木","金","土"];
  const SLOT_OPTIONS = ["①","②","③"];
  const TYPE_OPTIONS = [{v:"regular",l:"正社員"},{v:"part",l:"パート"},{v:"fixed",l:"固定時間"}];

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <button onClick={add} style={{ width:"100%", background:C.bg2, border:`1px dashed ${C.border2}`, borderRadius:12, padding:12, color:C.gold, fontSize:12, cursor:"pointer", marginBottom:12 }}>＋ スタッフ追加</button>
      {staff.map(s=>{
        const storeNames=(s.storeIds||[s.storeId]).map(id=>stores.find(st=>st.id===id)?.name).filter(Boolean).join('・'); const isEdit=editing===s.id;
        return (
          <div key={s.id} style={{ ...card(), marginBottom:10 }}>
            {isEdit ? (
              <div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                  <div><label style={{ fontSize:10,color:C.muted }}>氏名</label>
                    <input value={form.name||""} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                      style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                  <div><label style={{ fontSize:10,color:C.muted }}>店舗</label>
                    <select value={form.storeId||""} onChange={e=>setForm(f=>({...f,storeId:e.target.value}))}
                      style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}>
                      {stores.map(st=><option key={st.id} value={st.id}>{st.name}</option>)}
                    </select></div>
                  {showSalary && <>
                    <div><label style={{ fontSize:10,color:C.muted }}>基本給（円）</label>
                      <input type="number" value={form.baseSalary||""} onChange={e=>setForm(f=>({...f,baseSalary:e.target.value}))}
                        style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                    <div><label style={{ fontSize:10,color:C.muted }}>歩合率（%）</label>
                      <input type="number" value={form.rate||""} onChange={e=>setForm(f=>({...f,rate:e.target.value}))}
                        style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                    <div style={{ gridColumn:"1/-1" }}><label style={{ fontSize:10,color:C.muted }}>月間目標（円）</label>
                      <input type="number" value={form.target||""} onChange={e=>setForm(f=>({...f,target:e.target.value}))}
                        style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                    <div style={{ gridColumn:"1/-1" }}><label style={{ fontSize:10,color:C.muted }}>月間契約実働時間（h）</label>
                      <input type="number" value={form.contractHours||""} onChange={e=>setForm(f=>({...f,contractHours:e.target.value}))}
                        style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                    <div style={{ gridColumn:"1/-1" }}><label style={{ fontSize:10,color:C.muted }}>ログインPW（初期値: 0000）</label>
                      <input type="text" value={form.pw||""} onChange={e=>setForm(f=>({...f,pw:e.target.value}))} placeholder="0000"
                        style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                    <div><label style={{ fontSize:10,color:C.muted }}>メールアドレス</label>
                      <input type="email" value={form.email||""} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="example@mail.com"
                        style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                    <div><label style={{ fontSize:10,color:C.muted }}>入社年月日</label>
                      <input type="date" value={form.joinDate||""} onChange={e=>setForm(f=>({...f,joinDate:e.target.value}))}
                        style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/></div>
                  </>}
                </div>

                {/* 🌴 有給休暇設定 */}
                {showSalary && (
                  <div style={{ marginTop:12, padding:12, background:C.bg2, borderRadius:8, border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:11, color:C.gold, fontWeight:600, marginBottom:8 }}>🌴 有給休暇</div>
                    <div style={{ fontSize:10, color:C.muted2, marginBottom:8, lineHeight:1.5 }}>
                      💡 入社日から6ヶ月後に初回付与、その後1年ごとに自動付与（2年で失効）<br/>
                      初回有給残数：中途入社時の引継ぎ分を入力（失効なし）
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      <div>
                        <label style={{ fontSize:10,color:C.muted }}>年間付与日数</label>
                        <input type="number" value={form.paidLeaveAnnual||""} onChange={e=>setForm(f=>({...f,paidLeaveAnnual:e.target.value}))} placeholder="例: 10"
                          style={{ width:"100%",background:C.bg1,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/>
                      </div>
                      <div>
                        <label style={{ fontSize:10,color:C.muted }}>初回有給残数（手動）</label>
                        <input type="number" value={form.paidLeaveInitial||""} onChange={e=>setForm(f=>({...f,paidLeaveInitial:e.target.value}))} placeholder="例: 5"
                          style={{ width:"100%",background:C.bg1,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/>
                      </div>
                    </div>
                  </div>
                )}

                {showSalary && (
                  <div style={{ marginTop:12, padding:12, background:C.bg2, borderRadius:8, border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:11, color:C.gold, fontWeight:600, marginBottom:8 }}>📅 シフトルール</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                      <div>
                        <label style={{ fontSize:10,color:C.muted }}>最大出勤日数/月</label>
                        <input type="number" value={form.shiftMaxDays||""} onChange={e=>setForm(f=>({...f,shiftMaxDays:e.target.value}))}
                          style={{ width:"100%",background:C.bg1,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/>
                      </div>
                      <div>
                        <label style={{ fontSize:10,color:C.muted }}>勤務タイプ</label>
                        <select value={form.shiftType||"regular"} onChange={e=>setForm(f=>({...f,shiftType:e.target.value}))}
                          style={{ width:"100%",background:C.bg1,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}>
                          {TYPE_OPTIONS.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ marginBottom:8 }}>
                      <label style={{ fontSize:10,color:C.muted }}>対応可能スロット（時間帯）</label>
                      <div style={{ display:"flex", gap:6, marginTop:5 }}>
                        {SLOT_OPTIONS.map(sl=>{
                          const act = (form.shiftSlots||[]).includes(sl);
                          return (
                            <button key={sl} type="button" onClick={()=>toggleSlot(sl)} style={{
                              flex:1, padding:"8px 0", borderRadius:8,
                              border:`1px solid ${act?C.gold:C.border2}`,
                              background: act ? C.gold : C.bg1,
                              color: act ? "#fff" : C.muted,
                              fontSize:13, fontWeight:600, cursor:"pointer"
                            }}>{sl}</button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize:10, color:C.muted2, marginTop:3 }}>※ 固定時間の人は未選択でOK</div>
                    </div>

                    <div style={{ marginBottom:8 }}>
                      <label style={{ fontSize:10,color:C.muted }}>固定曜日（指定した曜日のみ出勤）</label>
                      <div style={{ display:"flex", gap:4, marginTop:5 }}>
                        {DAY_LABELS.map((label,i)=>{
                          const act = (form.shiftFixedDays||[]).includes(i);
                          return (
                            <button key={i} type="button" onClick={()=>toggleFixedDay(i)} style={{
                              flex:1, padding:"7px 0", borderRadius:6,
                              border:`1px solid ${act?C.gold:C.border2}`,
                              background: act ? C.gold : C.bg1,
                              color: act ? "#fff" : (i===0||i===6) ? "#c75a5a" : C.muted,
                              fontSize:12, fontWeight:act?600:400, cursor:"pointer"
                            }}>{label}</button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize:10, color:C.muted2, marginTop:3 }}>※ 通常勤務は未選択（全曜日OK）</div>
                    </div>

                    <div>
                      <label style={{ fontSize:10,color:C.muted }}>備考（例: 火木のみ、固定時間）</label>
                      <input type="text" value={form.shiftNote||""} onChange={e=>setForm(f=>({...f,shiftNote:e.target.value}))} placeholder=""
                        style={{ width:"100%",background:C.bg1,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 9px",color:C.cream,fontSize:12,marginTop:3 }}/>
                    </div>
                  </div>
                )}

                <div style={{ display:"flex", gap:8, marginTop:12 }}>
                  <button onClick={save} style={{ flex:2,background:C.gold,color:C.bg,border:"none",borderRadius:8,padding:9,fontSize:12,fontWeight:600,cursor:"pointer" }}>保存</button>
                  <button onClick={()=>setEditing(null)} style={{ flex:1,background:C.bg2,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,padding:9,fontSize:12,cursor:"pointer" }}>戻る</button>
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontSize:13,color:C.cream,fontWeight:500 }}>{s.name}</div>
                  <div style={{ fontSize:10,color:C.muted,marginTop:2 }}>{storeNames}{showSalary&&`｜基本 ${fmt(s.baseSalary)}｜歩合 ${s.rate}%｜実働 ${s.contractHours||0}h/月`}</div>
                  {showSalary&&<div style={{ fontSize:10,color:C.muted2,marginTop:1 }}>PW: {"●".repeat((s.pw||"0000").length)}</div>}
                  {s.email&&<div style={{ fontSize:10,color:C.muted2,marginTop:1 }}>✉ {s.email}</div>}
                  {s.joinDate&&<div style={{ fontSize:10,color:C.muted2,marginTop:1 }}>入社: {s.joinDate}</div>}
                  {showSalary && s.shiftMaxDays && (
                    <div style={{ fontSize:10,color:C.gold,marginTop:3,paddingTop:3,borderTop:`1px dashed ${C.border}` }}>
                      📅 {s.shiftType==="regular"?"正社員":s.shiftType==="part"?"パート":"固定"} / MAX{s.shiftMaxDays}日
                      {s.shiftSlots && s.shiftSlots.length>0 && ` / スロット${s.shiftSlots.join("")}`}
                      {s.shiftFixedDays && s.shiftFixedDays.length>0 && ` / 固定曜日: ${s.shiftFixedDays.map(d=>["日","月","火","水","木","金","土"][d]).join("・")}`}
                      {s.shiftNote && ` / ${s.shiftNote}`}
                    </div>
                  )}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={()=>{ setEditing(s.id); setForm(s); }} style={{ background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 12px",color:C.gold,fontSize:11,cursor:"pointer" }}>編集</button>
                  <button onClick={()=>remove(s.id)} style={{ background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 10px",color:C.red,fontSize:11,cursor:"pointer" }}>削除</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAYROLL
// ═══════════════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════════════
// SHIFT MANAGER（オーナー用シフト自動生成）
// ═══════════════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════════════
// SHIFT MANAGER（オーナー用：シフト自動生成・編集・確定）
// ═══════════════════════════════════════════════════════════════════════════
const HOLIDAYS_2026 = {
  "2026-01-01":"元日","2026-01-12":"成人の日","2026-02-11":"建国記念日","2026-02-23":"天皇誕生日",
  "2026-03-20":"春分の日","2026-04-29":"昭和の日","2026-05-03":"憲法記念日","2026-05-04":"みどりの日",
  "2026-05-05":"こどもの日","2026-05-06":"振替休日","2026-07-20":"海の日","2026-08-11":"山の日",
  "2026-09-21":"敬老の日","2026-09-22":"国民の休日","2026-09-23":"秋分の日","2026-10-12":"体育の日",
  "2026-11-03":"文化の日","2026-11-23":"勤労感謝の日",
};
const SHIFT_COLORS = ["#B89878","#8AA898","#7A9BC4","#B090B0","#C0967A","#6E9AA4"];

// ═══════════════════════════════════════════════════════════════════════════
// シフトコメントBOX（オーナー・スタッフ共通：月別×店舗別のプチメモ）
// ═══════════════════════════════════════════════════════════════════════════
function ShiftCommentBox({ ym, storeId, authorName, role, shiftComments, setShiftComments, colors }) {
  const cc = colors;
  const key = `${ym}_${storeId}`;
  const comments = shiftComments[key] || [];
  const [text, setText] = useState("");

  const post = () => {
    if (!text.trim()) return;
    const newComment = {
      id: `c_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      author: authorName,
      role: role,
      message: text.trim(),
      at: new Date().toLocaleString("ja-JP", { month:"numeric", day:"numeric", hour:"2-digit", minute:"2-digit" }),
    };
    setShiftComments(prev => ({ ...prev, [key]: [...(prev[key]||[]), newComment] }));
    setText("");
  };

  const remove = (id) => {
    if (!window.confirm("このコメントを削除しますか？")) return;
    setShiftComments(prev => ({ ...prev, [key]: (prev[key]||[]).filter(c => c.id !== id) }));
  };

  return (
    <div style={{
      background: cc.bg2, border:`1px solid ${cc.border}`, borderRadius:12, padding:14, marginTop:14
    }}>
      <div style={{ fontSize:12, fontWeight:600, color:cc.gold, marginBottom:8 }}>
        💬 シフトメモ・連絡欄（{ym.replace("-","年")}月）
      </div>
      <div style={{ fontSize:10, color:cc.muted2, marginBottom:10 }}>
        修正依頼・質問など、自由に書き込めます。オーナーとスタッフで共有されます。
      </div>

      {/* コメント一覧 */}
      <div style={{ marginBottom:10 }}>
        {comments.length === 0 ? (
          <div style={{ fontSize:11, color:cc.muted2, textAlign:"center", padding:"12px 0" }}>
            まだコメントはありません
          </div>
        ) : comments.map(c => (
          <div key={c.id} style={{
            padding:"8px 10px", marginBottom:6, borderRadius:8,
            background: c.role === "owner" ? `${cc.gold}15` : cc.bg1,
            border: `1px solid ${c.role === "owner" ? `${cc.gold}44` : cc.border}`,
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
              <div style={{ fontSize:11, fontWeight:600, color: c.role === "owner" ? cc.gold : cc.cream }}>
                {c.role === "owner" ? "👑 " : ""}{c.author}
                <span style={{ fontSize:9, color:cc.muted2, fontWeight:400, marginLeft:6 }}>{c.at}</span>
              </div>
              {c.author === authorName && (
                <button onClick={()=>remove(c.id)} style={{
                  background:"none", border:"none", color:cc.muted2, fontSize:11, cursor:"pointer", padding:"0 4px"
                }}>🗑</button>
              )}
            </div>
            <div style={{ fontSize:12, color:cc.cream, lineHeight:1.5, whiteSpace:"pre-wrap" }}>
              {c.message}
            </div>
          </div>
        ))}
      </div>

      {/* 入力欄 */}
      <div style={{ display:"flex", gap:6 }}>
        <textarea
          value={text}
          onChange={e=>setText(e.target.value)}
          placeholder="例：6/15 早番ありがとう、人不足の日があれば教えて"
          style={{
            flex:1, background:cc.bg1, border:`1px solid ${cc.border2}`, borderRadius:8,
            padding:"8px 10px", color:cc.cream, fontSize:12, minHeight:48, resize:"vertical",
            fontFamily:"inherit"
          }}
        />
        <button onClick={post} disabled={!text.trim()} style={{
          background: text.trim() ? cc.gold : cc.bg3, color:"#fff", border:"none", borderRadius:8,
          padding:"0 14px", fontSize:12, fontWeight:600, cursor: text.trim() ? "pointer" : "default",
          alignSelf:"stretch"
        }}>
          送信
        </button>
      </div>
    </div>
  );
}

function ShiftManager({ staff, stores, shiftRequests, confirmedShifts, setConfirmedShifts, notices, setNotices, shiftComments, setShiftComments }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`;
  });
  const [activeStore, setActiveStore] = useState(stores[0]?.id||"s1");
  const [shiftData, setShiftData] = useState({});
  const [generated, setGenerated] = useState(false);
  const [notified, setNotified] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMsg, setSaveMsg] = useState("");
  const [editDate, setEditDate] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const [year, month] = viewMonth.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();

  // 下書き保存キー
  const draftKey = `shiftDraft_${viewMonth}_${activeStore}`;

  // 確定済みシフトがあれば自動的に表示する（確定済み優先、なければ下書き）
  useEffect(() => {
    const existing = (confirmedShifts[viewMonth] || {})[activeStore];
    let draft = null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) draft = JSON.parse(raw);
    } catch(e) { draft = null; }

    if (existing && Object.keys(existing).length > 0) {
      // 確定済みを優先表示
      setShiftData(prev => ({ ...prev, [activeStore]: existing }));
      setGenerated(true);
      setNotified(true);
      // 確定済みがあるのに古い下書きが残っていたら削除
      if (draft && JSON.stringify(draft) !== JSON.stringify(existing)) {
        try { localStorage.removeItem(draftKey); } catch(e) {}
      }
    } else if (draft && Object.keys(draft).length > 0) {
      // 確定済みがない場合のみ下書き表示
      setShiftData(prev => ({ ...prev, [activeStore]: draft }));
      setGenerated(true);
      setNotified(false);
    } else {
      setShiftData(prev => { const n={...prev}; delete n[activeStore]; return n; });
      setGenerated(false);
      setNotified(false);
    }
    setEditDate(null);
    setWarnings([]);
  }, [viewMonth, activeStore, confirmedShifts]);

  // shiftDataが変更されたら下書きを localStorage に自動保存
  useEffect(() => {
    const data = shiftData[activeStore];
    if (data && Object.keys(data).length > 0) {
      const isConfirmed = (confirmedShifts[viewMonth] || {})[activeStore];
      // 確定済みと内容が同じ場合は下書きを削除
      if (isConfirmed && JSON.stringify(isConfirmed) === JSON.stringify(data)) {
        try { localStorage.removeItem(draftKey); } catch(e) {}
      } else {
        try { localStorage.setItem(draftKey, JSON.stringify(data)); } catch(e) {}
      }
    }
  }, [shiftData, activeStore, viewMonth, draftKey, confirmedShifts]);

  const ds = (d) => `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const dow = (d) => new Date(year, month-1, d).getDay();
  const isHoliday = (dsStr) => !!HOLIDAYS_2026[dsStr];

  const activeStoreObj = stores.find(s=>s.id===activeStore);
  const storeStaffList = staff.filter(s=>(s.storeIds||[s.storeId]).includes(activeStore));

  const getOffDays = (staffId) => {
    const req = shiftRequests.find(r=>r.staffId===staffId && r.month===viewMonth);
    return req ? req.dates : [];
  };

  const canWorkFixed = (s, d) => {
    if (!s.shiftFixedDays || s.shiftFixedDays.length===0) return true;
    return s.shiftFixedDays.includes(d);
  };

  const pickSlot = (s, day, isWeekendOrHoliday, isOpener) => {
    if (!s.shiftSlots || s.shiftSlots.length===0) return "";
    if (activeStore==="s4") {
      if (isWeekendOrHoliday) return "③";
      return isOpener ? "①" : "②";
    }
    if (activeStore==="s1") return isOpener ? "①" : "②";
    if (activeStore==="s3") return isWeekendOrHoliday ? "②" : "①";
    return s.shiftSlots[0];
  };

  const generate = () => {
    const rule = activeStoreObj;
    if (!rule) return;
    const consec = {}, workedDays = {};
    storeStaffList.forEach(s => { consec[s.id]=0; workedDays[s.id]=0; });
    const result = {};
    const warns = [];

    // 🔍 診断情報：店舗設定の人数を冒頭で表示
    const wdMinSet = rule.shiftWeekdayMin ?? 1;
    const wdMaxSet = rule.shiftWeekdayMax ?? 2;
    const weMinSet = rule.shiftWeekendMin ?? 2;
    const weMaxSet = rule.shiftWeekendMax ?? 2;

    // 需給バランスチェック
    let totalOp = 0;
    let weekendOp = 0;
    for (let d=1; d<=daysInMonth; d++) {
      const dsStr = ds(d);
      const day = dow(d);
      if ((rule.shiftClosedDays||[]).includes(day)) continue;
      if ((rule.irregularClosedDates||[]).includes(dsStr)) continue;
      totalOp++;
      if (day===0||day===6||isHoliday(dsStr)) weekendOp++;
    }
    const weekdayOp = totalOp - weekendOp;
    const totalDemand = storeStaffList.filter(s=>!s.isHelp).reduce((sum,s)=>sum+(s.shiftMaxDays||21), 0);

    // 🎯 動的最大人数：需要を満たすために必要なら自動拡張
    // 必要平均人数 = 需要 / 営業日（端数切上げ）
    const dynamicMaxNeeded = Math.ceil(totalDemand / Math.max(totalOp, 1));
    const wdMax = Math.max(wdMaxSet, dynamicMaxNeeded);
    const weMax = Math.max(weMaxSet, dynamicMaxNeeded);
    const wdMin = wdMinSet;
    const weMin = weMinSet;

    if (wdMax > wdMaxSet || weMax > weMaxSet) {
      warns.push(`🔍 ${rule.name}：平日 ${wdMin}〜${wdMax}名 / 週末 ${weMin}〜${weMax}名 ✨自動拡張済`);
      warns.push(`💡 設定は平日max${wdMaxSet}・週末max${weMaxSet}でしたが、需要(${totalDemand}日)を満たすため自動拡張しました`);
    } else {
      warns.push(`🔍 ${rule.name}：平日 ${wdMin}〜${wdMax}名 / 週末 ${weMin}〜${weMax}名`);
    }
    const totalSlotsMax = weekdayOp * wdMax + weekendOp * weMax;
    warns.push(`📊 月間スロット数：${totalSlotsMax}（最大）／ スタッフ希望勤務日数の合計：${totalDemand}日`);
    if (totalDemand > totalSlotsMax) {
      warns.push(`⚠️ 需要 > 供給：${totalDemand - totalSlotsMax}日の枠不足。スタッフのMAX日数を下げてください`);
    }
    let opSoFar = 0;

    for (let d=1; d<=daysInMonth; d++) {
      const dsStr = ds(d);
      const day = dow(d);
      if ((rule.shiftClosedDays||[]).includes(day) || (rule.irregularClosedDates||[]).includes(dsStr)) {
        result[dsStr] = [];
        storeStaffList.forEach(s => consec[s.id]=0);
        continue;
      }
      opSoFar++;
      const isWknd = day===0 || day===6 || isHoliday(dsStr);
      const offToday = storeStaffList.filter(s=>getOffDays(s.id).includes(dsStr)).map(s=>s.id);
      const mx = isWknd ? weMax : wdMax;
      const req = isWknd ? weMin : wdMin;

      const avail = storeStaffList.filter(s => {
        if (s.isHelp) return false;
        if (offToday.includes(s.id)) return false;
        if (!canWorkFixed(s, day)) return false;
        if (workedDays[s.id] >= (s.shiftMaxDays||21)) return false;
        return true;
      });

      const fixed = avail.filter(s => s.shiftFixedDays && s.shiftFixedDays.length > 0);
      const flex = avail.filter(s => !s.shiftFixedDays || s.shiftFixedDays.length === 0);

      // 🎯 ペースベース配分：月全体で均等に配分
      const flexWithPace = flex.map(s => {
        const sMax = s.shiftMaxDays || 21;
        const expectedByNow = (sMax / totalOp) * opSoFar;
        const deficit = expectedByNow - workedDays[s.id];
        const isAhead = workedDays[s.id] > expectedByNow + 0.5;
        return { staff: s, deficit, isAhead, expectedByNow };
      });

      const sortedByDeficit = [...flexWithPace].sort((a, b) => {
        if (Math.abs(a.deficit - b.deficit) > 0.01) return b.deficit - a.deficit;
        return consec[a.staff.id] - consec[b.staff.id];
      });

      let workers = [...fixed];
      // Phase 1: 必要最低人数まで埋める（遅れている人優先）
      for (const { staff: s } of sortedByDeficit) {
        if (workers.length >= req) break;
        if (workers.find(w=>w.id===s.id)) continue;
        workers.push(s);
      }

      // Phase 2: 最大人数まで埋める（ペース超過のスタッフはスキップ）
      for (const { staff: s, isAhead } of sortedByDeficit) {
        if (workers.length >= mx) break;
        if (workers.find(w => w.id === s.id)) continue;
        if (isAhead) continue;
        workers.push(s);
      }

      // Phase 3: それでも最低人数に届かない場合、ペース超過でも追加
      if (workers.length < req) {
        for (const { staff: s } of sortedByDeficit) {
          if (workers.length >= req) break;
          if (!workers.find(w => w.id === s.id)) workers.push(s);
        }
      }

      if (workers.length < req) {
        warns.push(`${month}/${d} ${rule.name}：人数不足（${workers.length}/${req}名）`);
      }

      const assigned = workers.map((s,i) => ({
        id: s.id, name: s.name,
        slot: pickSlot(s, day, isWknd, i===0),
      }));

      storeStaffList.forEach(s => {
        if (workers.find(w=>w.id===s.id)) { workedDays[s.id]++; consec[s.id]++; }
        else consec[s.id] = 0;
      });
      result[dsStr] = assigned;
    }

    // 勤務日数の達成状況をチェック
    storeStaffList.forEach(s => {
      const target = s.shiftMaxDays || 21;
      const actual = workedDays[s.id];
      if (actual < target) {
        const shortage = target - actual;
        warns.push(`📊 ${s.name}：勤務日数 ${actual}/${target}日（${shortage}日不足）`);
      }
    });

    setShiftData(prev => ({...prev, [activeStore]: result}));
    setGenerated(true);
    setNotified(false);
    setWarnings(warns);
  };

  const confirmShift = async () => {
    // 🔧 すべての営業日を含む完全なシフトデータを構築
    const currentShift = shiftData[activeStore] || {};
    const fullShifts = {};
    for (let d = 1; d <= daysInMonth; d++) {
      const dsStr = ds(d);
      const day = dow(d);
      // 定休日はスキップ
      if ((activeStoreObj?.shiftClosedDays||[]).includes(day)) continue;
      if ((activeStoreObj?.irregularClosedDates||[]).includes(dsStr)) continue;
      // workersは存在すればそれ、なければ空配列
      fullShifts[dsStr] = currentShift[dsStr] || [];
    }

    // ローカル即時更新
    setConfirmedShifts(prev => ({
      ...prev,
      [viewMonth]: {
        ...(prev[viewMonth] || {}),
        [activeStore]: fullShifts,
      }
    }));
    setNotices(prev => [{
      id: `n${Date.now()}`,
      title: `📅 ${year}年${month}月シフト確定のお知らせ（${activeStoreObj?.name}）`,
      url: "#",
      date: new Date().toISOString(),
      important: true,
      targetStores: [activeStore],
      content: `${year}年${month}月のシフトが確定しました。アプリの「📅 今月のシフト」タブから確認してください。\n更新日時: ${new Date().toLocaleString("ja-JP")}`,
    }, ...prev]);

    // スプシに保存（リトライ＋ステータスモーダル＋照合）
    setSaveStatus("saving");
    setSaveMsg(`${activeStoreObj?.name||""} ${year}年${month}月`);
    setNotified(true);
    const expectedCount = Object.values(fullShifts).reduce((a,w)=>a+w.length,0);

    try {
      const result = await gasPostWithRetry("saveConfirmedShifts", {
        yearMonth: viewMonth,
        storeId: activeStore,
        storeName: activeStoreObj?.name || "",
        shifts: fullShifts,
      });

      if (!result?.success) {
        setSaveStatus("error");
        setSaveMsg(`${result?.error || "通信エラー"}\n\nもう一度お試しください。`);
        setNotified(false);
        return;
      }

      // 確定したので下書きを削除
      try { localStorage.removeItem(draftKey); } catch(e) {}

      // 🌸 保存後にスプシから再取得して画面に確実反映＋件数照合
      let actualCount = 0;
      let verified = false;
      try {
        const fresh = await gasLoad();
        const freshData = fresh?.data || fresh;
        if (freshData?.confirmedShifts) {
          const shifts = {};
          const normYM = (v) => {
            if (!v) return "";
            if (v instanceof Date) {
              const j = new Date(v.getTime() + 9*60*60*1000);
              return `${j.getUTCFullYear()}-${String(j.getUTCMonth()+1).padStart(2,"0")}`;
            }
            const s = String(v).trim();
            const m = s.match(/(\d{4})[-/](\d{1,2})/);
            return m ? `${m[1]}-${String(m[2]).padStart(2,"0")}` : s;
          };
          const normDate = (v) => {
            if (!v) return "";
            if (v instanceof Date) {
              const j = new Date(v.getTime() + 9*60*60*1000);
              return `${j.getUTCFullYear()}-${String(j.getUTCMonth()+1).padStart(2,"0")}-${String(j.getUTCDate()).padStart(2,"0")}`;
            }
            const s = String(v).trim();
            const m = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
            return m ? `${m[1]}-${String(m[2]).padStart(2,"0")}-${String(m[3]).padStart(2,"0")}` : (s.includes("T") ? s.split("T")[0] : s);
          };
          freshData.confirmedShifts.forEach(row => {
            const ym = normYM(row["年月"]);
            const sid = String(row["店舗ID"]||"").trim();
            const ds2 = normDate(row["日付"]);
            const staffId = String(row["スタッフID"]||"").trim();
            if (!ym || !sid || !ds2 || !staffId) return;
            // 自分の店舗・月の件数をカウント
            if (ym === viewMonth && sid === activeStore) actualCount++;
            if (!shifts[ym]) shifts[ym] = {};
            if (!shifts[ym][sid]) shifts[ym][sid] = {};
            if (!shifts[ym][sid][ds2]) shifts[ym][sid][ds2] = [];
            if (shifts[ym][sid][ds2].some(w => String(w.id||"").trim() === staffId)) return;
            shifts[ym][sid][ds2].push({
              id: staffId,
              name: String(row["スタッフ名"]||"").trim(),
              slot: String(row["スロット"]||"").trim()
            });
          });
          setConfirmedShifts(shifts);
          verified = (actualCount === expectedCount);
        }
      } catch(e) { console.error("re-fetch error:", e); }

      // ステータスモーダル表示
      setSaveStatus("success");
      if (verified) {
        setSaveMsg(`${activeStoreObj?.name||""} ${year}年${month}月\n✓ スプシ確認済 ${actualCount}件\n旧データを削除して上書きしました`);
      } else {
        setSaveMsg(`${activeStoreObj?.name||""} ${year}年${month}月\n保存件数: ${expectedCount}件\nスプシ件数: ${actualCount}件${actualCount!==expectedCount?"\n⚠️ 件数不一致 - 再確認してください":""}`);
      }
      setTimeout(()=>setSaveStatus(null), 2500);

    } catch(e) {
      setSaveStatus("error");
      setSaveMsg(`通信エラー：${e.message}\n\nネット接続を確認してもう一度お試しください。`);
      setNotified(false);
    }
  };

  const toggleWorker = (dsStr, staffId) => {
    setShiftData(prev => {
      const storeShift = {...(prev[activeStore]||{})};
      const workers = [...(storeShift[dsStr]||[])];
      const idx = workers.findIndex(w=>w.id===staffId);
      if (idx >= 0) workers.splice(idx, 1);
      else {
        const s = storeStaffList.find(x=>x.id===staffId);
        if (!s) return prev;
        const d = parseInt(dsStr.split("-")[2]);
        const day = dow(d);
        const isWknd = day===0 || day===6 || isHoliday(dsStr);
        workers.push({ id:s.id, name:s.name, slot: pickSlot(s, day, isWknd, workers.length===0) });
      }
      storeShift[dsStr] = workers;
      return {...prev, [activeStore]: storeShift};
    });
  };

  const changeSlot = (dsStr, staffId, slot) => {
    setShiftData(prev => {
      const storeShift = {...(prev[activeStore]||{})};
      const workers = [...(storeShift[dsStr]||[])];
      const w = workers.find(x=>x.id===staffId);
      if (w) w.slot = slot;
      storeShift[dsStr] = workers;
      return {...prev, [activeStore]: storeShift};
    });
  };

  const shift = shiftData[activeStore] || {};
  const firstDow0 = new Date(year, month-1, 1).getDay();
  const firstDowMon = firstDow0===0 ? 6 : firstDow0-1;
  const DAYS = ["月","火","水","木","金","土","日"];

  const storeColors = {};
  storeStaffList.forEach((s,i) => { storeColors[s.id] = SHIFT_COLORS[i%SHIFT_COLORS.length]; });

  const workCount = {};
  storeStaffList.forEach(s => {
    workCount[s.id] = Object.values(shift).filter(w => w.find(x=>x.id===s.id)).length;
  });

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <div style={card({marginBottom:10})}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ fontSize:14, color:C.gold, fontWeight:600 }}>📅 シフト管理</div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={()=>{
              const d = new Date(year, month-2, 1);
              setViewMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
            }} style={{ background:"none",border:`1px solid ${C.border2}`,borderRadius:6,padding:"4px 9px",color:C.muted,cursor:"pointer",fontSize:13 }}>‹</button>
            <span style={{ fontSize:13, color:C.cream, fontWeight:500, minWidth:90, textAlign:"center" }}>{year}年{month}月</span>
            <button onClick={()=>{
              const d = new Date(year, month, 1);
              setViewMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
            }} style={{ background:"none",border:`1px solid ${C.border2}`,borderRadius:6,padding:"4px 9px",color:C.muted,cursor:"pointer",fontSize:13 }}>›</button>
          </div>
        </div>

        <div style={{ background:`linear-gradient(to right, ${C.gold}, ${C.gold2})`, color:"#fff", padding:"8px 14px", borderRadius:8, fontSize:13, fontWeight:600, textAlign:"center", marginBottom:10 }}>
          📍 現在編集中：{activeStoreObj?.name}
        </div>

        <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
          {stores.map(s => (
            <button key={s.id} onClick={()=>{ setActiveStore(s.id); setEditDate(null); }} style={{
              padding:"5px 12px", borderRadius:6, border:`1px solid ${activeStore===s.id?C.gold:C.border2}`,
              fontSize:11, cursor:"pointer",
              background: activeStore===s.id ? C.gold : C.bg2,
              color: activeStore===s.id ? "#fff" : C.muted,
              fontWeight: activeStore===s.id ? 600 : 400,
            }}>
              {activeStore===s.id ? "✓ " : ""}{s.name}
            </button>
          ))}
        </div>

        <button onClick={generate} style={{ width:"100%", padding:11, borderRadius:8, border:"none", background:C.gold, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:6 }}>
          ⚡ 自動生成
        </button>
        <div style={{ fontSize:10, color:C.muted2, textAlign:"center", marginBottom:6, padding:"4px 8px" }}>
          💡 自動生成のあと、日付タップで個別修正できます
        </div>
        {(() => {
          // 自動生成済み or 手入力でデータが入っていれば確定ボタン表示
          const hasData = generated || Object.values(shift).some(arr => Array.isArray(arr) && arr.length > 0);
          // 既に確定済みのシフトがあるかチェック
          const isAlreadyConfirmed = !!(confirmedShifts[viewMonth]||{})[activeStore];
          // 上書き保存のラッパー
          const handleConfirm = () => {
            if (isAlreadyConfirmed) {
              if (!window.confirm(`${year}年${month}月の${activeStoreObj?.name}のシフトを上書き保存します。\nスタッフに再通知されますがよろしいですか？`)) return;
            }
            confirmShift();
          };
          return hasData && (
            <button onClick={handleConfirm} style={{
              width:"100%", padding:10, borderRadius:8, border:"none",
              background: isAlreadyConfirmed ? C.gold : C.green,
              color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer"
            }}>
              {notified ? "✓ 通知しました（再保存可能）" : isAlreadyConfirmed ? "📝 修正を上書き保存して再通知" : "確定してスタッフに通知"}
            </button>
          );
        })()}
        {(() => {
          // 下書きが保存されているかチェック
          let hasDraft = false;
          try { hasDraft = !!localStorage.getItem(draftKey); } catch(e){}
          return hasDraft && !notified && (
            <div style={{ fontSize:10, color:C.muted2, textAlign:"center", marginTop:8, padding:"6px 10px", background:`${C.gold}11`, borderRadius:6 }}>
              💾 編集内容は自動保存されています（リロードしても復元されます）
            </div>
          );
        })()}
      </div>

      {warnings.length > 0 && (
        <div style={card({marginBottom:10, background:"#fff8f0", border:`1px solid ${C.accent}55`})}>
          <div style={{ fontSize:12, color:C.accent, fontWeight:600, marginBottom:6 }}>⚠ 診断情報・警告（{warnings.length}件）</div>
          {warnings.map((w,i) => <div key={i} style={{ fontSize:11, color:C.accent, marginBottom:3, lineHeight:1.4 }}>{w}</div>)}
        </div>
      )}

      {generated && (
        <div style={card({marginBottom:10, background:C.bg2})}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>{activeStoreObj?.name} 出勤状況</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {storeStaffList.filter(s=>!s.isHelp).map(s => {
              const ok = workCount[s.id] >= (s.shiftMaxDays||21);
              return (
                <div key={s.id} style={{ display:"flex", alignItems:"center", gap:4, background:C.bg1, borderRadius:6, padding:"3px 8px" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:storeColors[s.id] }}/>
                  <span style={{ fontSize:11, color:C.cream }}>{s.name}</span>
                  <span style={{ fontSize:10, color:ok?C.green:C.muted, fontWeight:ok?600:400 }}>{workCount[s.id]}/{s.shiftMaxDays||21}{ok?" ✓":""}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={card({padding:10})}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:3 }}>
          {DAYS.map((d,i) => (
            <div key={d} style={{ textAlign:"center", fontSize:11, fontWeight:500, padding:"3px 0", color: i>=5?"#c75a5a":C.muted }}>{d}</div>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
          {Array.from({length:firstDowMon}).map((_,i) => <div key={`e${i}`}/>)}
          {Array.from({length:daysInMonth}).map((_,i) => {
            const d = i+1;
            const dsStr = ds(d);
            const day = dow(d);
            const isClosed = (activeStoreObj?.shiftClosedDays||[]).includes(day) || (activeStoreObj?.irregularClosedDates||[]).includes(dsStr);
            const isHol = isHoliday(dsStr);
            const isRed = day===0||day===6||isHol;
            const rawWorkers = shift[dsStr] || [];
            // 🔧 厳格な重複除去 + 当該店舗スタッフのみフィルタ（文字列比較）
            const seenIds = new Set();
            const workers = rawWorkers.filter(w => {
              if (!w) return false;
              const wid = String(w.id||"").trim();
              if (!wid) return false;
              if (seenIds.has(wid)) return false;
              seenIds.add(wid);
              return storeStaffList.some(s => String(s.id).trim() === wid);
            });
            const offToday = storeStaffList.filter(s=>getOffDays(s.id).includes(dsStr));
            const req = isRed ? (activeStoreObj?.shiftWeekendMin||2) : (activeStoreObj?.shiftWeekdayMin||1);
            const isShort = !isClosed && workers.length > 0 && workers.length < req;

            let bg = C.bg1;
            if (isClosed) bg = C.bg3;
            else if (isShort) bg = "#fbeeee";
            else if (isRed) bg = "#fef5f5";

            return (
              <div key={d} onClick={()=>!isClosed && setEditDate(dsStr)} style={{
                minHeight:72, borderRadius:6, padding:"3px 2px",
                background: bg,
                border: editDate===dsStr ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
                cursor: isClosed ? "default" : "pointer",
              }}>
                <div style={{ fontSize:11, fontWeight:500, textAlign:"center",
                  color: isClosed ? C.muted2 : isRed ? "#c75a5a" : C.cream }}>
                  {d}{isClosed && <span style={{fontSize:9}}> 休</span>}{isHol && <span style={{fontSize:9}}> 祝</span>}
                </div>
                {!isClosed && workers.map(w => (
                  <div key={w.id} style={{
                    fontSize:10, borderRadius:3, padding:"1px 2px", textAlign:"center", marginTop:1, fontWeight:500, lineHeight:1.3,
                    background: `${storeColors[w.id]}33`, color: storeColors[w.id],
                  }}>
                    {w.name.split(" ")[0]}{w.slot ? ` ${w.slot}` : ""}
                  </div>
                ))}
                {!isClosed && offToday.length > 0 && (
                  <div style={{ fontSize:9, color:"#c75a5a", textAlign:"center", marginTop:1 }}>
                    希{offToday.map(s=>s.name[0]).join("")}
                  </div>
                )}
                {isShort && <div style={{ fontSize:9, color:"#e07878", textAlign:"center" }}>要確認</div>}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop:10, padding:10, background:C.bg2, borderRadius:8, border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:11, fontWeight:500, color:C.muted, marginBottom:5 }}>{activeStoreObj?.name} 勤務スケジュール</div>
          {String(activeStoreObj?.shiftSchedule||"").split("\n").filter(Boolean).map((s,i) => (
            <div key={i} style={{ fontSize:11, color:C.cream, marginBottom:2 }}>• {s}</div>
          ))}
        </div>

        {generated && (() => {
          const regulars = storeStaffList.filter(s => s.shiftType==="regular");
          if (regulars.length===0) return null;
          return (
            <div style={{ marginTop:8, paddingTop:8, borderTop:`1px dashed ${C.border2}` }}>
              <div style={{ fontSize:11, fontWeight:500, color:C.muted, marginBottom:5 }}>正社員の休日（{month}月）</div>
              {regulars.map(s => {
                const offDates = [];
                let closedCount = 0;
                for (let d=1; d<=daysInMonth; d++) {
                  const dsStr = ds(d);
                  if ((activeStoreObj?.shiftClosedDays||[]).includes(dow(d))) { closedCount++; continue; }
                  if ((activeStoreObj?.irregularClosedDates||[]).includes(dsStr)) { closedCount++; continue; }
                  const ws = shift[dsStr] || [];
                  if (!ws.find(w=>w.id===s.id)) offDates.push(d);
                }
                const workedCount = daysInMonth - offDates.length - closedCount;
                return (
                  <div key={s.id} style={{ display:"flex", gap:6, marginBottom:3, flexWrap:"wrap" }}>
                    <span style={{ fontSize:11, color:C.cream, fontWeight:500, minWidth:80 }}>{s.name}</span>
                    <span style={{ fontSize:10, color:C.muted }}>出勤{workedCount}日／休{offDates.length}日：{offDates.join("・")}</span>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* ⏰ 各スタッフの勤務スケジュール（時間表） */}
      <div style={card({marginTop:10, padding:14})}>
        <div style={{ fontSize:13, fontWeight:600, color:C.gold, marginBottom:10 }}>
          ⏰ 各スタッフの勤務スケジュール
        </div>
        <div style={{ fontSize:10, color:C.muted2, marginBottom:8 }}>
          各スタッフの①②③が何時か
        </div>
        {storeStaffList.map(s => (
          <div key={s.id} style={{
            padding:"8px 10px", marginBottom:5,
            background:C.bg2, borderRadius:8, border:`1px solid ${C.border}`,
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <div style={{ width:22, height:22, borderRadius:"50%", background:storeColors[s.id], display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:600, color:"#fff" }}>
                {s.name[0]}
              </div>
              <span style={{ fontSize:12, color:C.cream, fontWeight:500 }}>{s.name}</span>
              {s.shiftFixedDays && s.shiftFixedDays.length > 0 && (
                <span style={{ fontSize:9, color:"#a8c8b8" }}>
                  📌{s.shiftFixedDays.map(d => ["日","月","火","水","木","金","土"][d]).join("・")}
                </span>
              )}
              {s.shiftNote && <span style={{ fontSize:9, color:C.muted2 }}>({s.shiftNote})</span>}
            </div>
            {s.slotTimes && Object.keys(s.slotTimes).length > 0 ? (
              <div style={{ marginLeft:30, display:"flex", flexWrap:"wrap", gap:6 }}>
                {Object.entries(s.slotTimes).map(([slot, time]) => (
                  <span key={slot} style={{
                    fontSize:10, color:C.cream, background:`${C.gold}22`,
                    padding:"3px 8px", borderRadius:6, border:`1px solid ${C.gold}44`
                  }}>
                    <strong style={{ color:C.gold }}>{slot}</strong> {time}
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ marginLeft:30, fontSize:10, color:C.muted2 }}>
                時間情報なし（スタッフ管理で設定可能）
              </div>
            )}
          </div>
        ))}
      </div>

      {editDate && (() => {
        const d = parseInt(editDate.split("-")[2]);
        const day = dow(d);
        if ((activeStoreObj?.shiftClosedDays||[]).includes(day)) return null;
        if ((activeStoreObj?.irregularClosedDates||[]).includes(editDate)) return null;
        const rawWorkersM = shift[editDate] || [];
        const seenIdsM = new Set();
        const workers = rawWorkersM.filter(w => {
          if (!w) return false;
          const wid = String(w.id||"").trim();
          if (!wid) return false;
          if (seenIdsM.has(wid)) return false;
          seenIdsM.add(wid);
          return storeStaffList.some(s => String(s.id).trim() === wid);
        });
        const offToday = storeStaffList.filter(s=>getOffDays(s.id).includes(editDate));
        const DAY_NAMES = ["日","月","火","水","木","金","土"];
        const isHol = isHoliday(editDate);
        return (
          <div onClick={()=>setEditDate(null)} style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:300,
            display:"flex", alignItems:"center", justifyContent:"center", padding:16
          }}>
            <div onClick={(e)=>e.stopPropagation()} style={{
              background:C.bg1, borderRadius:14, padding:16, width:"100%", maxWidth:380,
              maxHeight:"85vh", overflowY:"auto", border:`1px solid ${C.border2}`,
              boxShadow:"0 8px 32px rgba(0,0,0,0.4)"
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{ fontSize:14, fontWeight:600, color:C.gold }}>
                  📅 {month}月{d}日（{DAY_NAMES[day]}{isHol?"・祝":""}）
                </div>
                <button onClick={()=>setEditDate(null)} style={{
                  background:"none", border:"none", color:C.muted, fontSize:18, cursor:"pointer", padding:"0 6px"
                }}>✕</button>
              </div>
              <div style={{ fontSize:11, color:C.muted2, marginBottom:10 }}>
                スタッフをタップして出勤/休みを切替
              </div>

              {/* スロット時間の凡例 */}
              {activeStoreObj?.shiftSchedule && (
                <div style={{
                  background:`${C.gold}11`, border:`1px solid ${C.gold}33`, borderRadius:8,
                  padding:"8px 10px", marginBottom:10
                }}>
                  <div style={{ fontSize:10, color:C.gold, fontWeight:600, marginBottom:4 }}>
                    ⏰ スロット時間の凡例
                  </div>
                  {String(activeStoreObj.shiftSchedule).split("\n").filter(Boolean).map((line,i) => (
                    <div key={i} style={{ fontSize:11, color:C.cream, lineHeight:1.5 }}>{line}</div>
                  ))}
                </div>
              )}
              {storeStaffList.map(s => {
                const w = workers.find(x=>x.id===s.id);
                const isWork = !!w;
                const isOff = offToday.find(x=>x.id===s.id);
                const hasSlots = s.shiftSlots && s.shiftSlots.length > 0;
                return (
                  <div key={s.id} style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"10px 12px", borderRadius:8, marginBottom:6,
                    border:`1px solid ${isWork?C.gold:C.border}`,
                    background: isWork ? `${C.gold}22` : C.bg2,
                  }}>
                    <div onClick={()=>toggleWorker(editDate, s.id)} style={{ display:"flex", alignItems:"center", gap:8, flex:1, cursor:"pointer" }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", background:storeColors[s.id], display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:600, color:"#fff" }}>
                        {s.name[0]}
                      </div>
                      <span style={{ fontSize:13, color:C.cream }}>{s.name}</span>
                      {isOff && <span style={{ fontSize:10, color:"#c75a5a" }}>希望休</span>}
                      {s.shiftNote && <span style={{ fontSize:9, color:C.muted2 }}>({s.shiftNote})</span>}
                    </div>
                    {isWork && hasSlots ? (
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
                        <div style={{ display:"flex", gap:4 }}>
                          {s.shiftSlots.map(sl => (
                            <button key={sl} onClick={(e)=>{e.stopPropagation(); changeSlot(editDate, s.id, sl);}} style={{
                              width:28, height:28, borderRadius:6,
                              border:`1px solid ${w.slot===sl?C.gold:C.border2}`,
                              background: w.slot===sl ? C.gold : C.bg1,
                              color: w.slot===sl ? "#fff" : C.muted,
                              fontSize:12, fontWeight:600, cursor:"pointer",
                            }}>{sl}</button>
                          ))}
                        </div>
                        {w.slot && s.slotTimes && s.slotTimes[w.slot] && (
                          <span style={{ fontSize:9, color:C.gold, fontWeight:500 }}>
                            {s.slotTimes[w.slot]}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize:12, color: isWork?C.gold:C.muted, fontWeight: isWork?600:400 }}>
                        {isWork ? "出勤" : "休み"}
                      </span>
                    )}
                  </div>
                );
              })}
              <button onClick={()=>setEditDate(null)} style={{
                width:"100%", padding:11, borderRadius:8, border:"none", background:C.gold, color:"#fff",
                fontSize:13, fontWeight:600, cursor:"pointer", marginTop:8
              }}>完了</button>
            </div>
          </div>
        );
      })()}

      {/* シフトメモ・連絡欄 */}
      <ShiftCommentBox
        ym={viewMonth}
        storeId={activeStore}
        authorName="オーナー"
        role="owner"
        shiftComments={shiftComments}
        setShiftComments={setShiftComments}
        colors={C}
      />
      <SaveStatusModal status={saveStatus} message={saveMsg} onClose={()=>setSaveStatus(null)}/>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MY SHIFT（スタッフ用：確定シフト表示）
// ═══════════════════════════════════════════════════════════════════════════
function MyShift({ staff, stores, confirmedShifts, activeStaffId, shiftComments, setShiftComments }) {
  const cc = useC("staff");
  const now = new Date();
  const [viewMonth, setViewMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);

  const me = staff.find(s=>s.id===activeStaffId);
  // 🌸 複数店舗対応：兼任スタッフ用に店舗切替
  const myStoreIdsList = me ? (me.storeIds || [me.storeId]).filter(Boolean) : [];
  const [activeStoreView, setActiveStoreView] = useState(myStoreIdsList[0] || me?.storeId);
  const myStoreId = activeStoreView || me?.storeId;
  const myStore = stores.find(s=>s.id===myStoreId);

  const [year, month] = viewMonth.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();

  const monthShift = (confirmedShifts[viewMonth] || {})[myStoreId] || {};
  const isConfirmed = Object.keys(monthShift).length > 0;

  const storeStaffList = staff.filter(s=>(s.storeIds||[s.storeId]).includes(myStoreId));
  const storeColors = {};
  storeStaffList.forEach((s,i) => { storeColors[s.id] = SHIFT_COLORS[i%SHIFT_COLORS.length]; });

  const ds = (d) => `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const dow = (d) => new Date(year, month-1, d).getDay();
  const isHoliday = (dsStr) => !!HOLIDAYS_2026[dsStr];

  const firstDow0 = new Date(year, month-1, 1).getDay();
  const firstDowMon = firstDow0===0 ? 6 : firstDow0-1;
  const DAYS = ["月","火","水","木","金","土","日"];

  let myDays = 0;
  for (let d=1; d<=daysInMonth; d++) {
    const ws = monthShift[ds(d)] || [];
    if (ws.find(w=>w.id===activeStaffId)) myDays++;
  }

  const workCount = {};
  storeStaffList.forEach(s => {
    workCount[s.id] = Object.values(monthShift).filter(w => w.find(x=>x.id===s.id)).length;
  });

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <button onClick={()=>{
          const d = new Date(year, month-2, 1);
          setViewMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
        }} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"4px 9px",color:cc.muted,cursor:"pointer",fontSize:13 }}>‹</button>
        <div style={{ fontSize:14, fontWeight:600, color:cc.gold }}>{year}年{month}月のシフト</div>
        <button onClick={()=>{
          const d = new Date(year, month, 1);
          setViewMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
        }} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"4px 9px",color:cc.muted,cursor:"pointer",fontSize:13 }}>›</button>
      </div>

      {/* 🌸 兼任スタッフ用：店舗切替タブ */}
      {myStoreIdsList.length > 1 && (
        <div style={{ display:"flex", gap:6, marginBottom:12, overflowX:"auto" }}>
          {myStoreIdsList.map(sid => {
            const st = stores.find(s=>s.id===sid);
            if (!st) return null;
            const active = activeStoreView === sid;
            return (
              <button key={sid} onClick={()=>setActiveStoreView(sid)} style={{
                flex:1, minWidth:80, padding:"8px 10px", borderRadius:8,
                border: active ? `1px solid ${cc.gold}` : `1px solid ${cc.border2}`,
                background: active ? `${cc.gold}22` : cc.bg2,
                color: active ? cc.gold : cc.muted,
                fontSize:12, fontWeight: active ? 600 : 400, cursor:"pointer",
              }}>
                {active ? "✓ " : ""}{st.name}
              </button>
            );
          })}
        </div>
      )}

      {!isConfirmed ? (
        <div style={{ ...cardS("staff"), textAlign:"center", padding:30 }}>
          <div style={{ fontSize:13, color:cc.muted, marginBottom:6 }}>🕐 シフト作成中</div>
          <div style={{ fontSize:11, color:cc.muted2 }}>まだ{month}月のシフトは確定していません。確定後にここに表示されます。</div>
        </div>
      ) : (
        <>
          <div style={{ background:`linear-gradient(to right, ${cc.gold}, ${cc.gold2})`, color:"#fff", padding:"10px 14px", borderRadius:10, fontSize:13, fontWeight:600, textAlign:"center", marginBottom:10 }}>
            📅 {me?.name}さんの{month}月シフト（確定）
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6, marginBottom:10 }}>
            <div style={{ background:cc.bg2, borderRadius:8, padding:8, textAlign:"center" }}>
              <div style={{ fontSize:18, fontWeight:600, color:cc.gold }}>{myDays}</div>
              <div style={{ fontSize:10, color:cc.muted, marginTop:2 }}>出勤日数</div>
            </div>
            <div style={{ background:cc.bg2, borderRadius:8, padding:8, textAlign:"center" }}>
              <div style={{ fontSize:18, fontWeight:600, color:cc.cream }}>{daysInMonth-myDays}</div>
              <div style={{ fontSize:10, color:cc.muted, marginTop:2 }}>休日</div>
            </div>
            <div style={{ background:cc.bg2, borderRadius:8, padding:8, textAlign:"center" }}>
              <div style={{ fontSize:13, fontWeight:600, color:cc.cream, paddingTop:3 }}>{myStore?.name}</div>
              <div style={{ fontSize:10, color:cc.muted, marginTop:2 }}>勤務店舗</div>
            </div>
          </div>

          <div style={cardS("staff")}>
            <div style={{ fontSize:12, color:cc.muted, marginBottom:6 }}>{myStore?.name}のシフト</div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:3 }}>
              {DAYS.map((d,i) => (
                <div key={d} style={{ textAlign:"center", fontSize:10, fontWeight:500, padding:"3px 0", color: i>=5?"#c75a5a":cc.muted }}>{d}</div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
              {Array.from({length:firstDowMon}).map((_,i) => <div key={`e${i}`}/>)}
              {Array.from({length:daysInMonth}).map((_,i) => {
                const d = i+1;
                const dsStr = ds(d);
                const day = dow(d);
                const isClosed = (myStore?.shiftClosedDays||[]).includes(day) || (myStore?.irregularClosedDates||[]).includes(dsStr);
                const isHol = isHoliday(dsStr);
                const isRed = day===0||day===6||isHol;
                const workers = monthShift[dsStr] || [];
                const isMine = workers.find(w=>w.id===activeStaffId);

                let bg = "#fff";
                if (isClosed) bg = cc.bg3;
                else if (isMine) bg = "#fff4df";
                else if (isRed) bg = "#fef5f5";

                return (
                  <div key={d} style={{
                    minHeight:68, borderRadius:6, padding:"3px 2px",
                    background: bg,
                    border: isMine ? `2px solid ${cc.gold}` : `1px solid ${cc.border}`,
                  }}>
                    <div style={{ fontSize:11, fontWeight:500, textAlign:"center",
                      color: isClosed ? cc.muted2 : isRed ? "#c75a5a" : cc.cream }}>
                      {d}{isClosed && <span style={{fontSize:9}}> 休</span>}{isHol && <span style={{fontSize:9}}> 祝</span>}
                    </div>
                    {workers.map(w => {
                      const isMeEntry = w.id===activeStaffId;
                      const c = storeColors[w.id];
                      return (
                        <div key={w.id} style={{
                          fontSize:9, borderRadius:3, padding:"1px 2px", textAlign:"center", marginTop:1, fontWeight:500, lineHeight:1.2,
                          background: `${c}33`, color: c,
                          border: isMeEntry ? `1px solid ${cc.gold}` : "none",
                        }}>
                          {w.name.split(" ")[0]}{w.slot ? ` ${w.slot}` : ""}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop:10, padding:10, background:cc.bg2, borderRadius:8, border:`1px solid ${cc.border}` }}>
              <div style={{ fontSize:11, fontWeight:500, color:cc.muted, marginBottom:5 }}>{myStore?.name} 勤務スケジュール</div>
              {String(myStore?.shiftSchedule||"").split("\n").filter(Boolean).map((s,i) => (
                <div key={i} style={{ fontSize:11, color:cc.cream, marginBottom:2 }}>• {s}</div>
              ))}
            </div>

            <div style={{ marginTop:8, paddingTop:8, borderTop:`1px dashed ${cc.border2}` }}>
              <div style={{ fontSize:11, color:cc.muted, marginBottom:5 }}>スタッフ別 出勤日数</div>
              {storeStaffList.map(s => (
                <div key={s.id} style={{ display:"flex", justifyContent:"space-between", padding:"3px 0", fontSize:11 }}>
                  <span style={{ color:storeColors[s.id], fontWeight:500 }}>{s.name}{s.id===activeStaffId?"（自分）":""}</span>
                  <span style={{ color:cc.muted }}>{workCount[s.id]}日</span>
                </div>
              ))}
            </div>

            <div style={{ display:"flex", gap:10, fontSize:10, color:cc.muted2, justifyContent:"center", marginTop:8, flexWrap:"wrap" }}>
              <span><span style={{ display:"inline-block", width:10, height:10, borderRadius:3, background:"#fff4df", border:`1px solid ${cc.gold}`, verticalAlign:"middle", marginRight:3 }}/>自分の出勤日</span>
              <span><span style={{ display:"inline-block", width:10, height:10, borderRadius:3, background:"#fef5f5", verticalAlign:"middle", marginRight:3 }}/>土日祝</span>
            </div>
          </div>

          {/* ⏰ 各スタッフの勤務スケジュール（コンパクト版） */}
          <div style={{ marginTop:10, padding:10, background:cc.bg2, border:`1px solid ${cc.border}`, borderRadius:10 }}>
            <div style={{ fontSize:12, fontWeight:600, color:cc.gold, marginBottom:6 }}>
              ⏰ 各スタッフの勤務スケジュール
            </div>
            {storeStaffList.map(s => (
              <div key={s.id} style={{
                padding:"5px 8px", marginBottom:3,
                background:cc.bg1, borderRadius:6, border:`1px solid ${cc.border}`,
                display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"
              }}>
                <div style={{ width:18, height:18, borderRadius:"50%", background:storeColors[s.id], display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:600, color:"#fff", flexShrink:0 }}>
                  {s.name[0]}
                </div>
                <span style={{ fontSize:11, color:cc.cream, fontWeight:500, minWidth:70 }}>{s.name}</span>
                {s.shiftFixedDays && s.shiftFixedDays.length > 0 && (
                  <span style={{ fontSize:9, color:"#a8c8b8" }}>
                    📌{s.shiftFixedDays.map(d => ["日","月","火","水","木","金","土"][d]).join("・")}
                  </span>
                )}
                {s.slotTimes && Object.keys(s.slotTimes).length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                    {Object.entries(s.slotTimes).map(([slot, time]) => (
                      <span key={slot} style={{
                        fontSize:9, color:cc.cream, background:`${cc.gold}22`,
                        padding:"1px 5px", borderRadius:4, border:`1px solid ${cc.gold}44`
                      }}>
                        <strong style={{ color:cc.gold }}>{slot}</strong> {time}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* シフトメモ・連絡欄 */}
      {myStoreId && (
        <ShiftCommentBox
          ym={viewMonth}
          storeId={myStoreId}
          authorName={me?.name || "スタッフ"}
          role="staff"
          shiftComments={shiftComments}
          setShiftComments={setShiftComments}
          colors={cc}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SHIFT REQUEST（希望休申請）
// ═══════════════════════════════════════════════════════════════════════════
function ShiftRequest({ shiftRequests, setShiftRequests, staff, stores, role, activeStaffId }) {
  const cc = useC(role);
  const now = new Date();
  // 希望休のデフォルト表示は翌々月（5月なら7月、6月なら8月）
  const defaultMonth = (() => {
    const target = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    return `${target.getFullYear()}-${String(target.getMonth()+1).padStart(2,"0")}`;
  })();
  const [viewMonth, setViewMonth] = useState(defaultMonth);
  const [selectedDates, setSelectedDates] = useState([]);
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [filterStaff, setFilterStaff] = useState("all");
  const [dateDetailModal, setDateDetailModal] = useState(null); // 日付詳細モーダル
  const [showConfirm, setShowConfirm] = useState(false); // 希望休 確認モーダル
  const [isPaidLeave, setIsPaidLeave] = useState(false); // 有給として申請するか

  const me = staff.find(s=>s.id===activeStaffId);
  const myStoreIds = me ? (me.storeIds||[me.storeId]) : [];

  // カレンダー生成
  const [year, month] = viewMonth.split("-").map(Number);
  const firstDay = new Date(year, month-1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const weeks = [];
  let day = 1 - firstDay;
  while (day <= daysInMonth) {
    const week = [];
    for (let i=0; i<7; i++, day++) {
      week.push(day >= 1 && day <= daysInMonth ? day : null);
    }
    weeks.push(week);
  }

  const dateStr = (d) => `${viewMonth}-${String(d).padStart(2,"0")}`;
  const isSelected = (d) => d && selectedDates.includes(dateStr(d));

  // 既存の自分の申請
  const myRequests = shiftRequests.filter(r=>r.staffId===activeStaffId && r.month===viewMonth);
  const myDates = myRequests.flatMap(r=>r.dates);

  // 申請済みの希望休を選択状態にプリロード（編集可能にする）
  useEffect(() => {
    const mine = shiftRequests.filter(r=>r.staffId===activeStaffId && r.month===viewMonth);
    setSelectedDates(mine.flatMap(r=>r.dates));
  }, [viewMonth, activeStaffId, shiftRequests]);

  const toggleDate = (d) => {
    if (!d) return;
    const ds = dateStr(d);
    setSelectedDates(prev => prev.includes(ds) ? prev.filter(x=>x!==ds) : [...prev, ds]);
  };

  const submit = () => {
    // 何も選択していない場合 → 「希望なし」として提出
    const isNoRequest = selectedDates.length === 0;
    const existing = shiftRequests.find(r=>r.staffId===activeStaffId&&r.month===viewMonth);
    const entry = {
      id: existing?.id || `sr${Date.now()}`,
      staffId: activeStaffId,
      staffName: me?.name||"",
      storeId: me?.storeId||"",
      month: viewMonth,
      dates: isNoRequest ? [] : selectedDates.sort(),
      paidLeaveDates: existing?.paidLeaveDates || [], // オーナーが管理
      noRequest: isNoRequest,
      note,
      submittedAt: todayStr(),
    };
    setShiftRequests(prev => existing
      ? prev.map(r=>r.id===existing.id?entry:r)
      : [...prev, entry]
    );
    if (typeof gasPost === "function") {
      const storeName = stores.find(s=>s.id===entry.storeId)?.name || "";
      gasPost("saveShiftRequests", { requests: [{ ...entry, storeName }] });
    }
    setSelectedDates([]); setNote(""); setSaved(true);
    setTimeout(()=>setSaved(false), 2500);
  };

  // オーナー用：月・スタッフでフィルター
  const ownerView = shiftRequests.filter(r=>r.month===viewMonth && (filterStaff==="all"||r.staffId===filterStaff));

  const WEEKDAYS = ["日","月","火","水","木","金","土"];

  return (
    <div style={{ padding:"14px 14px 32px" }}>

      {/* 月切り替え */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <button onClick={()=>{
          const d=new Date(year,month-2,1);
          setViewMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
          setSelectedDates([]);
        }} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:8,padding:"6px 12px",color:cc.muted,cursor:"pointer",fontSize:12 }}>‹ 前月</button>
        <div style={{ fontSize:15, fontWeight:600, color:cc.gold, fontFamily:"Cormorant Garamond" }}>
          {year}年{month}月
        </div>
        <button onClick={()=>{
          const d=new Date(year,month,1);
          setViewMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
          setSelectedDates([]);
        }} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:8,padding:"6px 12px",color:cc.muted,cursor:"pointer",fontSize:12 }}>次月 ›</button>
      </div>

      {/* ── スタッフ用カレンダー ── */}
      {role==="staff" && (() => {
        return (
        <>
          <div style={{ ...cardS(role,{marginBottom:12}) }}>
            <div style={{ fontSize:11,color:cc.muted,marginBottom:10 }}>
              希望休をタップして選択 → 申請する
              {myDates.length>0 && <span style={{ marginLeft:8,color:cc.gold }}>（申請済み {myDates.length}日）</span>}
              <div style={{ fontSize:10, color:cc.muted2, marginTop:4 }}>
                💡 何も選択せず「申請する」を押すと「希望休なし」として提出されます
              </div>
            </div>

            {/* 曜日ヘッダー */}
            <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4 }}>
              {WEEKDAYS.map((w,i)=>(
                <div key={w} style={{ textAlign:"center",fontSize:10,fontWeight:600,
                  color:i===0?"#e05050":i===6?"#4070c0":cc.muted,padding:"4px 0" }}>{w}</div>
              ))}
            </div>

            {/* カレンダー */}
            {weeks.map((week,wi)=>(
              <div key={wi} style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2 }}>
                {week.map((d,di)=>{
                  const isApplied = d && myDates.includes(dateStr(d));
                  const isSel = isSelected(d);
                  const isSun = di===0, isSat = di===6;
                  const isToday = d && dateStr(d)===todayStr();
                  return (
                    <div key={di} onClick={()=>toggleDate(d)} style={{
                      height:36, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:12, fontWeight:isSel||isApplied?700:400,
                      cursor:d?"pointer":"default",
                      background:isSel?"#e05050":isApplied?`${cc.gold}33`:d?"transparent":"transparent",
                      color:!d?cc.muted2:isSel?"#fff":isApplied?cc.gold:isSun?"#e05050":isSat?"#4070c0":cc.cream,
                      border:isToday?`2px solid ${cc.gold}`:isSel?"2px solid #e05050":"2px solid transparent",
                    }}>
                      {d||""}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* 凡例 */}
            <div style={{ display:"flex",gap:12,marginTop:10,fontSize:10,color:cc.muted2 }}>
              <span><span style={{ color:"#e05050",fontWeight:700 }}>■</span> 選択中</span>
              <span><span style={{ color:cc.gold }}>■</span> 申請済み</span>
            </div>
          </div>

          {/* 備考 */}
          <div style={{ ...cardS(role,{marginBottom:12}) }}>
            <label style={{ fontSize:10,color:cc.muted,display:"block",marginBottom:6 }}>備考（任意）</label>
            <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="理由・コメントなど"
              style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,
                padding:"8px 10px",color:cc.cream,fontSize:12,height:60,resize:"none",fontFamily:"Jost,sans-serif" }}/>
          </div>

          {selectedDates.length>0 && (
            <div style={{ ...cardS(role,{marginBottom:12,background:cc.bg2}) }}>
              <div style={{ fontSize:11,color:cc.muted,marginBottom:6 }}>選択中の希望休日（{selectedDates.length}日）</div>
              <div style={{ display:"flex",flexWrap:"wrap",gap:4 }}>
                {selectedDates.sort().map(d=>(
                  <span key={d} style={{ background:"#e0505022",color:"#e05050",borderRadius:8,padding:"2px 8px",fontSize:11 }}>
                    {d.slice(5).replace("-","/")}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button onClick={()=>setShowConfirm(true)} style={{
            width:"100%",background:saved?"#4a7a5a":cc.gold,
            color:cc.bg,
            border:"none",borderRadius:10,padding:13,fontSize:13,fontWeight:600,cursor:"pointer",transition:"background 0.3s"
          }}>
            {saved?"✓ 申請しました！":selectedDates.length>0?`${selectedDates.length}日を確認する`:"希望なしで提出する"}
          </button>

          {/* 確認モーダル */}
          {showConfirm && (
            <div onClick={()=>setShowConfirm(false)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
              <div onClick={e=>e.stopPropagation()} style={{ background:cc.bg1,border:`1px solid ${cc.border}`,borderRadius:12,padding:20,maxWidth:380,width:"100%",maxHeight:"80vh",overflow:"auto" }}>
                <div style={{ fontSize:14,fontWeight:600,color:cc.gold,marginBottom:14 }}>
                  {selectedDates.length === 0 ? "📋 「希望なし」で提出します" : "📋 希望休 申請内容の確認"}
                </div>

                <div style={{ background:cc.bg2,borderRadius:8,padding:12,marginBottom:12 }}>
                  <div style={{ fontSize:11,color:cc.muted,marginBottom:6 }}>対象月</div>
                  <div style={{ fontSize:14,color:cc.cream,fontWeight:500 }}>{year}年{month}月</div>
                </div>

                {selectedDates.length === 0 ? (
                  <div style={{ background:cc.bg2,borderRadius:8,padding:12,marginBottom:12 }}>
                    <div style={{ fontSize:12, color:cc.cream, lineHeight:1.7 }}>
                      ✅ <strong>{year}年{month}月の希望休はなし</strong>として提出します<br/>
                      <span style={{ fontSize:10, color:cc.muted2 }}>※全日勤務可能として登録されます</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ background:cc.bg2,borderRadius:8,padding:12,marginBottom:12 }}>
                    <div style={{ fontSize:11,color:cc.muted,marginBottom:6 }}>
                      希望休（{selectedDates.length}日）
                    </div>
                    <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                      {selectedDates.sort().map(d=>{
                        const day = parseInt(d.slice(8,10));
                        const dt = new Date(d);
                        const w = ["日","月","火","水","木","金","土"][dt.getDay()];
                        return (
                          <span key={d} style={{ background:`${cc.gold}22`,color:cc.gold,borderRadius:8,padding:"4px 10px",fontSize:12,fontWeight:500 }}>
                            {day}日({w})
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {note && (
                  <div style={{ background:cc.bg2,borderRadius:8,padding:12,marginBottom:12 }}>
                    <div style={{ fontSize:11,color:cc.muted,marginBottom:6 }}>備考</div>
                    <div style={{ fontSize:12,color:cc.cream,whiteSpace:"pre-wrap" }}>{note}</div>
                  </div>
                )}

                <div style={{ display:"flex",gap:8 }}>
                  <button onClick={()=>setShowConfirm(false)} style={{ flex:1,background:cc.bg2,color:cc.muted,border:`1px solid ${cc.border}`,borderRadius:8,padding:10,fontSize:12,cursor:"pointer" }}>戻る</button>
                  <button onClick={()=>{ submit(); setShowConfirm(false); }} style={{ flex:2,background:cc.gold,color:cc.bg,border:"none",borderRadius:8,padding:10,fontSize:12,fontWeight:600,cursor:"pointer" }}>
                    {selectedDates.length === 0 ? "希望なしで提出する" : "この内容で申請する"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {myDates.length>0 && (
            <div style={{ ...cardS(role,{marginTop:12}) }}>
              <div style={{ fontSize:11,color:cc.muted,marginBottom:8 }}>申請済みの希望休（{month}月）</div>
              <div style={{ display:"flex",flexWrap:"wrap",gap:4 }}>
                {myDates.sort().map(d=>(
                  <span key={d} style={{ background:`${cc.gold}22`,color:cc.gold,borderRadius:8,padding:"3px 10px",fontSize:11 }}>
                    {d.slice(5).replace("-","/")}
                  </span>
                ))}
              </div>
              {myRequests[0]?.note && <div style={{ fontSize:10,color:cc.muted2,marginTop:6 }}>備考: {myRequests[0].note}</div>}
            </div>
          )}
        </>
        );
      })()}

      {/* ── オーナー用：全スタッフ一覧 ── */}
      {role==="owner" && (
        <>
          {/* スタッフフィルター */}
          <div style={{ marginBottom:12,overflowX:"auto" }}>
            <div style={{ display:"flex",gap:6,paddingBottom:4 }}>
              <button onClick={()=>setFilterStaff("all")} style={{
                background:filterStaff==="all"?C.gold:C.bg2,color:filterStaff==="all"?C.bg:C.muted,
                border:`1px solid ${filterStaff==="all"?C.gold:C.border}`,borderRadius:16,padding:"5px 12px",fontSize:11,cursor:"pointer",whiteSpace:"nowrap"
              }}>全員</button>
              {staff.map(s=>(
                <button key={s.id} onClick={()=>setFilterStaff(s.id)} style={{
                  background:filterStaff===s.id?C.gold:C.bg2,color:filterStaff===s.id?C.bg:C.muted,
                  border:`1px solid ${filterStaff===s.id?C.gold:C.border}`,borderRadius:16,padding:"5px 12px",fontSize:11,cursor:"pointer",whiteSpace:"nowrap"
                }}>{s.name}</button>
              ))}
            </div>
          </div>

          {/* カレンダーヘッダー */}
          <div style={{ ...card({marginBottom:12}) }}>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:6 }}>
              {WEEKDAYS.map((w,i)=>(
                <div key={w} style={{ textAlign:"center",fontSize:10,fontWeight:600,
                  color:i===0?"#e05050":i===6?"#4070c0":C.muted }}>{w}</div>
              ))}
            </div>
            {weeks.map((week,wi)=>(
              <div key={wi} style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2 }}>
                {week.map((d,di)=>{
                  const ds = d ? dateStr(d) : null;
                  const requestors = ds ? ownerView.filter(r=>r.dates.includes(ds)) : [];
                  const isSun=di===0, isSat=di===6;
                  return (
                    <div key={di}
                      onClick={() => requestors.length > 0 && setDateDetailModal({ date: ds, requestors })}
                      style={{ minHeight:64,borderRadius:8,padding:"3px",
                      background:d?requestors.length>0?"#fff0f0":C.bg1:C.bg2,
                      border:`1px solid ${C.border}`,
                      cursor: requestors.length > 0 ? "pointer" : "default" }}>
                      {d && <>
                        <div style={{ fontSize:10,fontWeight:600,textAlign:"center",marginBottom:3,
                          color:isSun?"#e05050":isSat?"#4070c0":C.cream }}>{d}</div>
                        <div style={{ display:"flex",flexDirection:"column",gap:2 }}>
                          {requestors.length === 0 ? null : requestors.length <= 2 ? (
                            // 2名以下：名前を全て表示
                            requestors.map(r=>{
                              const fullName = r.staffName || "";
                              const lastName = fullName.split(/[\s　]/)[0];
                              const display = lastName.length > 3 ? lastName.slice(0,2) : lastName;
                              return (
                                <span key={r.staffId} title={fullName} style={{
                                  fontSize:9,
                                  background:"#e0505022",
                                  color:"#e05050",
                                  borderRadius:4,
                                  padding:"1px 3px",
                                  lineHeight:1.3,
                                  textAlign:"center",
                                  whiteSpace:"nowrap",
                                  overflow:"hidden",
                                  textOverflow:"ellipsis",
                                }}>
                                  {display}
                                </span>
                              );
                            })
                          ) : (
                            // 3名以上：人数表示＋タップで詳細
                            <span style={{
                              fontSize:10,
                              background:"#e05050",
                              color:"#fff",
                              borderRadius:4,
                              padding:"2px 4px",
                              fontWeight:600,
                              textAlign:"center",
                              lineHeight:1.3,
                            }}>
                              {requestors.length}名
                            </span>
                          )}
                        </div>
                      </>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* 日付詳細モーダル */}
          {dateDetailModal && (
            <div onClick={()=>setDateDetailModal(null)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
              <div onClick={e=>e.stopPropagation()} style={{ background:C.bg1,border:`1px solid ${C.border}`,borderRadius:12,padding:20,maxWidth:380,width:"100%",maxHeight:"80vh",overflow:"auto" }}>
                <div style={{ fontSize:14,fontWeight:600,color:C.gold,marginBottom:14 }}>
                  📅 {dateDetailModal.date.slice(5).replace("-","月")}日 の希望休（{dateDetailModal.requestors.length}名）
                </div>
                <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                  {dateDetailModal.requestors.map(r => {
                    const st = stores.find(s=>s.id===r.storeId);
                    const isPaid = (r.paidLeaveDates||[]).includes(dateDetailModal.date);
                    return (
                      <div key={r.staffId} style={{ background:C.bg2,borderRadius:8,padding:"10px 12px",border:`1px solid ${C.border}` }}>
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                          <div>
                            <span style={{ fontSize:13,color:C.cream,fontWeight:500 }}>{r.staffName}</span>
                            <span style={{ fontSize:10,color:C.muted,marginLeft:8 }}>{st?.name}</span>
                          </div>
                          {isPaid && <span style={{ fontSize:10,color:"#7ba87b",background:"rgba(123,168,123,0.18)",borderRadius:8,padding:"2px 8px" }}>🌴 有給</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={()=>setDateDetailModal(null)} style={{ marginTop:14,width:"100%",background:C.bg2,color:C.cream,border:`1px solid ${C.border}`,borderRadius:8,padding:10,fontSize:12,cursor:"pointer" }}>閉じる</button>
              </div>
            </div>
          )}

          {/* 提出状況サマリー */}
          {(() => {
            const submittedIds = new Set(ownerView.map(r => r.staffId));
            const submitted = staff.filter(s => submittedIds.has(s.id));
            const notSubmitted = staff.filter(s => !submittedIds.has(s.id));
            return (
              <div style={{ ...card({marginBottom:12}) }}>
                <div style={{ fontSize:12, color:C.gold, fontWeight:600, marginBottom:10 }}>
                  📊 提出状況（{submitted.length}/{staff.length}名）
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <div>
                    <div style={{ fontSize:10, color:"#7ba87b", marginBottom:4, fontWeight:600 }}>✅ 提出済 ({submitted.length}名)</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      {submitted.length === 0 ? (
                        <span style={{ fontSize:10, color:C.muted2 }}>—</span>
                      ) : submitted.map(s => {
                        const r = ownerView.find(x => x.staffId === s.id);
                        const isNoReq = r?.noRequest || r?.dates?.length === 0;
                        return (
                          <span key={s.id} style={{
                            background: isNoReq ? `${C.muted2}22` : "rgba(123,168,123,0.18)",
                            color: isNoReq ? C.muted : "#7ba87b",
                            borderRadius:8, padding:"3px 8px", fontSize:10
                          }}>
                            {s.name}{isNoReq && " (希望なし)"}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:"#c47060", marginBottom:4, fontWeight:600 }}>⚠️ 未提出 ({notSubmitted.length}名)</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      {notSubmitted.length === 0 ? (
                        <span style={{ fontSize:10, color:C.muted2 }}>全員提出済</span>
                      ) : notSubmitted.map(s => (
                        <span key={s.id} style={{ background:"rgba(196,112,96,0.18)", color:"#c47060", borderRadius:8, padding:"3px 8px", fontSize:10 }}>
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* スタッフ別一覧 */}
          {ownerView.length===0
            ? <div style={{ textAlign:"center",fontSize:12,color:C.muted2,padding:24 }}>この月の希望休申請はありません</div>
            : ownerView.map(r=>{
              const st=stores.find(s=>s.id===r.storeId);
              const isNoRequest = r.noRequest || r.dates?.length === 0;
              const paidLeaveDates = r.paidLeaveDates || [];
              return (
                <div key={r.id} style={{ ...card({marginBottom:8}) }}>
                  <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}>
                    <div>
                      <span style={{ fontSize:13,color:C.cream,fontWeight:500 }}>{r.staffName}</span>
                      <span style={{ fontSize:10,color:C.muted,marginLeft:8 }}>{st?.name}</span>
                      {isNoRequest && <span style={{ fontSize:10, color:C.muted2, marginLeft:8, background:`${C.muted2}22`, borderRadius:8, padding:"2px 8px" }}>希望なし</span>}
                    </div>
                    <span style={{ fontSize:10,color:C.muted2 }}>{r.submittedAt}申請</span>
                  </div>
                  {!isNoRequest && (
                    <>
                      <div style={{ display:"flex",flexWrap:"wrap",gap:4,marginBottom:6 }}>
                        {r.dates.map(d=>{
                          const isPaid = paidLeaveDates.includes(d);
                          const togglePaid = () => {
                            const newPaidDates = isPaid
                              ? paidLeaveDates.filter(x => x !== d)
                              : [...paidLeaveDates, d];
                            const updated = { ...r, paidLeaveDates: newPaidDates };
                            setShiftRequests(prev => prev.map(x => x.id === r.id ? updated : x));
                            if (typeof gasPost === "function") {
                              const storeName = stores.find(s=>s.id===updated.storeId)?.name || "";
                              gasPost("saveShiftRequests", { requests: [{ ...updated, storeName }] });
                            }
                          };
                          return (
                            <button key={d} onClick={togglePaid} title={isPaid ? "クリックで通常希望休に戻す" : "クリックで有給に変更"} style={{
                              background: isPaid ? "rgba(123,168,123,0.18)" : `${C.gold}22`,
                              color: isPaid ? "#7ba87b" : C.gold,
                              borderRadius:8, padding:"2px 8px", fontSize:11,
                              border: `1px solid ${isPaid ? "rgba(123,168,123,0.4)" : "transparent"}`,
                              cursor: "pointer"
                            }}>
                              {isPaid && "🌴"}{d.slice(5).replace("-","/")}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize:9, color:C.muted2, marginBottom:r.note?6:0 }}>
                        💡 日付タップで有給切替（{paidLeaveDates.length}日有給／{r.dates.length}日中）
                      </div>
                    </>
                  )}
                  {r.note && <div style={{ fontSize:10,color:C.muted2 }}>備考: {r.note}</div>}
                </div>
              );
            })
          }
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STAFF SALARY VIEW（スタッフ用給与確認）
// ═══════════════════════════════════════════════════════════════════════════
function StaffSalary({ records, staff, activeStaffId, stores, expenses, shiftRequests }) {
  const cc = useC("staff");
  const now = new Date();
  const day = now.getDate();
  const isMonthStart = day >= 1 && day <= 5; // 月初1〜5日

  const me = staff.find(s=>s.id===activeStaffId);
  if (!me) return null;

  const store = stores.find(st=>st.id===me.storeId);

  // 自分の経費（今月分）
  const curYMExp = nowYM();
  const myExpensesAll = (expenses||[]).filter(e => e.staffId === activeStaffId).sort((a,b)=>b.date.localeCompare(a.date));
  const myExpensesThisMonth = myExpensesAll.filter(e => ym(e.date) === curYMExp);
  const thisMonthExpenseTotal = myExpensesThisMonth.reduce((a,e)=>a+(e.amount||0), 0);

  // 目標達成金：正社員 1万円 / パート・その他 5千円
  const goalBonusAmount = me.shiftType === "regular" ? 10000 : 5000;
  const goalBonusLabel = me.shiftType === "regular" ? "正社員" : "パート・その他";

  // 今月
  const curYM = nowYM();
  const thisMonthRecs = records.filter(r=>r.staffId===activeStaffId&&ym(r.date)===curYM);
  const thisSales = thisMonthRecs.reduce((a,r)=>a+r.amount,0);
  const thisShimei = thisMonthRecs.reduce((a,r)=>a+(r.shimei||0),0);
  const thisCommission = me.rate>0 ? Math.round(thisSales*(me.rate/100)) : calcCommission(thisSales, store);
  const thisRate = me.rate>0 ? me.rate : getCommissionRate(thisSales, store);
  const thisShimeiBonus = calcShimeiBonus(thisShimei);
  const thisTaxExcluded = getTaxExcluded(thisSales);
  const thisPct = me.target>0 ? Math.min(100,Math.round(thisSales/me.target*100)) : 0;
  const thisGoalBonus = thisPct >= 100 ? goalBonusAmount : 0;
  // 追加支給合計（基本給を除く）
  const thisExtraTotal = thisCommission + thisShimeiBonus + thisGoalBonus;

  // 🌴 有給使用日数の集計（今月分・先月分）
  const myShiftReqs = (shiftRequests||[]).filter(r => r.staffId === activeStaffId);
  const thisMonthPaidDates = myShiftReqs
    .filter(r => r.month === curYM)
    .flatMap(r => r.paidLeaveDates || []);
  const thisMonthPaidUsed = thisMonthPaidDates.length;

  // 先月
  const prevDate = new Date(now.getFullYear(), now.getMonth()-1, 1);
  const prevYM = `${prevDate.getFullYear()}-${String(prevDate.getMonth()+1).padStart(2,"0")}`;
  const prevRecs = records.filter(r=>r.staffId===activeStaffId&&ym(r.date)===prevYM);
  const prevSales = prevRecs.reduce((a,r)=>a+r.amount,0);
  const prevShimei = prevRecs.reduce((a,r)=>a+(r.shimei||0),0);
  const prevCommission = me.rate>0 ? Math.round(prevSales*(me.rate/100)) : calcCommission(prevSales, store);
  const prevRate = me.rate>0 ? me.rate : getCommissionRate(prevSales, store);
  const prevShimeiBonus = calcShimeiBonus(prevShimei);
  const prevPct = me.target>0 ? Math.min(100,Math.round(prevSales/me.target*100)) : 0;
  const prevGoalBonus = prevPct >= 100 ? goalBonusAmount : 0;
  const prevExtraTotal = prevCommission + prevShimeiBonus + prevGoalBonus;

  // 🌴 先月の有給使用日数
  const prevMonthPaidDates = myShiftReqs
    .filter(r => r.month === prevYM)
    .flatMap(r => r.paidLeaveDates || []);
  const prevMonthPaidUsed = prevMonthPaidDates.length;

  // ティア（所属店舗のティアを表示）※税抜ベースで判定
  const storeTiers = getStoreTiers(store);
  const tiers = [...storeTiers].sort((a,b)=>a.taxEx-b.taxEx).map(t => ({
    taxExThreshold: t.taxEx,              // 税抜閾値（基準）
    inclThreshold: Math.round(t.taxEx * TAX_RATE), // 税込参考
    rate: t.rate
  }));
  // 次のティアまでの距離は税抜ベースで計算
  const thisSalesTaxEx = Math.round(thisSales / TAX_RATE);
  const nextTier = tiers.find(t=>thisSalesTaxEx < t.taxExThreshold);
  const remainingTaxEx = nextTier ? nextTier.taxExThreshold - thisSalesTaxEx : 0;

  const rowStyle = { display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${cc.border}` };
  const labelStyle = { fontSize:12, color:cc.muted };
  const valueStyle = { fontSize:13, fontWeight:600, color:cc.cream };

  return (
    <div style={{ padding:"14px 14px 32px" }}>

      {/* 月初メッセージ（1〜5日） */}
      {isMonthStart && prevSales>0 && (
        <div style={{ ...cardS("staff",{marginBottom:14, background:"#fff8f0", border:`1px solid ${cc.gold}66`}) }}>
          <div style={{ fontSize:16, textAlign:"center", marginBottom:8 }}>🎉</div>
          <div style={{ fontSize:14, fontWeight:700, color:cc.gold, textAlign:"center", marginBottom:4 }}>
            先月もお疲れ様でした！
          </div>
          <div style={{ fontSize:11, color:cc.muted, textAlign:"center", marginBottom:14 }}>
            {prevYM.replace("-","年")}月の追加支給（基本給に加えて支給されます）
          </div>
          {/* 歩合給：正社員・パートに表示 */}
          {(me.shiftType === "regular" || me.shiftType === "part") && (
            <div style={rowStyle}>
              <div>
                <div style={labelStyle}>歩合給（{prevRate}%）</div>
                <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>
                  先月売上 {fmt(prevSales)} → 税抜 {fmt(getTaxExcluded(prevSales))}
                </div>
              </div>
              <span style={valueStyle}>{fmt(prevCommission)}</span>
            </div>
          )}
          {/* 指名料：全員表示 */}
          {prevShimeiBonus>0 && (
            <div style={rowStyle}>
              <div>
                <div style={labelStyle}>指名料（{prevShimei}本 × ¥500）</div>
              </div>
              <span style={valueStyle}>{fmt(prevShimeiBonus)}</span>
            </div>
          )}
          {prevGoalBonus>0 && (
            <div style={rowStyle}>
              <div>
                <div style={labelStyle}>🏆 目標達成金（{goalBonusLabel}）</div>
                <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>達成率 {prevPct}%</div>
              </div>
              <span style={valueStyle}>{fmt(prevGoalBonus)}</span>
            </div>
          )}
          {/* 🌴 先月の有給使用 */}
          {prevMonthPaidUsed > 0 && (
            <div style={rowStyle}>
              <div>
                <div style={labelStyle}>🌴 有給使用</div>
                <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>先月使用した有給休暇の日数</div>
              </div>
              <span style={{ fontSize:13, fontWeight:600, color:cc.gold }}>{prevMonthPaidUsed}日</span>
            </div>
          )}
          <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 0" }}>
            <span style={{ fontSize:13, fontWeight:700, color:cc.gold }}>追加支給 合計</span>
            <span style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:cc.gold, fontWeight:700 }}>{fmt(prevExtraTotal)}</span>
          </div>
          <div style={{ fontSize:10, color:cc.muted2, textAlign:"center", marginTop:6 }}>
            ※ 基本給はこの金額とは別に支給されます
          </div>
          <div style={{ fontSize:11, color:cc.muted, textAlign:"center", marginTop:10 }}>
            今月もよろしくお願いします！💪
          </div>
        </div>
      )}

      {/* 🌴 有給使用日数（オーナー入力済の使用記録のみ表示） */}
      {(() => {
        // 自分の有給使用日（GAS/オーナー入力分）
        const allMyPaidDates = (shiftRequests||[])
          .filter(r => r.staffId === activeStaffId)
          .flatMap(r => r.paidLeaveDates || []);
        // 今月の有給使用
        const curYM = nowYM();
        const thisMonthPaid = allMyPaidDates.filter(d => d.startsWith(curYM));
        // 先月の有給使用
        const prevDate = new Date();
        prevDate.setMonth(prevDate.getMonth() - 1);
        const prevYM = `${prevDate.getFullYear()}-${String(prevDate.getMonth()+1).padStart(2,"0")}`;
        const prevMonthPaid = allMyPaidDates.filter(d => d.startsWith(prevYM));
        // 累計
        const totalPaid = allMyPaidDates.length;

        if (totalPaid === 0 && thisMonthPaid.length === 0 && prevMonthPaid.length === 0) return null;

        return (
          <div style={cardS("staff",{marginBottom:12})}>
            <div style={{ fontSize:12, color:cc.gold, fontWeight:600, marginBottom:8 }}>🌴 有給休暇 使用状況</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
              <div style={{ background:cc.bg2, borderRadius:8, padding:"10px 8px", textAlign:"center" }}>
                <div style={{ fontSize:9, color:cc.muted2, marginBottom:3 }}>今月使用</div>
                <div style={{ fontSize:16, fontWeight:700, color: thisMonthPaid.length>0?cc.gold:cc.muted }}>
                  {thisMonthPaid.length}<span style={{ fontSize:10, marginLeft:2 }}>日</span>
                </div>
              </div>
              <div style={{ background:cc.bg2, borderRadius:8, padding:"10px 8px", textAlign:"center" }}>
                <div style={{ fontSize:9, color:cc.muted2, marginBottom:3 }}>先月使用</div>
                <div style={{ fontSize:16, fontWeight:700, color: prevMonthPaid.length>0?cc.gold:cc.muted }}>
                  {prevMonthPaid.length}<span style={{ fontSize:10, marginLeft:2 }}>日</span>
                </div>
              </div>
              <div style={{ background:cc.bg2, borderRadius:8, padding:"10px 8px", textAlign:"center" }}>
                <div style={{ fontSize:9, color:cc.muted2, marginBottom:3 }}>累計使用</div>
                <div style={{ fontSize:16, fontWeight:700, color:cc.gold }}>
                  {totalPaid}<span style={{ fontSize:10, marginLeft:2 }}>日</span>
                </div>
              </div>
            </div>
            {thisMonthPaid.length > 0 && (
              <div style={{ marginTop:8, padding:"6px 10px", background:cc.bg2, borderRadius:8 }}>
                <div style={{ fontSize:9, color:cc.muted2, marginBottom:4 }}>今月の使用日</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                  {thisMonthPaid.sort().map(d => (
                    <span key={d} style={{ background:`${cc.gold}22`, color:cc.gold, borderRadius:6, padding:"2px 8px", fontSize:10 }}>
                      {d.slice(5).replace("-","/")}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div style={{ fontSize:9, color:cc.muted2, marginTop:8, lineHeight:1.5 }}>
              💡 有給残数の確認はオーナーまでお問い合わせください
            </div>
          </div>
        );
      })()}

      {/* 今月の見込み */}
      <div style={cardS("staff",{marginBottom:12})}>
        <div style={{ fontSize:12, color:cc.gold, fontWeight:600, marginBottom:8 }}>
          今月の追加支給（見込み）
        </div>
        <div style={{ background:cc.bg2, borderRadius:8, padding:"7px 10px", marginBottom:8, fontSize:10, color:cc.muted, lineHeight:1.5 }}>
          💡 <span style={{ color:cc.gold }}>基本給に加えて</span>以下が支給されます。住宅手当は基本給に含まれています。
        </div>
        <div style={{ background:cc.bg2, borderRadius:8, padding:"7px 10px", marginBottom:12, fontSize:10, color:cc.muted, lineHeight:1.5 }}>
          📌 こちらに記載の歩合給が給与計算に使用されます。相違のある場合は別途 <span style={{ color:cc.gold }}>worksplus2017@gmail.com</span> 宛にメールでお知らせください。
        </div>

        {/* 歩合給：正社員・パートに表示 */}
        {(me.shiftType === "regular" || me.shiftType === "part") && (
          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>🎯 歩合給（{me.rate>0?me.rate:thisRate}%）</div>
              <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>
                今月売上 {fmt(thisSales)} → 税抜 {fmt(thisTaxExcluded)}
              </div>
            </div>
            <span style={valueStyle}>{fmt(thisCommission)}</span>
          </div>
        )}
        {/* 指名料：全員表示（正社員・パート共通） */}
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>👥 指名料（{thisShimei}本 × ¥500）</div>
            <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>
              {thisShimei >= 10 ? `${thisShimei}本全て対象` : `10本未満のため対象外（あと${10-thisShimei}本）`}
            </div>
          </div>
          <span style={{ fontSize:13, fontWeight:600, color:thisShimeiBonus>0?cc.green:cc.muted }}>{thisShimeiBonus>0?fmt(thisShimeiBonus):"－"}</span>
        </div>
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>🏆 目標達成金（{goalBonusLabel}）</div>
            <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>
              {thisPct>=100 ? `達成率 ${thisPct}% 🎉` : `達成率 ${thisPct}%（100%で${fmt(goalBonusAmount)}）`}
            </div>
          </div>
          <span style={{ fontSize:13, fontWeight:600, color:thisGoalBonus>0?cc.green:cc.muted }}>{thisGoalBonus>0?fmt(thisGoalBonus):"－"}</span>
        </div>
        {/* 🌴 有給使用日数 */}
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>🌴 有給使用</div>
            <div style={{ fontSize:10, color:cc.muted2, marginTop:2 }}>
              今月使用した有給休暇の日数です
            </div>
          </div>
          <span style={{ fontSize:13, fontWeight:600, color:thisMonthPaidUsed>0?cc.gold:cc.muted }}>
            {thisMonthPaidUsed > 0 ? `${thisMonthPaidUsed}日` : "－"}
          </span>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 0", borderTop:`1px solid ${cc.border}` }}>
          <span style={{ fontSize:13, fontWeight:700, color:cc.gold }}>追加支給 合計（見込み）</span>
          <span style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:cc.gold, fontWeight:700 }}>{fmt(thisExtraTotal)}</span>
        </div>
        <div style={{ fontSize:10, color:cc.muted2, textAlign:"center", marginTop:6 }}>
          ※ 基本給はこの金額とは別に支給されます
        </div>

        {/* 目標達成バー */}
        <div style={{ marginTop:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:cc.muted2, marginBottom:4 }}>
            <span>目標達成率</span><span>{thisPct}%</span>
          </div>
          <div style={{ height:6, background:cc.bg2, borderRadius:3 }}>
            <div style={{ height:"100%", width:`${thisPct}%`, background:thisPct>=100?cc.green:cc.gold, borderRadius:3, transition:"width 0.8s" }}/>
          </div>
        </div>
      </div>

      {/* 歩合ティア案内（税抜ベース）※正社員・パートに表示 */}
      {(me.shiftType === "regular" || me.shiftType === "part") && (
      <div style={cardS("staff",{marginBottom:12})}>
        <div style={{ fontSize:12, color:cc.gold, fontWeight:600, marginBottom:4 }}>歩合ティア</div>
        <div style={{ fontSize:10, color:cc.muted2, marginBottom:10 }}>※税抜売上（売上÷1.1）で判定します</div>
        {tiers.map((t,i)=>{
          const prevTaxEx = i===0 ? 0 : tiers[i-1].taxExThreshold;
          const isActive = thisSalesTaxEx >= t.taxExThreshold;
          const isCurrent = thisSalesTaxEx >= prevTaxEx && thisSalesTaxEx < t.taxExThreshold;
          const thresholdMan = Math.round(t.taxExThreshold / 10000);
          return (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:`1px solid ${cc.border}` }}>
              <div style={{ width:28, height:28, borderRadius:"50%", flexShrink:0,
                background:isActive?cc.gold:isCurrent?`${cc.gold}33`:cc.bg2,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:11, fontWeight:700, color:isActive?cc.bg:cc.muted }}>
                {isActive?"✓":isCurrent?"▶":`${t.rate}%`}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, color:isActive||isCurrent?cc.cream:cc.muted }}>
                  {thresholdMan}万円以上（税込¥{fmt(t.inclThreshold).replace("¥","")}以上）→ {t.rate}%
                </div>
                {isCurrent && remainingTaxEx>0 && (
                  <div style={{ fontSize:10, color:cc.gold, marginTop:2 }}>
                    🔥 あと ¥{fmt(remainingTaxEx).replace("¥","")} で {t.rate}%にUP！
                  </div>
                )}
              </div>
              {isActive && <span style={{ fontSize:10, color:cc.gold }}>達成✓</span>}
            </div>
          );
        })}
        {thisRate===7 && (
          <div style={{ textAlign:"center", fontSize:12, color:cc.gold, padding:"10px 0", fontWeight:600 }}>
            🎉 最高ティア達成！素晴らしい！
          </div>
        )}
      </div>
      )}

      {/* 経費精算一覧 */}
      <div style={cardS("staff",{marginBottom:12})}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8 }}>
          <span style={{ fontSize:12, color:cc.gold, fontWeight:600 }}>🧾 経費精算</span>
          <span style={{ fontSize:10, color:cc.muted2 }}>今月合計: <span style={{ color:cc.gold, fontWeight:600, fontSize:13 }}>{fmt(thisMonthExpenseTotal)}</span></span>
        </div>
        <div style={{ background:cc.bg2, borderRadius:8, padding:"6px 10px", marginBottom:10, fontSize:10, color:cc.muted, lineHeight:1.5 }}>
          💡 申請した経費の一覧です。新しい申請は「個人売上」タブの「🧾 経費精算」から行えます。
        </div>

        {myExpensesAll.length === 0 ? (
          <div style={{ fontSize:11, color:cc.muted2, textAlign:"center", padding:16 }}>まだ経費の申請がありません</div>
        ) : (
          <>
            {/* 今月 */}
            {myExpensesThisMonth.length > 0 && (
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:10, color:cc.muted, marginBottom:6, letterSpacing:1 }}>📅 今月（{curYMExp.replace("-","年")}月）</div>
                {myExpensesThisMonth.map(e => (
                  <div key={e.id} style={{ padding:"8px 0", borderBottom:`1px solid ${cc.border}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                      <span style={{ fontSize:11, color:cc.muted }}>{e.date}</span>
                      <span style={{ fontSize:13, fontWeight:600, color:cc.gold }}>{fmt(e.amount)}</span>
                    </div>
                    {e.memo && <div style={{ fontSize:11, color:cc.cream, marginTop:3, whiteSpace:"pre-wrap" }}>{e.memo}</div>}
                    {e.fileData && (
                      <div style={{ marginTop:4, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                        <a href={e.fileData} download={e.fileName} style={{ fontSize:10, color:cc.gold, textDecoration:"none" }}>📎 {e.fileName}</a>
                        {e.uploadedAt && <span style={{ fontSize:9, color:cc.muted2 }}>添付日 {e.uploadedAt.split(" ")[0]}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* 過去月 */}
            {myExpensesAll.length > myExpensesThisMonth.length && (
              <div>
                <div style={{ fontSize:10, color:cc.muted, marginBottom:6, letterSpacing:1 }}>📆 過去の申請</div>
                {myExpensesAll.filter(e => ym(e.date) !== curYMExp).map(e => (
                  <div key={e.id} style={{ padding:"8px 0", borderBottom:`1px solid ${cc.border}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                      <span style={{ fontSize:11, color:cc.muted }}>{e.date}</span>
                      <span style={{ fontSize:12, fontWeight:500, color:cc.cream }}>{fmt(e.amount)}</span>
                    </div>
                    {e.memo && <div style={{ fontSize:10, color:cc.muted2, marginTop:2, whiteSpace:"pre-wrap" }}>{e.memo}</div>}
                    {e.fileData && (
                      <div style={{ marginTop:3, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                        <a href={e.fileData} download={e.fileName} style={{ fontSize:10, color:cc.gold, textDecoration:"none" }}>📎 {e.fileName}</a>
                        {e.uploadedAt && <span style={{ fontSize:9, color:cc.muted2 }}>添付日 {e.uploadedAt.split(" ")[0]}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Payroll({ records, staff, stores }) {
  const [selYM, setSelYM] = useState(nowYM());
  const curYM=selYM; const [y,m]=curYM.split("-");
  const monthOpts = useMemo(()=>{
    const set = new Set(records.map(r=>ym(r.date)));
    set.add(nowYM());
    return [...set].sort((a,b)=>b.localeCompare(a));
  },[records]);
  const stats=staff.map(s=>{
    const sr=records.filter(r=>r.staffId===s.id&&ym(r.date)===curYM);
    const sales=sr.reduce((a,r)=>a+r.amount,0);
    const myStore = stores.find(st=>st.id===s.storeId);
    const commission = s.rate > 0
      ? Math.round(sales*(s.rate/100))  // 個別設定がある場合
      : calcCommission(sales, myStore); // 店舗別階段式
    const currentRate = s.rate > 0 ? s.rate : getCommissionRate(sales, myStore);
    const shimei = sr.reduce((a,r)=>a+(r.shimei||0),0);
    const shimeiBonus = calcShimeiBonus(shimei);
    return { ...s, sales, commission, currentRate, shimei, shimeiBonus, total:s.baseSalary+commission+shimeiBonus, pct:Math.min(100,Math.round(sales/(s.target||1)*100)) };
  });
  const grandTotal=stats.reduce((a,s)=>a+s.total,0);
  const exportCSV = () => {
    const header=["氏名","店舗","今月売上","基本給","歩合率","歩合額","指名料","支給合計","目標達成率"];
    const rows=stats.map(s=>[s.name,stores.find(st=>st.id===s.storeId)?.name||"",s.sales,s.baseSalary,`${s.rate}%`,s.commission,s.shimeiBonus,s.total,`${s.pct}%`]);
    downloadCSV(`給与計算_${curYM}.csv`,[header,...rows]);
  };
  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <select value={selYM} onChange={e=>setSelYM(e.target.value)} style={{ width:"100%", padding:"10px 12px", marginBottom:12, borderRadius:10, border:`1px solid ${C.border2}`, background:C.bg2, color:C.cream, fontSize:14 }}>
        {monthOpts.map(mo=><option key={mo} value={mo}>{mo}</option>)}
      </select>
      <div style={{ ...card({marginBottom:12}), display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div><div style={{ fontSize:11,color:C.muted }}>{y}年{parseInt(m)}月 給与総額（見込）</div>
          <div style={{ fontFamily:"Cormorant Garamond",fontSize:22,color:C.accent,marginTop:2 }}>{fmt(grandTotal)}</div></div>
        <button onClick={exportCSV} style={{ background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:20,padding:"6px 14px",fontSize:11,color:C.gold,cursor:"pointer" }}>📥 CSV</button>
      </div>
      {stats.map(s=>{
        const storeNameP=(s.storeIds||[s.storeId]).map(id=>stores.find(st=>st.id===id)?.name).filter(Boolean).join('・');
        return (
          <div key={s.id} style={{ ...card({padding:10}), marginBottom:6 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13,color:C.cream,fontWeight:500,lineHeight:1.2 }}>
                  {s.name} <span style={{ fontSize:10,color:C.muted,fontWeight:400 }}>{storeNameP}</span>
                </div>
                <div style={{ fontSize:10,color:C.muted2,marginTop:2 }}>
                  売上 <span style={{ color:C.cream }}>{fmt(s.sales)}</span> ・
                  基本 <span style={{ color:C.cream }}>{fmt(s.baseSalary)}</span> ・
                  歩合 <span style={{ color:C.gold }}>{fmt(s.commission)}</span>
                  <span style={{ color:C.muted2 }}> ({s.rate>0?s.rate:s.currentRate}%{s.rate===0?"自動":""})</span>
                  {s.shimeiBonus>0 && <> ・指名料 <span style={{ color:C.gold }}>{fmt(s.shimeiBonus)}</span><span style={{ color:C.muted2 }}> ({s.shimei}本)</span></>}
                </div>
              </div>
              <div style={{ fontFamily:"Cormorant Garamond",fontSize:18,color:C.gold,lineHeight:1,marginLeft:8 }}>{fmt(s.total)}</div>
            </div>
            <div style={{ height:3,background:C.bg2,borderRadius:2 }}>
              <div style={{ height:"100%",width:`${s.pct}%`,background:C.gold,borderRadius:2,transition:"width 0.8s" }}/>
            </div>
            <div style={{ fontSize:9,color:C.muted2,marginTop:2,textAlign:"right" }}>目標 {s.pct}%達成</div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NOTICE BOARD
// ═══════════════════════════════════════════════════════════════════════════
function NoticeBoard({ notices, setNotices, role, stores=[], activeStaffId, staff=[], onSave, hasUnsaved, saveStatus }) {
  const cc = useC(role);
  const emptyForm = { title:"", url:"", content:"", important:false, targetStores:[], fileName:"", fileData:"", fileType:"" };
  const [form, setForm] = useState(emptyForm);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [fileSizeErr, setFileSizeErr] = useState("");
  const [expandedNotices, setExpandedNotices] = useState({});
  const canEdit = role==="owner";

  // 🔧 安全に myStoreIds を取得
  const myStoreIds = (() => {
    if (role !== "staff" || !activeStaffId) return [];
    const me = staff.find(s => s && s.id === activeStaffId);
    if (!me) return [];
    const ids = me.storeIds || (me.storeId ? [me.storeId] : []);
    return Array.isArray(ids) ? ids.filter(Boolean) : [];
  })();

  // 🔧 安全に visibleNotices を計算（不正なデータでもクラッシュしない）
  const visibleNotices = (Array.isArray(notices) ? notices : []).filter(n => {
    if (!n || typeof n !== "object") return false;
    if (!n.targetStores || !Array.isArray(n.targetStores) || n.targetStores.length === 0) return true;
    if (role === "owner") return true;
    return n.targetStores.some(sid => myStoreIds.includes(sid));
  });

  const toggleStore = (id) => setForm(f=>({
    ...f, targetStores: f.targetStores.includes(id)
      ? f.targetStores.filter(s=>s!==id)
      : [...f.targetStores, id]
  }));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // 3MB制限（base64化で約4MBに膨らむ、スプシセル50,000文字制限考慮）
    if (file.size > 3*1024*1024) {
      setFileSizeErr("ファイルサイズは3MB以下にしてください");
      setTimeout(()=>setFileSizeErr(""), 4000);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(f=>({ ...f, fileName: file.name, fileData: ev.target.result, fileType: file.type }));
    };
    reader.readAsDataURL(file);
  };

  const clearFile = () => setForm(f=>({ ...f, fileName:"", fileData:"", fileType:"" }));

  const save = () => {
    if (!form.title) return;
    if (editingId) {
      // 編集
      setNotices(prev => prev.map(n => n.id===editingId ? { ...n, ...form } : n));
    } else {
      // 新規
      setNotices(prev=>[{ id:`n${Date.now()}`, ...form, date:todayStr() }, ...prev]);
    }
    setForm(emptyForm); setAdding(false); setEditingId(null);
  };

  const startEdit = (n) => {
    setForm({
      title: n.title||"", url: n.url||"", content: n.content||"",
      important: !!n.important, targetStores: n.targetStores||[],
      fileName: n.fileName||"", fileData: n.fileData||"", fileType: n.fileType||""
    });
    setEditingId(n.id);
    setAdding(true);
  };

  const remove = (id) => {
    if (!window.confirm("このお知らせを削除しますか？")) return;
    setNotices(prev => prev.filter(n => n.id !== id));
  };

  const cancelEdit = () => {
    setForm(emptyForm); setAdding(false); setEditingId(null); setFileSizeErr("");
  };

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      {/* 🌸 オーナー：保存ボタン＋未保存状態表示 */}
      {canEdit && (
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
          <button
            onClick={async ()=>{ if(onSave) await onSave(); }}
            disabled={!hasUnsaved || saveStatus==="saving"}
            style={{
              flex:1,
              background: hasUnsaved ? cc.gold : cc.bg2,
              color: hasUnsaved ? cc.bg : cc.muted2,
              border: `1px solid ${hasUnsaved ? cc.gold : cc.border}`,
              borderRadius:10,
              padding:"10px 12px",
              fontSize:13,
              fontWeight:600,
              cursor: hasUnsaved && saveStatus!=="saving" ? "pointer" : "not-allowed",
              transition:"all 0.2s"
            }}>
            {saveStatus==="saving" ? "💾 保存中..." :
             hasUnsaved ? "💾 変更を保存する" :
             "✓ 保存済み"}
          </button>
          {hasUnsaved && (
            <span style={{ fontSize:10, color:cc.gold, whiteSpace:"nowrap" }}>● 未保存</span>
          )}
        </div>
      )}

      {canEdit && !adding && <button onClick={()=>setAdding(true)} style={{ width:"100%",background:cc.bg2,border:`1px dashed ${cc.border2}`,borderRadius:12,padding:12,color:cc.gold,fontSize:12,cursor:"pointer",marginBottom:12 }}>＋ お知らせを追加</button>}
      {adding && (
        <div style={{ ...cardS(role), marginBottom:12 }}>
          <div style={{ fontSize:12,color:cc.gold,marginBottom:10 }}>{editingId?"お知らせを編集":"新しいお知らせ"}</div>
          <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="タイトル（必須）"
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginBottom:8 }}/>
          <input value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} placeholder="URL（任意、https://...）"
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginBottom:8 }}/>
          <textarea value={form.content} onChange={e=>setForm(f=>({...f,content:e.target.value}))} placeholder="本文（任意）"
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginBottom:8,minHeight:60,resize:"vertical",fontFamily:"Jost,sans-serif" }}/>
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:10,color:cc.muted,display:"block",marginBottom:6 }}>添付ファイル（任意・3MBまで）</label>
            {form.fileName ? (
              <div style={{ display:"flex",alignItems:"center",gap:8,background:cc.bg2,border:`1px solid ${cc.border}`,borderRadius:8,padding:"6px 10px" }}>
                <span style={{ fontSize:11,color:cc.cream,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>📎 {form.fileName}</span>
                <button onClick={clearFile} style={{ background:"none",border:"none",color:cc.muted,fontSize:16,cursor:"pointer" }}>×</button>
              </div>
            ) : (
              <input type="file" onChange={handleFile}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                style={{ width:"100%",fontSize:11,color:cc.muted }}/>
            )}
            {fileSizeErr && <div style={{ fontSize:10,color:"#c44",marginTop:4 }}>{fileSizeErr}</div>}
          </div>
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:10,color:cc.muted,display:"block",marginBottom:6 }}>配信先</label>
            <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
              <button onClick={()=>setForm(f=>({...f,targetStores:[]}))} style={{
                background:form.targetStores.length===0?"#3a7a3a":cc.bg2,
                color:form.targetStores.length===0?"#fff":cc.muted,
                border:`1px solid ${form.targetStores.length===0?"#3a7a3a":cc.border}`,
                borderRadius:16,padding:"4px 12px",fontSize:11,cursor:"pointer"
              }}>全店</button>
              {stores.map(st=>(
                <button key={st.id} onClick={()=>toggleStore(st.id)} style={{
                  background:form.targetStores.includes(st.id)?cc.gold:cc.bg2,
                  color:form.targetStores.includes(st.id)?cc.bg:cc.muted,
                  border:`1px solid ${form.targetStores.includes(st.id)?cc.gold:cc.border}`,
                  borderRadius:16,padding:"4px 12px",fontSize:11,cursor:"pointer"
                }}>{st.name}</button>
              ))}
            </div>
          </div>
          <label style={{ display:"flex",alignItems:"center",gap:6,fontSize:12,color:cc.muted,marginBottom:12,cursor:"pointer" }}>
            <input type="checkbox" checked={form.important} onChange={e=>setForm(f=>({...f,important:e.target.checked}))} style={{ accentColor:cc.gold }}/>
            重要なお知らせとしてマーク
          </label>
          <div style={{ display:"flex",gap:8 }}>
            <button onClick={save} style={{ flex:2,background:cc.gold,color:cc.bg,border:"none",borderRadius:8,padding:9,fontSize:12,fontWeight:600,cursor:"pointer" }}>{editingId?"更新":"保存"}</button>
            <button onClick={cancelEdit} style={{ flex:1,background:cc.bg2,color:cc.muted,border:`1px solid ${cc.border}`,borderRadius:8,padding:9,fontSize:12,cursor:"pointer" }}>キャンセル</button>
          </div>
        </div>
      )}
      {visibleNotices.filter(n=>n.important).length>0&&(
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:10,color:cc.accent,letterSpacing:1,marginBottom:8 }}>⚠ 重要なお知らせ</div>
          {visibleNotices.filter(n=>n.important).map(n=>{
            // 🌸 シフト確定通知はコンパクトモード（縦幅小・タップで展開）
            const isShiftConfirm = String(n.title||"").includes("シフト確定");
            const isExpanded = expandedNotices?.[n.id];
            // 投稿時刻表示
            const timeStr = (() => {
              try {
                const d = new Date(n.date);
                if (isNaN(d.getTime())) return n.date;
                return `${d.getMonth()+1}/${d.getDate()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
              } catch(e) { return n.date; }
            })();

            if (isShiftConfirm) {
              return (
                <div key={n.id} style={{ ...cardS(role,{padding:"8px 12px",background:cc.bg2,border:`1px solid ${cc.accent}55`}),marginBottom:6 }}>
                  <div onClick={()=>setExpandedNotices(prev=>({...prev,[n.id]:!prev?.[n.id]}))} style={{ display:"flex",alignItems:"center",gap:8,cursor:"pointer" }}>
                    <span style={{ fontSize:14 }}>📌</span>
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ fontSize:12,color:cc.cream,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{n.title}</div>
                      <div style={{ fontSize:9,color:cc.muted2,marginTop:2 }}>📅 {timeStr}</div>
                    </div>
                    <span style={{ fontSize:11,color:cc.muted2 }}>{isExpanded ? "▲" : "▼"}</span>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop:8,paddingTop:8,borderTop:`1px solid ${cc.border}` }}>
                      {n.content && <div style={{ fontSize:11,color:cc.muted,lineHeight:1.5,whiteSpace:"pre-wrap" }}>{n.content}</div>}
                      {role==="owner"&&(
                        <div style={{ display:"flex",gap:6,marginTop:8 }}>
                          <button onClick={e=>{e.stopPropagation();remove(n.id);}} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"2px 8px",color:"#c44",fontSize:10,cursor:"pointer" }}>削除</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            const handleCardClick = () => { if (n.url) window.open(n.url, "_blank", "noopener,noreferrer"); };
            return (
            <div key={n.id} onClick={handleCardClick} style={{ ...cardS(role,{padding:"12px 14px",background:cc.bg2,border:`1px solid ${cc.accent}55`}),marginBottom:8,cursor:n.url?"pointer":"default" }}>
              <div style={{ display:"flex",alignItems:"flex-start",gap:10 }}>
                <div style={{ fontSize:18,paddingTop:2 }}>📌</div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:13,color:cc.cream,fontWeight:500 }}>{n.title}</div>
                  {n.content && <div style={{ fontSize:11,color:cc.muted,marginTop:4,lineHeight:1.5,whiteSpace:"pre-wrap" }}>{n.content}</div>}
                  {n.url && <div style={{ fontSize:11,color:cc.gold,marginTop:4 }}>🔗 タップで開く</div>}
                  {n.fileData && <a href={n.fileData} download={n.fileName} onClick={e=>e.stopPropagation()} style={{ fontSize:11,color:cc.gold,display:"inline-block",marginTop:4,textDecoration:"none" }}>📎 {n.fileName}</a>}
                  <div style={{ display:"flex",gap:6,marginTop:6,flexWrap:"wrap" }}>
                    <span style={{ fontSize:10,color:cc.muted2 }}>{timeStr}</span>
                    {role==="owner"&&<span style={{ fontSize:9,color:n.targetStores?.length>0?cc.gold:cc.muted2,background:cc.bg2,borderRadius:8,padding:"1px 6px" }}>{n.targetStores?.length>0?n.targetStores.map(id=>stores.find(s=>s.id===id)?.name).filter(Boolean).join('・'):"全店"}</span>}
                  </div>
                </div>
                {canEdit && (
                  <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
                    <button onClick={e=>{e.stopPropagation();startEdit(n);}} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"2px 8px",color:cc.muted,fontSize:10,cursor:"pointer" }}>編集</button>
                    <button onClick={e=>{e.stopPropagation();remove(n.id);}} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"2px 8px",color:"#c44",fontSize:10,cursor:"pointer" }}>削除</button>
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
      )}
      <div style={{ fontSize:10,color:cc.muted,letterSpacing:1,marginBottom:8 }}>お知らせ一覧</div>
      {visibleNotices.filter(n=>!n.important).map(n=>{
        const handleCardClick = () => { if (n.url) window.open(n.url, "_blank", "noopener,noreferrer"); };
        return (
        <div key={n.id} onClick={handleCardClick} style={{ ...cardS(role,{padding:"11px 14px"}),marginBottom:8,cursor:n.url?"pointer":"default" }}>
          <div style={{ display:"flex",alignItems:"flex-start",gap:10 }}>
            <div style={{ fontSize:16,paddingTop:2 }}>🔗</div>
            <div style={{ flex:1,minWidth:0 }}>
              <div style={{ fontSize:12,color:cc.cream }}>{n.title}</div>
              {n.content && <div style={{ fontSize:11,color:cc.muted,marginTop:3,lineHeight:1.5,whiteSpace:"pre-wrap" }}>{n.content}</div>}
              {n.url && <div style={{ fontSize:10,color:cc.gold,marginTop:3 }}>🔗 タップで開く</div>}
              {n.fileData && <a href={n.fileData} download={n.fileName} onClick={e=>e.stopPropagation()} style={{ fontSize:10,color:cc.gold,display:"inline-block",marginTop:3,textDecoration:"none" }}>📎 {n.fileName}</a>}
              <div style={{ display:"flex",gap:6,marginTop:5,flexWrap:"wrap" }}>
                <span style={{ fontSize:9,color:cc.muted2 }}>{n.date}</span>
                {role==="owner"&&<span style={{ fontSize:9,color:n.targetStores?.length>0?cc.gold:cc.muted2,background:cc.bg2,borderRadius:8,padding:"1px 6px" }}>{n.targetStores?.length>0?n.targetStores.map(id=>stores.find(s=>s.id===id)?.name).filter(Boolean).join('・'):"全店"}</span>}
              </div>
            </div>
            {canEdit && (
              <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
                <button onClick={e=>{e.stopPropagation();startEdit(n);}} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"2px 8px",color:cc.muted,fontSize:10,cursor:"pointer" }}>編集</button>
                <button onClick={e=>{e.stopPropagation();remove(n.id);}} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"2px 8px",color:"#c44",fontSize:10,cursor:"pointer" }}>削除</button>
              </div>
            )}
          </div>
        </div>
      );})}
      {visibleNotices.length===0&&<div style={{ textAlign:"center",fontSize:12,color:cc.muted2,padding:24 }}>お知らせがありません</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// THREAD BOARD
// ═══════════════════════════════════════════════════════════════════════════
function ThreadBoard({ threads, setThreads, staff, stores, role, activeStaffId }) {
  const cc = useC(role);
  const [view, setView]     = useState("list");
  const [activeId, setActiveId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newStore, setNewStore] = useState("all");
  const [reply, setReply]   = useState("");
  const [adding, setAdding] = useState(false);
  const bottomRef = useRef(null);
  const authorName = role==="owner"?"オーナー":(staff.find(s=>s.id===activeStaffId)?.name||"スタッフ");

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[activeId,threads]);

  const createThread = () => {
    if (!newTitle) return;
    const t={ id:`t${Date.now()}`, title:newTitle, storeId:newStore, author:authorName, date:todayStr(), replies:[] };
    setThreads(prev=>[t,...prev]); setNewTitle(""); setAdding(false); setActiveId(t.id); setView("thread");
  };
  const sendReply = () => {
    if (!reply.trim()) return;
    setThreads(prev=>prev.map(t=>t.id===activeId?{...t,replies:[...t.replies,{id:`tr${Date.now()}`,author:authorName,text:reply.trim(),date:todayStr()}]}:t));
    setReply("");
  };

  if (view==="thread") {
    const t=threads.find(th=>th.id===activeId);
    if (!t) { setView("list"); return null; }
    return (
      <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 108px)" }}>
        <div style={{ padding:"10px 14px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${cc.border}` }}>
          <button onClick={()=>setView("list")} style={{ background:"none",border:"none",color:cc.gold,fontSize:22,cursor:"pointer",lineHeight:1 }}>‹</button>
          <div><div style={{ fontSize:13,color:cc.cream,fontWeight:500 }}>{t.title}</div>
            <div style={{ fontSize:10,color:cc.muted }}>{t.storeId==="all"?"全店":stores.find(s=>s.id===t.storeId)?.name} · {t.date}</div></div>
        </div>
        <div style={{ flex:1,overflowY:"auto",padding:"12px 14px",display:"flex",flexDirection:"column",gap:10 }}>
          <div style={{ display:"flex",gap:8 }}>
            <div style={{ width:30,height:30,borderRadius:"50%",background:cc.gold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:cc.bg,flexShrink:0 }}>{t.author[0]}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10,color:cc.muted,marginBottom:3 }}>{t.author} · {t.date}</div>
              <div style={{ background:cc.bg2,borderRadius:"4px 14px 14px 14px",padding:"10px 12px",fontSize:13,color:cc.cream,lineHeight:1.5 }}>{t.title}</div>
            </div>
          </div>
          {t.replies.map(r=>{
            const isMe=r.author===authorName;
            return (
              <div key={r.id} style={{ display:"flex",gap:8,flexDirection:isMe?"row-reverse":"row" }}>
                <div style={{ width:28,height:28,borderRadius:"50%",background:isMe?cc.accent:cc.bg3,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:cc.cream,flexShrink:0 }}>{r.author[0]}</div>
                <div style={{ maxWidth:"75%" }}>
                  <div style={{ fontSize:10,color:cc.muted,marginBottom:3,textAlign:isMe?"right":"left" }}>{r.author}</div>
                  <div style={{ background:isMe?`${cc.gold}33`:cc.bg2,borderRadius:isMe?"14px 4px 14px 14px":"4px 14px 14px 14px",padding:"9px 12px",fontSize:13,color:cc.cream,lineHeight:1.5 }}>{r.text}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>
        <div style={{ padding:"10px 14px",borderTop:`1px solid ${cc.border}`,display:"flex",gap:8 }}>
          <input value={reply} onChange={e=>setReply(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); sendReply(); }}}
            placeholder="メッセージを入力…"
            style={{ flex:1,background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:24,padding:"9px 14px",color:cc.cream,fontSize:13 }}/>
          <button onClick={sendReply} style={{ background:cc.gold,border:"none",borderRadius:"50%",width:38,height:38,cursor:"pointer",fontSize:16,color:cc.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>↑</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      {!adding
        ? <button onClick={()=>setAdding(true)} style={{ width:"100%",background:cc.bg2,border:`1px dashed ${cc.border2}`,borderRadius:12,padding:12,color:cc.gold,fontSize:12,cursor:"pointer",marginBottom:12 }}>＋ 新しいスレッドを立てる</button>
        : <div style={{ ...cardS(role), marginBottom:12 }}>
            <div style={{ fontSize:12,color:cc.gold,marginBottom:10 }}>新規スレッド</div>
            <input value={newTitle} onChange={e=>setNewTitle(e.target.value)} placeholder="タイトル・内容"
              style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginBottom:8 }}/>
            <select value={newStore} onChange={e=>setNewStore(e.target.value)}
              style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginBottom:10 }}>
              <option value="all">全店向け</option>
              {stores.map(s=><option key={s.id} value={s.id}>{s.name}向け</option>)}
            </select>
            <div style={{ display:"flex",gap:8 }}>
              <button onClick={createThread} style={{ flex:2,background:cc.gold,color:cc.bg,border:"none",borderRadius:8,padding:9,fontSize:12,fontWeight:600,cursor:"pointer" }}>投稿</button>
              <button onClick={()=>setAdding(false)} style={{ flex:1,background:cc.bg2,color:cc.muted,border:`1px solid ${cc.border}`,borderRadius:8,padding:9,fontSize:12,cursor:"pointer" }}>キャンセル</button>
            </div>
          </div>
      }
      {threads.map(t=>(
        <div key={t.id} onClick={()=>{ setActiveId(t.id); setView("thread"); }} style={{ ...cardS(role, {marginBottom:8,cursor:"pointer"}) }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13,color:cc.cream,fontWeight:500,marginBottom:3 }}>{t.title}</div>
              <div style={{ fontSize:10,color:cc.muted }}>{t.storeId==="all"?"全店":stores.find(s=>s.id===t.storeId)?.name} · {t.author} · {t.date}</div>
            </div>
            <div style={{ background:cc.bg2,borderRadius:10,padding:"2px 8px",fontSize:10,color:cc.muted,flexShrink:0 }}>💬 {t.replies.length}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ORDER TAB
// ═══════════════════════════════════════════════════════════════════════════
function OrderTab({ stores, staff, role, activeStaffId, orders, setOrders, approve, rejectOpen, rejectId, rejectMsg, setRejectMsg, rejectConfirm, setRejectId }) {
  const cc = useC(role);
  const CATEGORIES=["仕入れ","消耗品","備品","その他"];
  const PAY_METHODS=["クレジット","レジ現金","その他"];
  const SUPPLIERS=["TAT","ネイルパートナー","アスクル"];
  const [form,setForm]=useState({ storeId:stores[0]?.id||"",category:"消耗品",supplier:"TAT",supplierCustom:"",payMethod:"クレジット",amount:"",note:"" });
  const [attachFiles,setAttachFiles]=useState([]); // 複数ファイル対応
  const fileRef=useRef(null);
  const [saved,setSaved]=useState(false);
  const [showCustom,setShowCustom]=useState(false);
  const [subTab, setSubTab] = useState("pending"); // "pending" or "delivered"
  const [deliverModal, setDeliverModal] = useState(null); // 納品モーダルのorderId
  const [deliverForm, setDeliverForm] = useState({ deliveredDate: todayStr(), deliveredFiles: [] });
  const deliverFileRef = useRef(null);

  const authorName=role==="owner"?"長尾":(staff.find(s=>s.id===activeStaffId)?.name||"スタッフ");
  const canApprove=role==="owner";

  // ファイル選択（複数）
  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.size <= 3*1024*1024); // 3MB制限
    if (valid.length < files.length) alert("3MB以下のファイルのみ追加されました");
    // 既存と合わせて最大5枚まで
    const newFiles = [...attachFiles, ...valid].slice(0, 5);
    if (attachFiles.length + valid.length > 5) alert("添付は最大5枚までです");

    // base64化
    Promise.all(newFiles.slice(attachFiles.length).map(f => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = () => res({ name: f.name, data: reader.result });
      reader.readAsDataURL(f);
    }))).then(loaded => {
      setAttachFiles([...attachFiles, ...loaded]);
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (idx) => {
    setAttachFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if(!form.amount)return;
    const supplier=showCustom?form.supplierCustom:form.supplier;

    // 🌸 編集モード時は既存の発注を更新
    if (form._editingId) {
      const updated = {
        ...orders.find(o=>o.id===form._editingId),
        ...form, supplier,
        attachments: attachFiles,
      };
      delete updated._editingId;
      setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
      const result = await gasPostWithRetry("saveOrders", {
        orders: [{ ...updated, storeName: stores.find(s=>s.id===updated.storeId)?.name||"" }]
      });
      if (result?.success) {
        setForm(f=>({...f,amount:"",note:"",_editingId:null}));
        setAttachFiles([]);
        if(fileRef.current)fileRef.current.value="";
        setSaved(true);setTimeout(()=>setSaved(false),2000);
      } else {
        alert(`⚠️ 更新失敗：${result?.error||"通信エラー"}`);
      }
      return;
    }

    const newOrder = {
      id:`o${Date.now()}`,
      ...form, supplier,
      date:todayStr(), author:authorName, status:"申請中",
      attachments: attachFiles,
      delivered: false,
      deliveredDate: "",
      deliveredFiles: [],
    };
    setOrders(prev=>[newOrder, ...prev]);

    // 🌸 リトライ付き保存
    const result = await gasPostWithRetry("saveOrders", {
      orders: [{ ...newOrder, storeName: stores.find(s=>s.id===newOrder.storeId)?.name||"" }]
    });

    if (result?.success) {
      setForm(f=>({...f,amount:"",note:""}));
      setAttachFiles([]);
      if(fileRef.current)fileRef.current.value="";
      setSaved(true);setTimeout(()=>setSaved(false),2000);
    } else {
      alert(`⚠️ 発注申請の保存に失敗しました：\n${result?.error||"通信エラー"}\n\nもう一度「発注申請する」ボタンを押してください。`);
    }
  };

  // 🌸 編集モード開始
  const startEditOrder = (o) => {
    setForm({
      storeId: o.storeId,
      category: o.category,
      supplier: o.supplier,
      supplierCustom: SUPPLIERS.includes(o.supplier) ? "" : o.supplier,
      payMethod: o.payMethod,
      amount: o.amount,
      note: o.note || "",
      _editingId: o.id,
    });
    setShowCustom(!SUPPLIERS.includes(o.supplier));
    setAttachFiles(o.attachments || []);
    window.scrollTo({top:0, behavior:"smooth"});
  };

  // 🌸 取り消し
  const cancelOrder = async (id) => {
    if (!window.confirm("この発注申請を取り消しますか？")) return;
    setOrders(prev => prev.filter(o => o.id !== id));
    await gasPostWithRetry("deleteOrder", { id });
  };

  // 納品処理
  const onDeliverFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.size <= 3*1024*1024);
    if (valid.length < files.length) alert("3MB以下のファイルのみ追加されました");
    Promise.all(valid.map(f => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = () => res({ name: f.name, data: reader.result });
      reader.readAsDataURL(f);
    }))).then(loaded => {
      const newFiles = [...deliverForm.deliveredFiles, ...loaded].slice(0, 5);
      setDeliverForm(f => ({ ...f, deliveredFiles: newFiles }));
    });
    if (deliverFileRef.current) deliverFileRef.current.value = "";
  };

  const markDelivered = (orderId) => {
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      delivered: true,
      deliveredDate: deliverForm.deliveredDate,
      deliveredFiles: deliverForm.deliveredFiles,
    } : o));
    const target = orders.find(o => o.id === orderId);
    if (target && typeof gasPost === "function") {
      gasPost("saveOrders", { orders: [{
        ...target,
        delivered: true,
        deliveredDate: deliverForm.deliveredDate,
        deliveredFiles: deliverForm.deliveredFiles,
        storeName: stores.find(s=>s.id===target.storeId)?.name||"",
      }]});
    }
    setDeliverModal(null);
    setDeliverForm({ deliveredDate: todayStr(), deliveredFiles: [] });
  };

  const undoDelivered = (orderId) => {
    if (!window.confirm("納品済みを取り消しますか？")) return;
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o, delivered: false, deliveredDate: "", deliveredFiles: [],
    } : o));
    const target = orders.find(o => o.id === orderId);
    if (target && typeof gasPost === "function") {
      gasPost("saveOrders", { orders: [{
        ...target, delivered: false, deliveredDate: "", deliveredFiles: [],
        storeName: stores.find(s=>s.id===target.storeId)?.name||"",
      }]});
    }
  };

  // フィルター
  const pendingOrders = orders.filter(o => !o.delivered);
  const deliveredOrders = orders.filter(o => o.delivered);
  const showOrders = subTab === "delivered" ? deliveredOrders : pendingOrders;

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <div style={{ ...cardS(role), marginBottom:12 }}>
        <div style={{ fontSize:12,color:cc.gold,fontWeight:600,marginBottom:12 }}>発注申請</div>
        <div style={{ marginBottom:8 }}>
          <label style={{ fontSize:10,color:cc.muted }}>店舗</label>
          <select value={form.storeId} onChange={e=>setForm(f=>({...f,storeId:e.target.value}))}
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginTop:3 }}>
            {stores.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom:8 }}>
          <label style={{ fontSize:10,color:cc.muted }}>科目</label>
          <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginTop:4 }}>
            {CATEGORIES.map(c=>(
              <button key={c} onClick={()=>setForm(f=>({...f,category:c}))} style={{
                background:form.category===c?cc.gold:cc.bg2,color:form.category===c?cc.bg:cc.muted,
                border:`1px solid ${form.category===c?cc.gold:cc.border}`,borderRadius:16,padding:"5px 13px",fontSize:12,cursor:"pointer"
              }}>{c}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom:8 }}>
          <label style={{ fontSize:10,color:cc.muted }}>発注先</label>
          <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginTop:4,marginBottom:showCustom?6:0 }}>
            {SUPPLIERS.map(s=>(
              <button key={s} onClick={()=>{ setForm(f=>({...f,supplier:s}));setShowCustom(false); }} style={{
                background:(!showCustom&&form.supplier===s)?cc.gold:cc.bg2,color:(!showCustom&&form.supplier===s)?cc.bg:cc.muted,
                border:`1px solid ${(!showCustom&&form.supplier===s)?cc.gold:cc.border}`,borderRadius:16,padding:"5px 13px",fontSize:12,cursor:"pointer"
              }}>{s}</button>
            ))}
            <button onClick={()=>setShowCustom(true)} style={{ background:showCustom?cc.gold:cc.bg2,color:showCustom?cc.bg:cc.muted,border:`1px solid ${showCustom?cc.gold:cc.border}`,borderRadius:16,padding:"5px 13px",fontSize:12,cursor:"pointer" }}>テキスト入力</button>
          </div>
          {showCustom&&<input value={form.supplierCustom} onChange={e=>setForm(f=>({...f,supplierCustom:e.target.value}))} placeholder="発注先を入力"
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12 }}/>}
        </div>
        <div style={{ marginBottom:8 }}>
          <label style={{ fontSize:10,color:cc.muted }}>支払い方法</label>
          <div style={{ display:"flex",gap:6,marginTop:4 }}>
            {PAY_METHODS.map(p=>(
              <button key={p} onClick={()=>setForm(f=>({...f,payMethod:p}))} style={{
                background:form.payMethod===p?cc.gold:cc.bg2,color:form.payMethod===p?cc.bg:cc.muted,
                border:`1px solid ${form.payMethod===p?cc.gold:cc.border}`,borderRadius:16,padding:"5px 13px",fontSize:12,cursor:"pointer"
              }}>{p}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:10,color:cc.muted }}>金額（円）</label>
          <input type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="例: 5000"
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginTop:3 }}/>
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:10,color:cc.muted }}>備考</label>
          <input value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} placeholder="品名・詳細など"
            style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginTop:3 }}/>
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:10,color:cc.muted,display:"block",marginBottom:4 }}>添付ファイル（任意・複数枚OK・各3MBまで・最大5枚）</label>
          <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
            <button onClick={()=>fileRef.current?.click()} style={{
              background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,
              padding:"7px 14px",color:cc.muted,fontSize:11,cursor:"pointer",whiteSpace:"nowrap"
            }}>📎 ファイルを追加</button>
            <span style={{ fontSize:11,color:cc.muted2 }}>{attachFiles.length>0 ? `${attachFiles.length}枚選択中` : "未選択"}</span>
          </div>
          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" style={{ display:"none" }} onChange={onFilePick}/>
          {attachFiles.length > 0 && (
            <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:4 }}>
              {attachFiles.map((f, idx) => (
                <div key={idx} style={{ display:"flex",alignItems:"center",gap:6,background:cc.bg2,borderRadius:6,padding:"4px 8px" }}>
                  <span style={{ fontSize:11,color:cc.gold,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>📎 {f.name}</span>
                  <button onClick={()=>removeFile(idx)} style={{ background:"none",border:"none",color:cc.muted2,fontSize:14,cursor:"pointer",padding:0 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {form._editingId && (
          <div style={{ background:`${cc.gold}22`, border:`1px solid ${cc.gold}55`, borderRadius:8, padding:"6px 10px", fontSize:11, color:cc.gold, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span>✏️ 編集中（申請中の発注を更新します）</span>
            <button onClick={()=>{ setForm({ storeId:stores[0]?.id||"", category:CATEGORIES[0], supplier:SUPPLIERS[0], supplierCustom:"", payMethod:PAY_METHODS[0], amount:"", note:"", _editingId:null }); setShowCustom(false); setAttachFiles([]); }} style={{ background:"none",border:`1px solid ${cc.border2}`,borderRadius:6,padding:"2px 8px",color:cc.muted,fontSize:10,cursor:"pointer" }}>キャンセル</button>
          </div>
        )}
        <button onClick={submit} style={{ width:"100%",background:saved?"#4a7a5a":cc.gold,color:cc.bg,border:"none",borderRadius:10,padding:11,fontSize:13,fontWeight:600,cursor:"pointer",transition:"background 0.3s" }}>
          {saved?(form._editingId?"✓ 更新しました":"✓ 申請しました"):(form._editingId?"更新する":"発注申請する")}
        </button>
      </div>
      <div style={cardS(role)}>
        {/* サブタブ */}
        <div style={{ display:"flex", gap:6, marginBottom:14 }}>
          <button onClick={()=>setSubTab("pending")} style={{
            flex:1, padding:"8px 12px", borderRadius:8, border:"none",
            background: subTab==="pending" ? cc.gold : cc.bg2,
            color: subTab==="pending" ? cc.bg : cc.muted,
            fontSize:12, fontWeight:600, cursor:"pointer"
          }}>📦 申請中／承認済 ({pendingOrders.length})</button>
          <button onClick={()=>setSubTab("delivered")} style={{
            flex:1, padding:"8px 12px", borderRadius:8, border:"none",
            background: subTab==="delivered" ? cc.gold : cc.bg2,
            color: subTab==="delivered" ? cc.bg : cc.muted,
            fontSize:12, fontWeight:600, cursor:"pointer"
          }}>✅ 納品済み ({deliveredOrders.length})</button>
        </div>

        <div style={{ fontSize:11,color:cc.muted,marginBottom:12 }}>
          {subTab==="pending" ? "申請中・承認済の発注" : "納品済みの発注"}（{showOrders.length}件）
        </div>
        {showOrders.length===0
          ?<div style={{ textAlign:"center",fontSize:12,color:cc.muted2,padding:12 }}>{subTab==="pending"?"まだ申請がありません":"納品済みの発注はまだありません"}</div>
          :showOrders.map(o=>{
            const st=stores.find(s=>s.id===o.storeId);
            // 旧形式（fileName/fileURL）と新形式（attachments）両対応
            const attachList = (o.attachments && o.attachments.length>0) ? o.attachments
              : (o.fileName ? [{name:o.fileName, data:o.fileURL}] : []);
            return (
              <div key={o.id} style={{ padding:"10px 0",borderBottom:`1px solid ${cc.border}` }}>
                <div style={{ display:"flex",alignItems:"flex-start",gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex",gap:6,alignItems:"center",marginBottom:2,flexWrap:"wrap" }}>
                      <span style={{ fontSize:10,background:cc.bg2,color:cc.gold,borderRadius:10,padding:"1px 7px" }}>{o.category}</span>
                      <span style={{ fontSize:12,color:cc.cream }}>{o.supplier}</span>
                      <span style={{ fontSize:11,color:cc.muted2 }}>{o.payMethod}</span>
                    </div>
                    <div style={{ fontSize:13,color:cc.gold,fontWeight:600,marginBottom:2 }}>{fmt(o.amount||0)}</div>
                    <div style={{ fontSize:10,color:cc.muted2 }}>{st?.name}｜{o.author}｜申請日 {o.date}{o.note?`｜${o.note}`:""}</div>
                    {/* 申請時の添付（複数対応） */}
                    {attachList.length>0 && (
                      <div style={{ marginTop:4, display:"flex", flexWrap:"wrap", gap:4 }}>
                        {attachList.map((f,i)=>(
                          <a key={i} href={f.data} download={f.name} style={{ fontSize:10,color:cc.gold,background:cc.bg2,borderRadius:6,padding:"2px 8px",textDecoration:"none" }}>📎 {f.name}</a>
                        ))}
                      </div>
                    )}
                    {/* 納品済み情報 */}
                    {o.delivered && (
                      <div style={{ marginTop:6, padding:"6px 10px", background:cc.bg2, borderRadius:6, borderLeft:`3px solid ${cc.green||"#7ba87b"}` }}>
                        <div style={{ fontSize:10, color:cc.muted, marginBottom:2 }}>✅ 納品日：{o.deliveredDate}</div>
                        {(o.deliveredFiles||[]).length>0 && (
                          <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                            {o.deliveredFiles.map((f,i)=>(
                              <a key={i} href={f.data} download={f.name} style={{ fontSize:10,color:cc.gold,textDecoration:"none" }}>📎 {f.name}</a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0 }}>
                    {canApprove && o.status==="申請中" ? (
                      <div style={{ display:"flex",gap:4 }}>
                        <button onClick={()=>approve(o.id)} style={{ background:"#2a4a3a",color:cc.green,border:`1px solid ${cc.green}`,borderRadius:10,padding:"3px 10px",fontSize:10,cursor:"pointer" }}>承認</button>
                        <button onClick={()=>rejectOpen(o.id)} style={{ background:"#3a1a14",color:cc.red,border:`1px solid ${cc.red}`,borderRadius:10,padding:"3px 10px",fontSize:10,cursor:"pointer" }}>差し戻し</button>
                      </div>
                    ) : (
                      <span style={{ fontSize:10,
                        color:o.status==="承認済"?cc.green:o.status==="差し戻し"?cc.red:cc.gold,
                        background:cc.bg2,borderRadius:12,padding:"3px 8px" }}>{o.status}</span>
                    )}
                    {/* 🌸 申請中の発注は本人 or オーナーが編集・取消可能 */}
                    {o.status==="申請中" && (role==="owner" || o.author===authorName) && (
                      <div style={{ display:"flex",gap:4,marginTop:2 }}>
                        <button onClick={()=>startEditOrder(o)} style={{ background:cc.bg2,color:cc.muted,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"2px 8px",fontSize:9,cursor:"pointer" }}>✏️ 編集</button>
                        <button onClick={()=>cancelOrder(o.id)} style={{ background:cc.bg2,color:"#c44",border:`1px solid ${cc.border2}`,borderRadius:8,padding:"2px 8px",fontSize:9,cursor:"pointer" }}>🗑 取消</button>
                      </div>
                    )}
                    {o.status==="差し戻し"&&o.rejectMsg&&<span style={{ fontSize:9,color:cc.red,maxWidth:120,textAlign:"right" }}>💬 {o.rejectMsg}</span>}
                    {/* 納品ボタン：承認済 かつ 未納品 */}
                    {o.status==="承認済" && !o.delivered && (
                      <button onClick={()=>{ setDeliverModal(o.id); setDeliverForm({deliveredDate:todayStr(), deliveredFiles:[]}); }}
                        style={{ background:cc.gold,color:cc.bg,border:"none",borderRadius:10,padding:"4px 10px",fontSize:10,cursor:"pointer",fontWeight:600 }}>📥 納品済みにする</button>
                    )}
                    {o.delivered && canApprove && (
                      <button onClick={()=>undoDelivered(o.id)} style={{ background:"none",border:"none",color:cc.muted2,fontSize:9,cursor:"pointer" }}>納品取消</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        }
      </div>

      {/* 納品モーダル */}
      {deliverModal && (
        <div onClick={()=>setDeliverModal(null)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:cc.bg1,border:`1px solid ${cc.border}`,borderRadius:12,padding:20,maxWidth:380,width:"100%",maxHeight:"80vh",overflow:"auto" }}>
            <div style={{ fontSize:14,fontWeight:600,color:cc.gold,marginBottom:14 }}>📥 納品の記録</div>

            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:10,color:cc.muted }}>納品日</label>
              <input type="date" value={deliverForm.deliveredDate} onChange={e=>setDeliverForm(f=>({...f,deliveredDate:e.target.value}))}
                style={{ width:"100%",background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,padding:"8px 10px",color:cc.cream,fontSize:12,marginTop:3 }}/>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:10,color:cc.muted,display:"block",marginBottom:4 }}>添付ファイル（任意・最大5枚）</label>
              <button onClick={()=>deliverFileRef.current?.click()} style={{
                background:cc.bg2,border:`1px solid ${cc.border2}`,borderRadius:8,
                padding:"7px 14px",color:cc.muted,fontSize:11,cursor:"pointer"
              }}>📎 ファイルを追加</button>
              <input ref={deliverFileRef} type="file" multiple accept="image/*,application/pdf" style={{ display:"none" }} onChange={onDeliverFilePick}/>
              {deliverForm.deliveredFiles.length > 0 && (
                <div style={{ marginTop:6, display:"flex", flexDirection:"column", gap:4 }}>
                  {deliverForm.deliveredFiles.map((f, idx) => (
                    <div key={idx} style={{ display:"flex",alignItems:"center",gap:6,background:cc.bg2,borderRadius:6,padding:"4px 8px" }}>
                      <span style={{ fontSize:11,color:cc.gold,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>📎 {f.name}</span>
                      <button onClick={()=>setDeliverForm(fm=>({...fm,deliveredFiles:fm.deliveredFiles.filter((_,i)=>i!==idx)}))} style={{ background:"none",border:"none",color:cc.muted2,fontSize:14,cursor:"pointer",padding:0 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display:"flex",gap:8 }}>
              <button onClick={()=>setDeliverModal(null)} style={{ flex:1,background:cc.bg2,color:cc.muted,border:`1px solid ${cc.border}`,borderRadius:8,padding:10,fontSize:12,cursor:"pointer" }}>キャンセル</button>
              <button onClick={()=>markDelivered(deliverModal)} style={{ flex:2,background:cc.gold,color:cc.bg,border:"none",borderRadius:8,padding:10,fontSize:12,fontWeight:600,cursor:"pointer" }}>納品済みにする</button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MANAGER VIEW — FCオーナー用 閲覧専用ページ
// ═══════════════════════════════════════════════════════════════════════════
function ManagerView({ token, managerTokens, stores, staff, records, storeSales, expenses, cashRegisterOuts, confirmedShifts }) {
  const [subTab, setSubTab] = useState("overview");
  const [period, setPeriod] = useState("month");

  // トークン検証
  const tokenData = (managerTokens||[]).find(t => t.token === token && t.active);
  if (!tokenData) {
    return (
      <div style={{ minHeight:"100vh", background:"#faf7f2", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
        <div style={{ maxWidth:400, background:"#fff", border:"1px solid rgba(184,152,120,0.4)", borderRadius:12, padding:30, textAlign:"center", boxShadow:"0 4px 12px rgba(184,152,120,0.1)" }}>
          <div style={{ fontSize:40, marginBottom:10 }}>🔒</div>
          <div style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:"#9c7e5d", marginBottom:8 }}>アクセスできません</div>
          <div style={{ fontSize:12, color:"#8a7a68", lineHeight:1.7 }}>このURLは無効化されているか、正しくありません。<br/>オーナーにお問い合わせください。</div>
        </div>
      </div>
    );
  }

  const store = stores.find(s => s.id === tokenData.storeId);
  if (!store) {
    return <div style={{ padding:30, textAlign:"center", color:"#8a7a68" }}>店舗情報が見つかりません</div>;
  }

  // 当店舗のスタッフ
  const storeStaff = staff.filter(s => s.storeId === store.id);
  // 期間の計算
  const now = new Date();
  const curYM = nowYM();
  const prevDate = new Date(now.getFullYear(), now.getMonth()-1, 1);
  const prevYM = `${prevDate.getFullYear()}-${String(prevDate.getMonth()+1).padStart(2,"0")}`;
  const targetYM = period === "prev" ? prevYM : curYM;

  // 当店舗のデータ
  const storeStaffIds = storeStaff.map(s => s.id);
  const storeRecords = records.filter(r => storeStaffIds.includes(r.staffId) && ym(r.date) === targetYM);
  const storeSalesData = storeSales.filter(s => s.storeId === store.id && ym(s.date) === targetYM);
  const storeExpenses = (expenses||[]).filter(e => storeStaffIds.includes(e.staffId) && ym(e.date) === targetYM);
  const storeCashOuts = (cashRegisterOuts||[]).filter(c => c.storeId === store.id && ym(c.date) === targetYM);

  // 当月の総売上
  const monthTotal = storeSalesData.reduce((a,s) => a + (s.totalSales || 0), 0);
  const target = store.targetNormal || 0;
  const achieveRate = target > 0 ? Math.round(monthTotal / target * 100) : 0;

  // KPI
  const totalGuests = storeSalesData.reduce((a,s) => a + (s.newGuests||0) + (s.regularGuests||0), 0);
  const newGuests = storeSalesData.reduce((a,s) => a + (s.newGuests||0), 0);
  const avgPrice = totalGuests > 0 ? Math.round(monthTotal/totalGuests) : 0;
  const repeatRate = totalGuests > 0 ? Math.round((totalGuests-newGuests)/totalGuests*100) : 0;

  // スタッフ別売上
  const staffStats = storeStaff.map(s => {
    const srecs = storeRecords.filter(r => r.staffId === s.id);
    const sales = srecs.reduce((a,r) => a+r.amount, 0);
    const guests = srecs.reduce((a,r) => a+(r.totalGuests||0), 0);
    const shimei = srecs.reduce((a,r) => a+(r.shimei||0), 0);
    return { ...s, sales, guests, shimei };
  }).sort((a,b) => b.sales - a.sales);

  // 給与計算
  const salaryList = storeStaff.map(s => {
    const srecs = storeRecords.filter(r => r.staffId === s.id);
    const sales = srecs.reduce((a,r) => a+r.amount, 0);
    const shimei = srecs.reduce((a,r) => a+(r.shimei||0), 0);
    const commission = s.rate > 0 ? Math.round(sales*(s.rate/100)) : calcCommission(sales, store);
    const rate = s.rate > 0 ? s.rate : getCommissionRate(sales, store);
    const shimeiBonus = calcShimeiBonus(shimei);
    const goalBonus = (s.target > 0 && sales >= s.target) ? (s.shiftType === "regular" ? 10000 : 5000) : 0;
    const total = (s.baseSalary||0) + commission + shimeiBonus + goalBonus;
    return { ...s, sales, commission, rate, shimeiBonus, goalBonus, total };
  });
  const salaryTotal = salaryList.reduce((a,s)=>a+s.total, 0);

  // 経費合計
  const expenseTotal = storeExpenses.reduce((a,e)=>a+(e.amount||0), 0);

  // 歩合ティア
  const storeTiers = getStoreTiers(store);
  const sortedTiers = [...storeTiers].sort((a,b)=>a.taxEx-b.taxEx);

  // CSV出力
  const exportCSV = (type) => {
    const [yy, mm] = targetYM.split("-");
    let rows = [];
    let filename = "";
    if (type === "sales") {
      rows.push(["日付","総売上","現金","クレジット","ポイント","その他","新規客数","顧客数"]);
      storeSalesData.forEach(s => {
        rows.push([s.date, s.totalSales||0, s.cashSales||0, s.creditSales||0, s.pointSales||0, s.otherSales||0, s.newGuests||0, s.regularGuests||0]);
      });
      filename = `店舗売上_${store.name}_${targetYM}.csv`;
    } else if (type === "staffSales") {
      rows.push(["順位","氏名","売上","客数","指名数"]);
      staffStats.forEach((s,i) => rows.push([i+1, s.name, s.sales, s.guests, s.shimei]));
      filename = `スタッフ売上_${store.name}_${targetYM}.csv`;
    } else if (type === "salary") {
      rows.push(["氏名","雇用区分","基本給","歩合率","歩合額","指名料","達成金","支給総額"]);
      salaryList.forEach(s => rows.push([s.name, s.shiftType==="regular"?"正社員":"パート", s.baseSalary||0, `${s.rate}%`, s.commission, s.shimeiBonus, s.goalBonus, s.total]));
      filename = `給与明細_${store.name}_${targetYM}.csv`;
    } else if (type === "expense") {
      rows.push(["日付","申請者","金額","メモ","ファイル名","添付日"]);
      storeExpenses.forEach(e => {
        const sName = staff.find(x=>x.id===e.staffId)?.name||"";
        rows.push([e.date, sName, e.amount||0, e.memo||"", e.fileName||"", e.uploadedAt?.split(" ")[0]||""]);
      });
      filename = `経費明細_${store.name}_${targetYM}.csv`;
    } else if (type === "cashout") {
      rows.push(["日付","金額","概要","入力者"]);
      storeCashOuts.forEach(c => rows.push([c.date, c.amount||0, c.memo||"", c.inputBy||""]));
      filename = `レジ出金_${store.name}_${targetYM}.csv`;
    }
    downloadCSV(filename, rows);
  };

  // カラーパレット（ライトベージュ）
  const mc = {
    bg: "#faf7f2", bg1: "#ffffff", bg2: "#f5f0e8", bg3: "#ebe3d5",
    cream: "#fffdf9", text: "#3d2b1c", text2: "#6b5847",
    gold: "#b89878", goldDeep: "#9c7e5d", goldSoft: "#d4b894",
    border: "rgba(184, 152, 120, 0.22)", border2: "rgba(184, 152, 120, 0.4)",
    muted: "#8a7a68", muted2: "#a89684", accent: "#c88a6a",
    success: "#7ba87b",
  };

  const tabsM = [
    { id: "overview", label: "📊 概要" },
    { id: "shift",    label: "🗓 シフト" },
    { id: "salary",   label: "💰 給与明細" },
    { id: "expense",  label: "🧾 経費" },
    { id: "settings", label: "⚙️ 店舗設定" },
    { id: "tier",     label: "💯 歩合ティア" },
  ];

  return (
    <div style={{ minHeight:"100vh", background:mc.bg, maxWidth:500, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ padding:"18px 18px 14px", borderBottom:`1px solid ${mc.border}`, background:`linear-gradient(180deg, ${mc.cream}, ${mc.bg})`, position:"sticky", top:0, zIndex:10 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <div style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:mc.goldDeep, letterSpacing:1.5 }}>Worksplus</div>
          <div style={{ background:`linear-gradient(135deg, ${mc.gold}, ${mc.goldDeep})`, color:"#fff", fontSize:10, fontWeight:600, padding:"4px 10px", borderRadius:20, letterSpacing:1 }}>管理者</div>
        </div>
        <div style={{ fontSize:16, color:mc.text, fontWeight:500 }}>🏪 {store.name}</div>
        <div style={{ fontSize:11, color:mc.muted, marginTop:2 }}>FCオーナー：{tokenData.ownerName} 様</div>
      </div>

      {/* Read-only banner */}
      <div style={{ background:`linear-gradient(90deg, rgba(184,152,120,0.12), rgba(184,152,120,0.04))`, border:`1px solid ${mc.border2}`, borderRadius:10, padding:"10px 14px", margin:"14px 18px 0", fontSize:10, color:mc.goldDeep, display:"flex", alignItems:"center", gap:8 }}>
        <span>🛡️ このページは<strong>閲覧専用</strong>です。データの編集はできません。</span>
      </div>

      {/* Nav */}
      <div style={{ display:"flex", gap:4, padding:"12px 12px 0", overflowX:"auto" }}>
        {tabsM.map(t => (
          <button key={t.id} onClick={()=>setSubTab(t.id)} style={{
            padding:"8px 14px", background: subTab===t.id ? mc.gold : mc.bg1,
            border:`1px solid ${subTab===t.id ? mc.gold : mc.border}`,
            borderRadius:20, color: subTab===t.id ? "#fff" : mc.muted,
            fontSize:11, cursor:"pointer", whiteSpace:"nowrap", fontWeight: subTab===t.id?600:400, flexShrink:0,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ===== OVERVIEW ===== */}
      {subTab === "overview" && (
        <>
          {/* CSV Export */}
          <div style={{ background:`linear-gradient(135deg, ${mc.cream}, ${mc.bg2})`, border:`1px solid ${mc.border2}`, borderRadius:12, padding:14, margin:"14px 18px 0" }}>
            <div style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600, marginBottom:8 }}>📥 CSVエクスポート</div>
            <div style={{ fontSize:10, color:mc.muted, marginBottom:10 }}>会計処理・確定申告用にデータをダウンロードできます</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
              {[
                ["sales", "店舗売上（日別）"],
                ["staffSales", "スタッフ売上"],
                ["salary", "給与明細"],
                ["expense", "経費明細"],
                ["cashout", "レジ出金"],
              ].map(([key,lbl]) => (
                <button key={key} onClick={()=>exportCSV(key)} style={{
                  background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:8,
                  padding:"10px 8px", fontSize:11, color:mc.text, cursor:"pointer", fontWeight:500,
                }}>📥 {lbl}</button>
              ))}
            </div>
          </div>

          {/* Period tabs */}
          <div style={{ display:"flex", gap:6, padding:"12px 18px 0" }}>
            {[["month","今月"],["prev","先月"]].map(([k,l]) => (
              <button key={k} onClick={()=>setPeriod(k)} style={{
                flex:1, padding:"8px 0", background: period===k ? mc.gold : mc.bg1,
                border:`1px solid ${period===k ? mc.gold : mc.border}`, borderRadius:8,
                color: period===k ? "#fff" : mc.muted, fontSize:11, cursor:"pointer",
                fontWeight: period===k?600:400,
              }}>{l}</button>
            ))}
          </div>

          {/* Main sales */}
          <div style={{ padding:"14px 18px" }}>
            <div style={{ background:`linear-gradient(135deg, ${mc.cream}, ${mc.bg2})`, border:`1px solid ${mc.border2}`, borderRadius:12, padding:16, marginBottom:12 }}>
              <div style={{ fontSize:10, color:mc.muted, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>{period==="month"?"今月":"先月"}の売上</div>
              <div style={{ fontFamily:"Cormorant Garamond", fontSize:34, color:mc.goldDeep, fontWeight:500, lineHeight:1 }}>{fmt(monthTotal)}</div>
              {target > 0 && (
                <>
                  <div style={{ display:"inline-flex", alignItems:"center", gap:4, background:"rgba(123,168,123,0.15)", color: achieveRate>=100?mc.success:mc.muted, padding:"3px 8px", borderRadius:12, fontSize:10, fontWeight:600, marginTop:8 }}>
                    {achieveRate>=100 ? "✓" : ""} 目標達成率 {achieveRate}%
                  </div>
                  <div style={{ background:mc.bg3, height:6, borderRadius:3, overflow:"hidden", marginTop:10 }}>
                    <div style={{ height:"100%", width:`${Math.min(100,achieveRate)}%`, background:`linear-gradient(90deg, ${mc.gold}, ${mc.goldSoft})` }}/>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginTop:10, paddingTop:10, borderTop:`1px solid ${mc.border}` }}>
                    <div>
                      <div style={{ fontSize:9, color:mc.muted2, letterSpacing:1 }}>月間目標</div>
                      <div style={{ fontSize:14, color:mc.text, fontWeight:500 }}>{fmt(target)}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:9, color:mc.muted2, letterSpacing:1 }}>税抜売上</div>
                      <div style={{ fontSize:14, color:mc.text, fontWeight:500 }}>{fmt(Math.round(monthTotal/TAX_RATE))}</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* KPI grid */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                ["来店客数", `${totalGuests}`, "名"],
                ["新規客数", `${newGuests}`, "名"],
                ["客単価", `${fmt(avgPrice)}`, ""],
                ["リピート率", `${repeatRate}`, "%"],
              ].map(([lbl,val,unit]) => (
                <div key={lbl} style={{ background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:10, padding:12 }}>
                  <div style={{ fontSize:10, color:mc.muted, marginBottom:4 }}>{lbl}</div>
                  <div style={{ fontFamily:"Cormorant Garamond", fontSize:20, color:mc.goldDeep, fontWeight:500 }}>
                    {val}<span style={{ fontSize:11, color:mc.muted2, marginLeft:3 }}>{unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Staff Ranking */}
          <div style={{ padding:"0 18px 14px" }}>
            <div style={{ background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:12, padding:16 }}>
              <div style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600, marginBottom:10 }}>STAFF RANKING</div>
              {staffStats.length === 0 ? (
                <div style={{ fontSize:11, color:mc.muted2, textAlign:"center", padding:16 }}>データなし</div>
              ) : staffStats.map((s,i) => (
                <div key={s.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom: i<staffStats.length-1?`1px solid ${mc.border}`:"none" }}>
                  <div style={{ width:24, height:24, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:600,
                    background: i===0?`linear-gradient(135deg, #e8c994, ${mc.gold})` : i===1?"#c0c0c0" : i===2?"#c19a7a" : mc.bg2,
                    color: i<3?"#fff":mc.muted }}>{i+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, color:mc.text }}>{s.name}</div>
                    <div style={{ fontSize:9, color:mc.muted2, marginTop:1 }}>客数 {s.guests}名 ／ 指名 {s.shimei}件</div>
                  </div>
                  <div style={{ fontFamily:"Cormorant Garamond", fontSize:16, color:mc.goldDeep, fontWeight:500 }}>{fmt(s.sales)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ===== SHIFT ===== */}
      {subTab === "shift" && (
        <div style={{ padding:"14px 18px" }}>
          <div style={{ background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:12, padding:16 }}>
            <div style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600, marginBottom:10 }}>SHIFT CALENDAR ({curYM})</div>
            <ShiftCalendarView confirmedShifts={confirmedShifts} store={store} staff={staff} mc={mc}/>
          </div>
        </div>
      )}

      {/* ===== SALARY ===== */}
      {subTab === "salary" && (
        <div style={{ padding:"14px 18px" }}>
          <div style={{ background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:12, padding:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <span style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600 }}>STAFF SALARY ({targetYM})</span>
              <button onClick={()=>exportCSV("salary")} style={{ background:mc.bg1, border:`1px solid ${mc.border2}`, borderRadius:20, padding:"4px 12px", fontSize:10, color:mc.goldDeep, cursor:"pointer" }}>📥 CSV</button>
            </div>
            {salaryList.map(s => (
              <div key={s.id} style={{ padding:12, background:mc.bg2, borderRadius:10, marginBottom:8, borderLeft:`3px solid ${mc.gold}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:13, color:mc.text, fontWeight:500 }}>{s.name}（{s.shiftType==="regular"?"正社員":"パート"}）</span>
                  <span style={{ fontFamily:"Cormorant Garamond", fontSize:18, color:mc.goldDeep, fontWeight:500 }}>{fmt(s.total)}</span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, fontSize:10, color:mc.text2 }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span>基本給</span><span style={{ color:mc.text, fontWeight:500 }}>{fmt(s.baseSalary||0)}</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span>歩合（{s.rate}%）</span><span style={{ color:mc.text, fontWeight:500 }}>{fmt(s.commission)}</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span>指名料</span><span style={{ color:mc.text, fontWeight:500 }}>{fmt(s.shimeiBonus)}</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span>達成金</span><span style={{ color:mc.text, fontWeight:500 }}>{fmt(s.goalBonus)}</span></div>
                </div>
              </div>
            ))}
            <div style={{ background:mc.bg3, borderRadius:8, padding:12, marginTop:10, display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
              <span style={{ fontSize:11, color:mc.muted, letterSpacing:1 }}>支給総額（見込）</span>
              <span style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:mc.goldDeep, fontWeight:600 }}>{fmt(salaryTotal)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ===== EXPENSE ===== */}
      {subTab === "expense" && (
        <div style={{ padding:"14px 18px" }}>
          <div style={{ background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:12, padding:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <span style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600 }}>EXPENSE ({targetYM})</span>
              <button onClick={()=>exportCSV("expense")} style={{ background:mc.bg1, border:`1px solid ${mc.border2}`, borderRadius:20, padding:"4px 12px", fontSize:10, color:mc.goldDeep, cursor:"pointer" }}>📥 CSV</button>
            </div>
            <div style={{ background:mc.bg2, borderRadius:8, padding:12, marginBottom:10, display:"flex", justifyContent:"space-between" }}>
              <span style={{ fontSize:11, color:mc.muted }}>今月の経費合計</span>
              <span style={{ fontFamily:"Cormorant Garamond", fontSize:20, color:mc.goldDeep, fontWeight:500 }}>{fmt(expenseTotal)}</span>
            </div>
            {storeExpenses.length === 0 ? (
              <div style={{ fontSize:11, color:mc.muted2, textAlign:"center", padding:16 }}>経費申請がありません</div>
            ) : storeExpenses.map(e => {
              const s = staff.find(x=>x.id===e.staffId);
              return (
                <div key={e.id} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${mc.border}` }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10, color:mc.muted }}>{e.date}</div>
                    <div style={{ fontSize:12, color:mc.text, marginTop:2 }}>{e.memo}</div>
                    <div style={{ fontSize:9, color:mc.muted2, marginTop:2 }}>申請者：{s?.name||"-"}</div>
                    {e.fileName && <div style={{ fontSize:9, color:mc.gold, marginTop:2 }}>📎 {e.fileName} ・添付日 {e.uploadedAt?.split(" ")[0]||""}</div>}
                  </div>
                  <div style={{ fontFamily:"Cormorant Garamond", fontSize:15, color:mc.goldDeep, fontWeight:500 }}>{fmt(e.amount)}</div>
                </div>
              );
            })}

            {/* レジ出金 */}
            {storeCashOuts.length > 0 && (
              <>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:20, marginBottom:8 }}>
                  <span style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600 }}>CASH OUT ({targetYM})</span>
                  <button onClick={()=>exportCSV("cashout")} style={{ background:mc.bg1, border:`1px solid ${mc.border2}`, borderRadius:20, padding:"4px 12px", fontSize:10, color:mc.goldDeep, cursor:"pointer" }}>📥 CSV</button>
                </div>
                {storeCashOuts.map(c => (
                  <div key={c.id} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${mc.border}` }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:10, color:mc.muted }}>{c.date}</div>
                      <div style={{ fontSize:12, color:mc.text, marginTop:2 }}>{c.memo}</div>
                    </div>
                    <div style={{ fontFamily:"Cormorant Garamond", fontSize:15, color:mc.accent, fontWeight:500 }}>− {fmt(c.amount)}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== SETTINGS ===== */}
      {subTab === "settings" && (
        <div style={{ padding:"14px 18px" }}>
          <div style={{ background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:12, padding:16 }}>
            <div style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600, marginBottom:10 }}>STORE SETTINGS</div>
            <div style={{ fontSize:10, color:mc.muted, marginBottom:10 }}>💡 店舗の運営設定です。変更はオーナーのみ可能です。</div>
            {[
              ["店舗名", store.name],
              ["月間目標（通常期）", fmt(store.targetNormal||0)],
              ["月間目標（繁忙期）", fmt(store.targetBusy||0)],
              ["平日日割目標", fmt(store.weekdayTarget||0)],
              ["週末日割目標", fmt(store.weekendTarget||0)],
              ["定休日", ["日","月","火","水","木","金","土"].filter((_,i) => (store.shiftClosedDays||[]).includes(i)).join("、") || "なし"],
              ["平日シフト人数", `${store.shiftWeekdayMin||1}〜${store.shiftWeekdayMax||2}名`],
              ["週末シフト人数", `${store.shiftWeekendMin||2}〜${store.shiftWeekendMax||3}名`],
            ].map(([lbl,val]) => (
              <div key={lbl} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${mc.border}`, fontSize:12 }}>
                <span style={{ color:mc.muted }}>{lbl}</span>
                <span style={{ color:mc.text, fontWeight:500 }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== TIER ===== */}
      {subTab === "tier" && (
        <div style={{ padding:"14px 18px" }}>
          <div style={{ background:mc.bg1, border:`1px solid ${mc.border}`, borderRadius:12, padding:16 }}>
            <div style={{ fontSize:11, color:mc.goldDeep, letterSpacing:2, fontWeight:600, marginBottom:10 }}>COMMISSION TIER</div>
            <div style={{ fontSize:10, color:mc.muted, marginBottom:14 }}>💡 税抜売上で判定する歩合給のティア</div>

            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:`1px solid ${mc.border}` }}>
              <div style={{ width:36, height:36, borderRadius:"50%", background:mc.bg2, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:mc.muted }}>-</div>
              <div style={{ flex:1, fontSize:12, color:mc.text }}>
                <div>〜{Math.round((sortedTiers[0]?.taxEx||0)/10000-1)}万円（税抜）</div>
                <div style={{ fontSize:9, color:mc.muted2, marginTop:1 }}>歩合なし</div>
              </div>
            </div>
            {sortedTiers.map(t => (
              <div key={t.taxEx} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:`1px solid ${mc.border}` }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:`linear-gradient(135deg, ${mc.gold}, ${mc.goldDeep})`, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700 }}>{t.rate}%</div>
                <div style={{ flex:1, fontSize:12, color:mc.text }}>
                  <div>{Math.round(t.taxEx/10000)}万円以上（税込 {fmt(Math.round(t.taxEx*TAX_RATE))}以上）</div>
                  <div style={{ fontSize:9, color:mc.muted2, marginTop:1 }}>→ 売上 × {t.rate}%</div>
                </div>
              </div>
            ))}

            <div style={{ background:`linear-gradient(135deg, ${mc.cream}, ${mc.bg2})`, borderRadius:10, padding:12, marginTop:14, fontSize:11, color:mc.text2, lineHeight:1.6 }}>
              💡 <strong>指名料：</strong>10本以上で全本数×¥500<br/>
              💡 <strong>目標達成金：</strong>正社員¥10,000 ／ パート・その他¥5,000
            </div>
          </div>
        </div>
      )}

      <div style={{ textAlign:"center", padding:20, fontSize:10, color:mc.muted2, borderTop:`1px solid ${mc.border}`, marginTop:20 }}>
        <div style={{ width:40, height:1, background:mc.border2, margin:"0 auto 10px" }}/>
        <div>Worksplus Salon Manager</div>
        <div style={{ marginTop:4 }}>管理者ページ（閲覧専用）</div>
        <div style={{ marginTop:8, fontSize:9 }}>このURLは{tokenData.ownerName}様専用です。共有にはご注意ください。</div>
      </div>
    </div>
  );
}

// シフトカレンダー表示コンポーネント
function ShiftCalendarView({ confirmedShifts, store, staff, mc }) {
  const ym = nowYM();
  const [yy, mm] = ym.split("-").map(Number);
  const firstDay = new Date(yy, mm-1, 1).getDay();
  const daysInMonth = new Date(yy, mm, 0).getDate();
  const today = todayStr();

  const storeShift = (confirmedShifts||[]).find(cs => cs.yearMonth === ym && cs.storeId === store.id);
  const shifts = storeShift?.shifts || {};

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${yy}-${String(mm).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    const dayShift = shifts[dateStr] || [];
    const names = dayShift.map(sid => staff.find(s=>s.id===sid)?.name?.split(" ")[0]||"").filter(Boolean).join("・");
    cells.push({ date: d, dateStr, names, dow: new Date(yy, mm-1, d).getDay(), isToday: dateStr === today });
  }

  const dowLabels = ["日","月","火","水","木","金","土"];

  return (
    <>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:2 }}>
        {dowLabels.map((l,i) => (
          <div key={l} style={{ textAlign:"center", fontSize:9, color: i===0?"#c47060" : i===6?"#6b8ac4" : mc.muted, padding:"4px 0", fontWeight:500 }}>{l}</div>
        ))}
        {cells.map((c,i) => {
          if (!c) return <div key={i}/>;
          return (
            <div key={i} style={{
              aspectRatio:"1", borderRadius:6, padding:3,
              background: c.isToday ? `linear-gradient(135deg, #fff4e6, #fde5c9)` : (c.dow===0 ? "#fdf2ef" : mc.bg2),
              boxShadow: c.isToday ? `0 0 0 1.5px ${mc.gold}` : "none",
              display:"flex", flexDirection:"column",
            }}>
              <div style={{ fontSize:10, fontWeight:600, color: c.dow===0?"#c47060" : c.dow===6?"#6b8ac4" : mc.text }}>{c.date}</div>
              <div style={{ fontSize:7, color:mc.goldDeep, fontWeight:500, lineHeight:1.1, overflow:"hidden" }}>{c.names}</div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MANAGER URL GENERATOR — オーナー画面でFCオーナー用URLを発行
// ═══════════════════════════════════════════════════════════════════════════
function ManagerURLGenerator({ managerTokens, setManagerTokens, stores }) {
  const [form, setForm] = useState({ storeId: stores[0]?.id || "", ownerName: "" });
  const [saved, setSaved] = useState(false);

  const generate = () => {
    if (!form.storeId || !form.ownerName) return;
    const token = generateToken();
    const newRecord = {
      token,
      storeId: form.storeId,
      ownerName: form.ownerName,
      active: true,
      createdAt: new Date().toLocaleString("ja-JP"),
    };
    setManagerTokens(prev => [newRecord, ...prev]);
    if (typeof gasPost === "function") {
      gasPost("saveManagerTokens", { records: [{ ...newRecord, storeName: stores.find(s=>s.id===newRecord.storeId)?.name||"" }] });
    }
    setForm({ storeId: stores[0]?.id || "", ownerName: "" });
    setSaved(true); setTimeout(()=>setSaved(false), 2000);
  };

  const toggleActive = (token) => {
    const target = managerTokens.find(t => t.token === token);
    if (!target) return;
    const newActive = !target.active;
    if (!window.confirm(newActive ? "このURLを再有効化しますか？" : "このURLを無効化しますか？\n（再有効化は一覧から可能です）")) return;
    setManagerTokens(prev => prev.map(t => t.token === token ? { ...t, active: newActive } : t));
    if (typeof gasPost === "function") {
      gasPost("updateManagerToken", { token, active: newActive });
    }
  };

  const remove = (token) => {
    if (!window.confirm("このURLを完全に削除しますか？この操作は取り消せません。")) return;
    setManagerTokens(prev => prev.filter(t => t.token !== token));
    if (typeof gasPost === "function") {
      gasPost("deleteManagerToken", { token });
    }
  };

  const copyURL = (token) => {
    const url = `${window.location.origin}/?manager=${token}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(()=>alert("URLをコピーしました:\n" + url));
    } else {
      prompt("URLをコピーしてください:", url);
    }
  };

  return (
    <div style={{ padding:"14px 14px 32px" }}>
      <div style={{ ...card({marginBottom:12}) }}>
        <div style={{ fontSize:12, color:C.gold, fontWeight:600, marginBottom:12 }}>🔗 FCオーナー用 管理者URL発行</div>
        <div style={{ background:C.bg2, borderRadius:8, padding:"7px 10px", marginBottom:10, fontSize:10, color:C.muted, lineHeight:1.5 }}>
          💡 FCオーナー専用の閲覧URLを発行します。このURLからアクセスすると、その店舗のデータを閲覧できます（編集不可）。
        </div>

        <div style={{ marginBottom:10 }}>
          <label style={{ fontSize:10, color:C.muted }}>店舗を選択</label>
          <select value={form.storeId} onChange={e=>setForm(f=>({...f,storeId:e.target.value}))}
            style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 10px", color:C.cream, fontSize:12, marginTop:3 }}>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:10, color:C.muted }}>FCオーナー名</label>
          <input type="text" value={form.ownerName} onChange={e=>setForm(f=>({...f,ownerName:e.target.value}))}
            placeholder="例: 田中"
            style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 10px", color:C.cream, fontSize:12, marginTop:3 }}/>
        </div>

        <button onClick={generate} disabled={!form.storeId||!form.ownerName}
          style={{ width:"100%", background:saved?"#4a7a5a":(!form.storeId||!form.ownerName)?C.bg2:C.gold, color:saved?"#fff":(!form.storeId||!form.ownerName)?C.muted:C.bg, border:"none", borderRadius:10, padding:11, fontSize:13, fontWeight:600, cursor:(!form.storeId||!form.ownerName)?"not-allowed":"pointer", transition:"background 0.3s" }}>
          {saved?"✓ URLを発行しました":"🔗 URLを発行する"}
        </button>
      </div>

      <div style={card({marginBottom:12})}>
        <div style={{ fontSize:11, color:C.muted, marginBottom:10 }}>発行済みURL一覧（{(managerTokens||[]).length}件）</div>
        {(managerTokens||[]).length === 0 ? (
          <div style={{ fontSize:11, color:C.muted2, textAlign:"center", padding:16 }}>まだURLは発行されていません</div>
        ) : (managerTokens||[]).map(t => {
          const storeName = stores.find(s=>s.id===t.storeId)?.name || "-";
          const url = `${window.location.origin}/?manager=${t.token}`;
          return (
            <div key={t.token} style={{ padding:"12px 0", borderBottom:`1px solid ${C.border}`, opacity: t.active ? 1 : 0.5 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
                <span style={{ fontSize:13, color:C.cream, fontWeight:500 }}>{storeName} ／ {t.ownerName}様</span>
                <span style={{ fontSize:9, color: t.active ? "#4a7a5a" : C.accent, background: t.active ? "rgba(74,122,90,0.15)" : "rgba(212,120,107,0.15)", padding:"2px 8px", borderRadius:10 }}>
                  {t.active ? "✓ 有効" : "✕ 無効"}
                </span>
              </div>
              <div style={{ fontSize:10, color:C.muted2, marginBottom:6, wordBreak:"break-all" }}>{url}</div>
              <div style={{ fontSize:9, color:C.muted2, marginBottom:6 }}>発行日: {t.createdAt}</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <button onClick={()=>copyURL(t.token)} style={{ background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:6, padding:"4px 10px", fontSize:10, color:C.gold, cursor:"pointer" }}>📋 URLコピー</button>
                <button onClick={()=>toggleActive(t.token)} style={{ background:C.bg2, border:`1px solid ${C.border2}`, borderRadius:6, padding:"4px 10px", fontSize:10, color: t.active ? C.accent : "#4a7a5a", cursor:"pointer" }}>
                  {t.active ? "🔒 無効化" : "🔓 再有効化"}
                </button>
                <button onClick={()=>remove(t.token)} style={{ background:"none", border:"none", padding:"4px 10px", fontSize:10, color:C.muted2, cursor:"pointer" }}>削除</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════════════════════════════════════
export default function App() {
  const [role, setRole]               = useState(null);
  const [activeStaffId, setActiveStaffId] = useState(null);
  const [records, setRecords]         = useState(genRecords());
  const [staff, setStaff]             = useState(STAFF_SEED);
  const [stores, setStores]           = useState(STORES_SEED);
  const [storeSales, setStoreSales]   = useState([]);
  const [orders, setOrders]           = useState(ORDERS_SEED);
  const [shiftRequests, setShiftRequests] = useState([]);
  const [confirmedShifts, setConfirmedShifts] = useState({});
  const [shiftComments, setShiftComments] = useState(() => {
    try {
      const saved = localStorage.getItem("worksplus_shift_comments");
      return saved ? JSON.parse(saved) : {};
    } catch (e) { return {}; }
  });
  // shiftComments を localStorage に自動保存
  useEffect(() => {
    try { localStorage.setItem("worksplus_shift_comments", JSON.stringify(shiftComments)); } catch(e){}
  }, [shiftComments]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [rejectId, setRejectId]       = useState(null);
  const [rejectMsg, setRejectMsg]     = useState("");
  const approve = id => {
    setOrders(prev => {
      const updated = prev.map(o=>o.id===id?{...o,status:"承認済",rejectMsg:""}:o);
      const target = updated.find(o => o.id === id);
      if (target && typeof gasPost === "function") {
        gasPost("saveOrders", { orders: [{ ...target, storeName: stores.find(s=>s.id===target.storeId)?.name||"" }] });
      }
      return updated;
    });
  };
  const rejectOpen  = id => { setRejectId(id); setRejectMsg(""); };
  const rejectConfirm = () => {
    if (!rejectMsg.trim()) return;
    setOrders(prev => {
      const updated = prev.map(o=>o.id===rejectId?{...o,status:"差し戻し",rejectMsg}:o);
      const target = updated.find(o => o.id === rejectId);
      if (target && typeof gasPost === "function") {
        gasPost("saveOrders", { orders: [{ ...target, storeName: stores.find(s=>s.id===target.storeId)?.name||"" }] });
      }
      return updated;
    });
    setRejectId(null); setRejectMsg("");
  };
  const [notices, setNotices]         = useState(NOTICES_SEED);
  const [threads, setThreads]         = useState(THREADS_SEED);
  const [expenses, setExpenses]       = useState([]);
  const [cashRegisterOuts, setCashRegisterOuts] = useState([]); // レジ出金履歴
  const [managerTokens, setManagerTokens] = useState([]); // FCオーナー用URLトークン
  const [readStates, setReadStates]   = useState({}); // 既読状態（バッジ用）
  const [tab, setTab]                 = useState("dashboard");

  const TABS = {
    staff: [{id:"dashboard",label:"ダッシュボード"},{id:"input",label:"個人売上"},{id:"storeinput",label:"店舗売上"},{id:"myshift",label:"📅 今月のシフト"},{id:"salary",label:"給与確認"},{id:"shift",label:"🗓 希望休"},{id:"notice",label:"📌 重要連絡"},{id:"order",label:"発注申請"}],
    owner: [{id:"dashboard",label:"ダッシュボード"},{id:"saleslist",label:"売上一覧"},{id:"staffsales",label:"スタッフ売上"},{id:"shiftmgr",label:"📅 シフト管理"},{id:"shift",label:"🗓 希望休管理"},{id:"notice",label:"📌 重要連絡"},{id:"order",label:"発注申請"},{id:"managerurl",label:"🔗 管理者URL"},{id:"staffmaster",label:"スタッフ管理"},{id:"storemaster",label:"店舗設定"},{id:"payroll",label:"給与計算"}],
    clerk: [{id:"saleslist",label:"📋 売上一覧（タップで修正）"},{id:"staffsales",label:"📊 スタッフ売上"}],
  };

  // ヘッダー色
  const hc = useC(role);
  const headerBg    = (role==="owner"||role==="clerk") ? "#3a2414" : hc.bg1;
  const headerBorder= (role==="owner"||role==="clerk") ? "#7a502855" : hc.border;
  const logoColor   = (role==="owner"||role==="clerk") ? "#d4a866"  : hc.gold;
  const tabActiveBorder = (role==="owner"||role==="clerk") ? "#d4a866" : hc.gold;
  const tabActiveColor  = (role==="owner"||role==="clerk") ? "#d4a866" : hc.gold;
  const tabMutedColor   = role==="staff" ? hc.muted : C.muted;
  const bodyBg          = role==="staff" ? hc.bg : C.bg;

  const logout = () => { setRole(null); setActiveStaffId(null); setTab("dashboard"); document.body.classList.remove("staff-mode"); };

  // Apply body class for staff light theme
  useEffect(()=>{
    if (role==="staff") document.body.classList.add("staff-mode");
    else document.body.classList.remove("staff-mode");
    return () => document.body.classList.remove("staff-mode");
  },[role]);

  // GASからデータ読み込み（初回）
  useEffect(()=>{
    gasLoad().then(res => {
      console.log("📥 gasLoad raw response:", res);
      if (!res) { console.warn("⚠️ gasLoad returned null/undefined"); return; }
      const d = res.data || res; // 後方互換
      console.log("📊 staffSales count:", d?.staffSales?.length || 0);
      console.log("📊 storeSales count:", d?.storeSales?.length || 0);
      console.log("📊 staffSales sample:", d?.staffSales?.[0]);
      console.log("📊 storeSales sample:", d?.storeSales?.[0]);
        // 🔧 日付・キーを確実に文字列化するヘルパー
        // 🔧 日付を確実にJSTの YYYY-MM-DD 文字列に変換（UTC→JST補正含む）
        const toDateStr = (v) => {
          if (!v) return "";
          let d;
          if (v instanceof Date) {
            d = v;
          } else {
            const s = String(v);
            // ISO形式（T含む）はDate型に変換してJST補正
            if (s.includes("T")) {
              d = new Date(s);
              if (isNaN(d.getTime())) return s.split("T")[0];
            } else {
              // すでに YYYY-MM-DD 形式ならそのまま
              const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
              if (m) return `${m[1]}-${String(m[2]).padStart(2,"0")}-${String(m[3]).padStart(2,"0")}`;
              return s.slice(0,10);
            }
          }
          // JSTで取得（UTC+9時間）
          const jst = new Date(d.getTime() + 9*60*60*1000);
          return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth()+1).padStart(2,"0")}-${String(jst.getUTCDate()).padStart(2,"0")}`;
        };

        if (d.staffSales?.length) {
          const allRecs = d.staffSales
            .filter(r => r["ID"])
            .map(r => ({
              id: String(r["ID"]),
              date: toDateStr(r["日付"]),
              staffId: String(r["スタッフID"]||""),
              storeId: String(r["店舗ID"]||""),
              amount: Number(r["総売上"])||0,
              newGuests: Number(r["新規客数"])||0,
              regularGuests: Number(r["顧客数"])||0,
              totalGuests: Number(r["総人数"])||0,
              nextReserve: Number(r["次回予約数"])||0,
              shimei: Number(r["指名数"])||0
            }));
          // GAS優先：完全置換（保存反映が確実に画面に出るように）
          if (allRecs.length > 0) setRecords(allRecs);
        }
        if (d.storeSales?.length) {
          const allRecs = d.storeSales
            .filter(s => s["キー"])
            .map(s => ({
              key: String(s["キー"]),
              date: toDateStr(s["日付"]),
              storeId: String(s["店舗ID"]||""),
              newGuests: Number(s["新規客数"])||0,
              regularGuests: Number(s["顧客数"])||0,
              totalGuests: Number(s["総人数"])||0,
              totalSales: Number(s["総売上"])||0,
              cashSales: Number(s["現金売上"])||0,
              creditSales: Number(s["クレジット売上"])||0,
              pointSales: Number(s["ポイント売上"])||0,
              otherSales: Number(s["その他売上"])||0,
              nextReserves: Number(s["次回予約数"])||0,
              register: Number(s["レジ残高"])||10000,
              inputBy: String(s["入力者"]||"")
            }));
          // GAS優先：完全置換
          if (allRecs.length > 0) setStoreSales(allRecs);
        }
        if (d.orders?.length) {
          // GAS優先で完全置換（編集・納品・取消が正しく反映されるように）
          const loaded = d.orders
            .filter(o => o["ID"])
            .map(o => {
              // 添付ファイルJSONをパース
              let attachments = [];
              try {
                const raw = o["添付ファイル"];
                if (raw && String(raw).trim()) {
                  const parsed = JSON.parse(String(raw));
                  if (Array.isArray(parsed)) attachments = parsed;
                }
              } catch(e) { attachments = []; }
              let deliveredFiles = [];
              try {
                const raw = o["納品添付"];
                if (raw && String(raw).trim()) {
                  const parsed = JSON.parse(String(raw));
                  if (Array.isArray(parsed)) deliveredFiles = parsed;
                }
              } catch(e) { deliveredFiles = []; }
              return {
                id: o["ID"],
                date: String(o["日付"]||"").split("T")[0],
                storeId: o["店舗ID"],
                category: o["科目"],
                supplier: o["発注先"],
                payMethod: o["支払方法"],
                amount: Number(o["金額"])||0,
                note: o["備考"]||"",
                author: o["申請者"],
                status: o["ステータス"]||"申請中",
                attachments,
                delivered: o["納品済"]===true || o["納品済"]==="TRUE" || o["納品済"]==="true",
                deliveredDate: String(o["納品日"]||"").split("T")[0],
                deliveredFiles,
              };
            });
          if (loaded.length > 0) setOrders(loaded);
        }
        if (d.shiftRequests?.length) {
          // GAS優先で完全置換（編集・追加が正しく反映されるように）
          const loaded = d.shiftRequests
            .filter(r => r["ID"])
            .map(r => {
              // 月の値が日付セル形式の場合に対応（YYYY-MM形式に統一）
              let monthVal = r["年月"] || r["月"] || "";
              if (monthVal) {
                const s = String(monthVal);
                // ISO形式 (2026-06-01T...) や 日付形式 (Sun Jun 01 2026...) を YYYY-MM に変換
                if (s.includes("T") || s.match(/^\d{4}-\d{2}-\d{2}/) || s.match(/[A-Z][a-z]{2}\s/)) {
                  const d = new Date(s);
                  if (!isNaN(d.getTime())) {
                    monthVal = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                  }
                } else {
                  monthVal = s.slice(0,7); // 既に YYYY-MM 形式
                }
              }
              const parseDate = (d) => {
                const s = String(d).trim();
                if (s.includes("T") || s.match(/[A-Z][a-z]{2}\s/)) {
                  const dt = new Date(s);
                  if (!isNaN(dt.getTime())) {
                    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
                  }
                }
                return s.slice(0,10);
              };
              const noReqRaw = r["希望なし"];
              return {
                id: r["ID"],
                staffId: r["スタッフID"],
                staffName: r["スタッフ名"],
                storeId: r["店舗ID"],
                month: monthVal,
                dates: String(r["希望休日（カンマ区切り）"]||"").split(",").filter(Boolean).map(parseDate),
                paidLeaveDates: String(r["有給日（カンマ区切り）"]||"").split(",").filter(Boolean).map(parseDate),
                noRequest: noReqRaw === true || noReqRaw === "TRUE" || noReqRaw === "true",
                note: r["備考"]||"",
                submittedAt: r["申請日"]||""
              };
            });
          if (loaded.length > 0) setShiftRequests(loaded);
        }
        // 確定シフトの読み込み
        if (d.confirmedShifts?.length) {
          const shifts = {};
          // JST想定でタイムゾーン補正（UTC → JST）
          const normDate = (v) => {
            if (!v) return "";
            let date;
            if (v instanceof Date) {
              date = v;
            } else {
              const s = String(v);
              if (s.includes("T")) {
                date = new Date(s);
              } else {
                const m = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
                if (m) return `${m[1]}-${String(m[2]).padStart(2,"0")}-${String(m[3]).padStart(2,"0")}`;
                return s;
              }
            }
            // UTC日時に9時間加算してJSTとして取得
            const jst = new Date(date.getTime() + 9*60*60*1000);
            return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth()+1).padStart(2,"0")}-${String(jst.getUTCDate()).padStart(2,"0")}`;
          };
          const normYM = (v) => {
            if (!v) return "";
            let date;
            if (v instanceof Date) {
              date = v;
            } else {
              const s = String(v);
              if (s.includes("T")) {
                date = new Date(s);
              } else {
                const m = s.match(/(\d{4})[-/](\d{1,2})/);
                if (m) return `${m[1]}-${String(m[2]).padStart(2,"0")}`;
                return s;
              }
            }
            const jst = new Date(date.getTime() + 9*60*60*1000);
            return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth()+1).padStart(2,"0")}`;
          };
          d.confirmedShifts.forEach(row => {
            const ym = normYM(row["年月"]);
            const sid = String(row["店舗ID"]||"").trim();
            const ds = normDate(row["日付"]);
            const staffId = String(row["スタッフID"]||"").trim();
            if (!ym || !sid || !ds || !staffId) return;
            if (!shifts[ym]) shifts[ym] = {};
            if (!shifts[ym][sid]) shifts[ym][sid] = {};
            if (!shifts[ym][sid][ds]) shifts[ym][sid][ds] = [];
            // 🔧 厳格な重複防止：staffIDを文字列化して比較
            const exists = shifts[ym][sid][ds].some(w => String(w.id||"").trim() === staffId);
            if (exists) return;
            shifts[ym][sid][ds].push({
              id: staffId,
              name: String(row["スタッフ名"]||"").trim(),
              slot: String(row["スロット"]||"").trim()
            });
          });
          console.log("Loaded confirmedShifts keys:", Object.keys(shifts));
          setConfirmedShifts(shifts);
        }
        // スタッフマスターの読み込み（マージ方式 - 既存データを保持しつつシート優先）
        if (d.staffMaster?.length) {
          try {
            const sheetStaff = d.staffMaster.filter(r => r["ID"] && r["氏名"]).map(r => {
              const storeIdsArr = (r["店舗IDリスト"]||"").toString().split(",").filter(Boolean);
              const slotsArr = (r["対応スロット"]||"").toString().split("").filter(Boolean);
              const fixedDaysArr = (r["固定曜日"]||"").toString().split(",").filter(x=>x!=="").map(Number);
              return {
                id: r["ID"], name: r["氏名"], storeId: r["店舗ID"],
                // storeIds は複数店舗勤務者だけ（空配列はフォールバックを壊すので含めない）
                ...(storeIdsArr.length > 0 ? { storeIds: storeIdsArr } : {}),
                baseSalary: Number(r["基本給"])||0, rate: Number(r["歩合率"])||0,
                target: Number(r["月間目標"])||0, contractHours: Number(r["月間契約時間"])||0,
                pw: String(r["ログインPW"]||"0000"), email: r["メール"]||"",
                joinDate: String(r["入社日"]||"").split("T")[0],
                shiftMaxDays: Number(r["シフトMAX日数"])||21,
                shiftType: r["勤務タイプ"]||"regular",
                shiftSlots: slotsArr,
                shiftFixedDays: fixedDaysArr,
                shiftNote: r["備考"]||"",
                paidLeaveAnnual: Number(r["年間付与日数"])||0,
                paidLeaveInitial: Number(r["初回有給残数"])||0,
              };
            });
            if (sheetStaff.length > 0) {
              setStaff(prev => {
                const merged = [...prev];
                sheetStaff.forEach(ss => {
                  const idx = merged.findIndex(s=>s.id===ss.id);
                  if (idx>=0) {
                    // 既存データと統合。空のstoreIdsはマージで消す
                    const existing = {...merged[idx]};
                    if (!ss.storeIds) delete existing.storeIds;
                    merged[idx] = {...existing, ...ss};
                  } else {
                    merged.push(ss);
                  }
                });
                return merged;
              });
            }
          } catch(e) { console.error("staff load error:", e); }
        }
        // 店舗マスターの読み込み（マージ方式）
        if (d.storeMaster?.length) {
          try {
            const sheetStores = d.storeMaster.filter(r => r["ID"] && r["店舗名"]).map(r => {
              // 歩合ティアのJSON文字列を配列に復元
              let tiers = [];
              try {
                const raw = r["歩合ティア"];
                if (raw && String(raw).trim()) {
                  const parsed = JSON.parse(String(raw));
                  if (Array.isArray(parsed)) tiers = parsed.filter(t=>t.taxEx>0&&t.rate>0);
                }
              } catch(e) { tiers = []; }
              return {
                id: r["ID"], name: r["店舗名"],
                targetBusy: Number(r["繁忙目標"])||0, targetNormal: Number(r["通常目標"])||0,
                weekdayTarget: Number(r["平日日割"])||0, weekendTarget: Number(r["週末日割"])||0,
                weekdayTargetBusy: Number(r["平日日割_繁忙"])||0, weekendTargetBusy: Number(r["週末日割_繁忙"])||0,
                shiftClosedDays: (r["定休日"]||"").toString().split(",").filter(x=>x!=="").map(Number),
                irregularClosedDates: (r["不定期定休日"]||"").toString().split(",").map(x=>x.trim()).filter(Boolean),
                shiftWeekdayMin: Number(r["平日最低"])||1, shiftWeekdayMax: Number(r["平日MAX"])||2,
                shiftWeekendMin: Number(r["週末最低"])||2, shiftWeekendMax: Number(r["週末MAX"])||2,
                shiftSchedule: String(r["勤務スケジュール"]||""),
                commissionTiers: tiers,
              };
            });
            if (sheetStores.length > 0) {
              setStores(prev => {
                const merged = [...prev];
                sheetStores.forEach(ss => {
                  const idx = merged.findIndex(s=>s.id===ss.id);
                  if (idx>=0) merged[idx] = {...merged[idx], ...ss};
                  else merged.push(ss);
                });
                return merged;
              });
            }
          } catch(e) { console.error("store load error:", e); }
        }
        // 重要連絡の読み込み（マージ方式・編集時は更新）
        if (d.notices?.length) {
          try {
            const loaded = d.notices.filter(r => r["ID"]).map(r => ({
              id: r["ID"],
              date: String(r["日付"]||"").split("T")[0] || new Date().toISOString().slice(0,10),
              title: r["タイトル"]||"",
              url: r["URL"]||"",
              content: r["本文"]||"",
              important: r["重要フラグ"]==="TRUE" || r["重要フラグ"]===true,
              targetStores: (r["対象店舗IDs"]||"").toString().split(",").filter(Boolean),
              fileName: r["ファイル名"]||"",
              fileData: r["ファイルデータ"]||"",
              fileType: r["ファイル形式"]||"",
            }));
            if (loaded.length > 0) {
              // スプシ優先で完全置き換え（編集・削除が正しく反映されるように）
              setNotices(loaded);
            }
          } catch(e) { console.error("notices load error:", e); }
        }
        // 経費精算の読み込み
        if (d.expenses?.length) {
          try {
            const loaded = d.expenses.filter(r => r["ID"]).map(r => ({
              id: r["ID"],
              staffId: r["スタッフID"] || "",
              date: String(r["日付"]||"").split("T")[0] || "",
              amount: Number(r["金額"])||0,
              memo: r["メモ"]||"",
              fileName: r["ファイル名"]||"",
              fileData: r["ファイルデータ"]||"",
              uploadedAt: r["添付日時"]||"",
              inputBy: r["入力者"]||"",
              inputAt: r["登録日時"]||"",
            }));
            if (loaded.length > 0) setExpenses(loaded);
          } catch(e) { console.error("expenses load error:", e); }
        }
        // レジ出金の読み込み
        if (d.cashRegisterOuts?.length) {
          try {
            const loaded = d.cashRegisterOuts.filter(r => r["ID"]).map(r => ({
              id: r["ID"],
              storeId: r["店舗ID"] || "",
              date: String(r["日付"]||"").split("T")[0] || "",
              amount: Number(r["金額"])||0,
              memo: r["概要"]||"",
              inputBy: r["入力者"]||"",
              inputAt: r["登録日時"]||"",
            }));
            if (loaded.length > 0) setCashRegisterOuts(loaded);
          } catch(e) { console.error("cashRegisterOuts load error:", e); }
        }
        // 管理者トークンの読み込み
        if (d.managerTokens?.length) {
          try {
            const loaded = d.managerTokens.filter(r => r["トークン"]).map(r => ({
              token: r["トークン"],
              storeId: r["店舗ID"] || "",
              ownerName: r["FCオーナー名"] || "",
              active: r["有効"] === true || r["有効"] === "TRUE" || r["有効"] === "true",
              createdAt: r["発行日時"] || "",
            }));
            if (loaded.length > 0) setManagerTokens(loaded);
          } catch(e) { console.error("managerTokens load error:", e); }
        }
      setIsLoaded(true);
    });
  },[]);

  // スタッフマスター自動保存（変更時）
  const staffFirstRun = useRef(true);
  useEffect(() => {
    if (!isLoaded) return;
    if (staffFirstRun.current) { staffFirstRun.current = false; return; }
    const t = setTimeout(() => gasPost("saveStaffMaster", { staff }), 1000);
    return () => clearTimeout(t);
  }, [staff, isLoaded]);

  // 店舗マスター自動保存（変更時）
  const storesFirstRun = useRef(true);
  useEffect(() => {
    if (!isLoaded) return;
    if (storesFirstRun.current) { storesFirstRun.current = false; return; }
    const t = setTimeout(() => gasPost("saveStoreMaster", { stores }), 1000);
    return () => clearTimeout(t);
  }, [stores, isLoaded]);

  // 🌸 重要連絡 手動保存（ボタン押下で保存）
  const [noticesSaveStatus, setNoticesSaveStatus] = useState(""); // "", "saving", "saved", "error"
  const [noticesSaveMsg, setNoticesSaveMsg] = useState("");
  const lastSavedNoticesRef = useRef(null);
  const noticesFirstRun = useRef(true);

  // 初回ロード時に保存済み内容を記録（後で「変更あり」検知用）
  useEffect(() => {
    if (!isLoaded) return;
    if (noticesFirstRun.current) {
      noticesFirstRun.current = false;
      lastSavedNoticesRef.current = JSON.stringify(notices);
    }
  }, [notices, isLoaded]);

  // 🌸 手動で保存を実行（NoticeBoard の保存ボタンから呼ばれる）
  const saveNoticesManually = async () => {
    setNoticesSaveStatus("saving");
    setNoticesSaveMsg(`重要連絡 ${notices.length}件を保存中...`);
    const result = await gasPostWithRetry("saveNotices", { notices });
    if (result?.success) {
      lastSavedNoticesRef.current = JSON.stringify(notices);
      // スプシ照合
      let verified = false;
      try {
        const fresh = await gasLoad();
        const freshData = fresh?.data || fresh;
        if (freshData?.notices) {
          verified = freshData.notices.length === notices.length;
        }
      } catch(e){}
      setNoticesSaveStatus("saved");
      setNoticesSaveMsg(`重要連絡 ${notices.length}件\n${verified ? "✓ スプシ確認済" : "保存しました"}`);
      setTimeout(() => setNoticesSaveStatus(""), 2500);
      return true;
    } else {
      setNoticesSaveStatus("error");
      setNoticesSaveMsg(`保存に失敗しました\n${result?.error || "ネット接続を確認してください"}\n\nファイルが大きい場合は3MB以下にしてみてください`);
      return false;
    }
  };

  // 「未保存の変更があるか」判定
  const hasUnsavedNotices = () => {
    if (noticesFirstRun.current) return false;
    return JSON.stringify(notices) !== lastSavedNoticesRef.current;
  };

  // 手動再送信
  const retryNoticesSave = async () => {
    setNoticesSaveStatus("saving");
    setNoticesSaveMsg(`重要連絡 ${notices.length}件を再送信中...`);
    const result = await gasPostWithRetry("saveNotices", { notices });
    if (result?.success) {
      lastSavedNoticesRef.current = JSON.stringify(notices);
      setNoticesSaveStatus("saved");
      setNoticesSaveMsg(`重要連絡 ${notices.length}件\n✓ 保存しました`);
      setTimeout(() => setNoticesSaveStatus(""), 2500);
    } else {
      setNoticesSaveStatus("error");
      setNoticesSaveMsg(`再送信失敗：${result?.error||"通信エラー"}`);
    }
  };

  // スタッフPWマイグレーション（pw="0000"のスタッフのみ個別PWに更新）
  useEffect(() => {
    if (!isLoaded) return;
    const PW_ASSIGN = {
      "p4":"3847","p8":"5291","p10":"7463","p15":"2958",
      "p28":"8134","p29":"4726","p30":"6385","p31":"9142",
      "p35":"3570","p36":"6829","p37":"7204","p38":"5891",
    };
    setStaff(prev => {
      let changed = false;
      const updated = prev.map(s => {
        if (PW_ASSIGN[s.id] && (s.pw === "0000" || !s.pw)) {
          changed = true;
          return { ...s, pw: PW_ASSIGN[s.id] };
        }
        return s;
      });
      if (changed) console.log("✓ スタッフPWを個別設定に更新しました");
      return changed ? updated : prev;
    });
  }, [isLoaded]);

  // 個人売上保存
  // 🌸 売上データをスプシから再取得して画面に反映
  const reloadSalesFromGAS = async () => {
    try {
      const fresh = await gasLoad();
      if (!fresh) return;
      // success が無くてもデータがあれば処理する（後方互換）

      const toDateStr = (v) => {
        if (!v) return "";
        let d;
        if (v instanceof Date) {
          d = v;
        } else {
          const s = String(v);
          if (s.includes("T")) {
            d = new Date(s);
            if (isNaN(d.getTime())) return s.split("T")[0];
          } else {
            const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
            if (m) return `${m[1]}-${String(m[2]).padStart(2,"0")}-${String(m[3]).padStart(2,"0")}`;
            return s.slice(0,10);
          }
        }
        const jst = new Date(d.getTime() + 9*60*60*1000);
        return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth()+1).padStart(2,"0")}-${String(jst.getUTCDate()).padStart(2,"0")}`;
      };

      // 個人売上
      if (fresh.staffSales) {
        const allRecs = (fresh.staffSales || [])
          .filter(r => r["ID"])
          .map(r => ({
            id: String(r["ID"]),
            date: toDateStr(r["日付"]),
            staffId: String(r["スタッフID"]||"").trim(),
            storeId: String(r["店舗ID"]||"").trim(),
            amount: Number(r["総売上"])||0,
            newGuests: Number(r["新規客数"])||0,
            regularGuests: Number(r["顧客数"])||0,
            totalGuests: Number(r["総人数"])||0,
            nextReserve: Number(r["次回予約数"])||0,
            shimei: Number(r["指名数"])||0
          }));
        setRecords(allRecs);
      }

      // 店舗売上
      if (fresh.storeSales) {
        const allRecs = (fresh.storeSales || [])
          .filter(s => s["キー"])
          .map(s => ({
            key: String(s["キー"]),
            date: toDateStr(s["日付"]),
            storeId: String(s["店舗ID"]||"").trim(),
            newGuests: Number(s["新規客数"])||0,
            regularGuests: Number(s["顧客数"])||0,
            totalGuests: Number(s["総人数"])||0,
            totalSales: Number(s["総売上"])||0,
            cashSales: Number(s["現金売上"])||0,
            creditSales: Number(s["クレジット売上"])||0,
            pointSales: Number(s["ポイント売上"])||0,
            otherSales: Number(s["その他売上"])||0,
            nextReserves: Number(s["次回予約数"])||0,
            register: Number(s["レジ残高"])||10000,
            inputBy: String(s["入力者"]||"")
          }));
        setStoreSales(allRecs);
      }
    } catch(e) {
      console.error("reloadSalesFromGAS error:", e);
    }
  };

  const saveRecordsToGAS = async (newRecords) => {
    const staff_map = {};
    staff.forEach(s=>{ staff_map[s.id]=s; });
    const stores_map = {};
    stores.forEach(s=>{ stores_map[s.id]=s; });
    const result = await gasPost("saveStaffSales", { records: newRecords.map(r=>({
      ...r,
      staffName: staff_map[r.staffId]?.name||"",
      storeName: stores_map[r.storeId]?.name||"",
    }))});
    // 🔧 保存結果チェック：失敗時は明示的にエラーをthrow
    if (!result || result.success === false) {
      const errMsg = result?.error || "GASからの応答がありません";
      console.error("saveStaffSales failed:", errMsg);
      throw new Error(errMsg);
    }
    // 🌸 保存成功後、スプシから再取得して画面に反映
    await reloadSalesFromGAS();
    return result;
  };

  // 管理者URLトークンでアクセスされた場合、ManagerViewを表示
  const managerToken = getManagerTokenFromURL();
  if (managerToken && isLoaded) {
    return (
      <>
        <FontLink/>
        <ManagerView
          token={managerToken}
          managerTokens={managerTokens}
          stores={stores}
          staff={staff}
          records={records}
          storeSales={storeSales}
          expenses={expenses}
          cashRegisterOuts={cashRegisterOuts}
          confirmedShifts={confirmedShifts}
        />
      </>
    );
  }
  // 読み込み中の場合はローディング表示
  if (managerToken && !isLoaded) {
    return (
      <div style={{ minHeight:"100vh", background:"#faf7f2", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ fontSize:12, color:"#9c7e5d", letterSpacing:2 }}>Loading...</div>
      </div>
    );
  }

  if (!role) return <><FontLink/><Login staff={staff} stores={stores} onLogin={(r,sid)=>{ setRole(r); setActiveStaffId(sid); setTab(r==="clerk" ? "saleslist" : "dashboard"); }}/></>;

  const tabs = TABS[role]||TABS.staff;

  // 既読状態のキー
  const readKey = `${role}_${activeStaffId||"owner"}`;
  const myReads = readStates[readKey] || { noticeAt: "", boardAt: "" };

  // 未読カウント
  const myStoreId = role==="staff" ? (staff.find(s=>s.id===activeStaffId)?.storeId) : null;
  const unreadNotices = (notices||[]).filter(n => {
    // 自店舗 or 全店向けのみ対象
    if (role==="staff" && myStoreId) {
      const targets = Array.isArray(n.targetStores) ? n.targetStores : [];
      if (targets.length > 0 && !targets.includes(myStoreId)) return false;
    }
    const createdAt = n.createdAt || n.date || "";
    return createdAt > (myReads.noticeAt || "");
  }).length;
  const unreadBoard = (threads||[]).filter(t => {
    const lastUpdate = t.lastUpdatedAt || t.createdAt || "";
    return lastUpdate > (myReads.boardAt || "");
  }).length;

  // タブ切替時に自動で既読
  const handleSetTab = (newTab) => {
    setTab(newTab);
    const now = new Date().toISOString();
    if (newTab === "notice") {
      setReadStates(prev => ({ ...prev, [readKey]: { ...myReads, noticeAt: now } }));
    } else if (newTab === "board") {
      setReadStates(prev => ({ ...prev, [readKey]: { ...myReads, boardAt: now } }));
    }
  };

  return (
    <>
      <FontLink/>
      <div style={{ minHeight:"100vh", background:bodyBg, overflowX:"hidden", maxWidth:"100vw" }}>
      <div style={{ position:"sticky", top:0, zIndex:100, background:headerBg, borderBottom:`1px solid ${headerBorder}`, paddingTop:"env(safe-area-inset-top, 0px)" }}>
        <div style={{ padding:"10px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
          <div style={{ minWidth:0, flex:"1 1 auto", overflow:"hidden" }}>
            <div style={{ fontFamily:"Cormorant Garamond", fontSize:22, color:logoColor, letterSpacing:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>Worksplus</div>
            <div style={{ fontSize:9, color:logoColor, opacity:0.7, letterSpacing:2, marginTop:1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>Salon Manager</div>
            {role==="staff"&&activeStaffId&&<div style={{ fontSize:9,color:C.muted, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{staff.find(s=>s.id===activeStaffId)?.name}</div>}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
            <RoleBadge role={role}/>
            <button onClick={logout} style={{ background:"none",border:`1px solid ${headerBorder}`,borderRadius:16,padding:"4px 8px",color:C.muted,fontSize:10,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0 }}>ログアウト</button>
          </div>
        </div>
        <div style={{ display:"flex", overflowX:"auto", background:headerBg, borderBottom:`1px solid ${headerBorder}`, paddingLeft:4 }}>
          {tabs.map(t=>{
            const badgeCount = t.id==="notice" ? unreadNotices : t.id==="board" ? unreadBoard : 0;
            return (
              <button key={t.id} onClick={()=>handleSetTab(t.id)} style={{
                background:"none", border:"none", padding:"10px 14px",
                color:tab===t.id?tabActiveColor:tabMutedColor, fontSize:12,
                borderBottom:`2px solid ${tab===t.id?tabActiveBorder:"transparent"}`,
                cursor:"pointer", whiteSpace:"nowrap", fontWeight:tab===t.id?600:400,
                position:"relative",
              }}>
                {t.label}
                {badgeCount > 0 && (
                  <span style={{
                    position:"absolute", top:4, right:2,
                    background:"#d4786b", color:"#fff",
                    fontSize:9, fontWeight:700,
                    minWidth:16, height:16, borderRadius:8,
                    display:"inline-flex", alignItems:"center", justifyContent:"center",
                    padding:"0 4px", boxShadow:"0 2px 4px rgba(212,120,107,0.4)",
                  }}>{badgeCount > 9 ? "9+" : badgeCount}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {tab==="dashboard"   && <Dashboard records={records} storeSales={storeSales} staff={staff} stores={stores} role={role} activeStaffId={activeStaffId} onNav={setTab} orders={orders} setOrders={setOrders} approve={approve} rejectOpen={rejectOpen}/>}
      {tab==="saleslist"   && <SalesList records={records} storeSales={storeSales} setStoreSales={setStoreSales} staff={staff} stores={stores} role={role} reloadSalesFromGAS={reloadSalesFromGAS} cashRegisterOuts={cashRegisterOuts}/>}
      {tab==="staffsales"  && <StaffSalesView records={records} staff={staff} stores={stores}/>}
      {tab==="input"       && role==="staff" && <SalesInput records={records} setRecords={setRecords} staff={staff} stores={stores} role={role} activeStaffId={activeStaffId} expenses={expenses} setExpenses={setExpenses} saveRecordsToGAS={saveRecordsToGAS}/>}
      {tab==="salary"      && role==="staff" && <StaffSalary records={records} staff={staff} activeStaffId={activeStaffId} stores={stores} expenses={expenses} shiftRequests={shiftRequests}/>}
      {tab==="storeinput"  && <StoreSalesInput storeSales={storeSales} setStoreSales={setStoreSales} stores={stores} staff={staff} activeStaffId={activeStaffId} role={role} cashRegisterOuts={cashRegisterOuts} setCashRegisterOuts={setCashRegisterOuts} reloadSalesFromGAS={reloadSalesFromGAS}/>}
      {tab==="staffmaster" && <StaffMaster staff={staff} setStaff={setStaff} stores={stores} role={role}/>}
      {tab==="storemaster" && <StoreMaster stores={stores} setStores={setStores}/>}
      {tab==="payroll"     && <Payroll records={records} staff={staff} stores={stores}/>}
      {tab==="notice"      && <NoticeBoard notices={notices} setNotices={setNotices} role={role} stores={stores} activeStaffId={activeStaffId} staff={staff} onSave={saveNoticesManually} hasUnsaved={hasUnsavedNotices()} saveStatus={noticesSaveStatus}/>}
      {tab==="shift"       && <ShiftRequest shiftRequests={shiftRequests} setShiftRequests={setShiftRequests} staff={staff} stores={stores} role={role} activeStaffId={activeStaffId}/>}
      {tab==="shiftmgr"    && role==="owner" && <ShiftManager staff={staff} stores={stores} shiftRequests={shiftRequests} confirmedShifts={confirmedShifts} setConfirmedShifts={setConfirmedShifts} notices={notices} setNotices={setNotices} shiftComments={shiftComments} setShiftComments={setShiftComments}/>}
      {tab==="myshift"     && role==="staff" && <MyShift staff={staff} stores={stores} confirmedShifts={confirmedShifts} activeStaffId={activeStaffId} shiftComments={shiftComments} setShiftComments={setShiftComments}/>}
      {tab==="board"       && <ThreadBoard threads={threads} setThreads={setThreads} staff={staff} stores={stores} role={role} activeStaffId={activeStaffId}/>}
      {tab==="order"       && <OrderTab stores={stores} staff={staff} role={role} activeStaffId={activeStaffId} orders={orders} setOrders={setOrders} approve={approve} rejectOpen={rejectOpen} rejectId={rejectId} rejectMsg={rejectMsg} setRejectMsg={setRejectMsg} rejectConfirm={rejectConfirm} setRejectId={setRejectId}/>}
      {tab==="managerurl"  && role==="owner" && <ManagerURLGenerator managerTokens={managerTokens} setManagerTokens={setManagerTokens} stores={stores}/>}
      </div>

      {/* 差し戻しモーダル（全画面共通） */}
      {rejectId && (
        <div style={{ position:"fixed",inset:0,background:"#0008",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24 }}>
          <div style={{ background:C.bg1,borderRadius:16,padding:20,width:"100%",maxWidth:320,border:`1px solid ${C.border2}` }}>
            <div style={{ fontSize:13,color:C.gold,fontWeight:600,marginBottom:10 }}>差し戻しコメント</div>
            <textarea value={rejectMsg} onChange={e=>setRejectMsg(e.target.value)} placeholder="理由を入力してください"
              style={{ width:"100%",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 10px",color:C.cream,fontSize:12,height:80,resize:"none",fontFamily:"Jost,sans-serif" }}/>
            <div style={{ display:"flex",gap:8,marginTop:10 }}>
              <button onClick={rejectConfirm} style={{ flex:2,background:C.red,color:"#fff",border:"none",borderRadius:8,padding:10,fontSize:12,fontWeight:600,cursor:"pointer" }}>差し戻す</button>
              <button onClick={()=>setRejectId(null)} style={{ flex:1,background:C.bg2,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,padding:10,fontSize:12,cursor:"pointer" }}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* 🌸 重要連絡 保存ステータスモーダル（画面中央表示） */}
      <SaveStatusModal
        status={noticesSaveStatus === "saved" ? "success" : noticesSaveStatus === "saving" ? "saving" : noticesSaveStatus === "error" ? "error" : null}
        message={noticesSaveMsg}
        onClose={()=>{ setNoticesSaveStatus(""); setNoticesSaveMsg(""); }}
      />
    </>
  );
}
